import { Controller } from "@hotwired/stimulus"
import { mentionChipFor } from "../utils/mention_chips"
import { createConsumer } from "@rails/actioncable"
import * as bootstrap from "bootstrap"
import { showSystemToast } from "../utils/system_toast"
import { csrfToken } from "../utils/csrf"
import { dragHasFiles, uploadTaskAttachments } from "../utils/task_attachments"
import { taskPaneTrigger } from "../utils/task_pane"
import { closeTaskPopover, openAssignPopover, openDatePopover, remindTimeOf } from "../utils/task_popovers"
import { nativeHaptic } from "../utils/native_bridge"
import { celebrateDailyTasks } from "../utils/daily_celebration"
import { Picker } from "emoji-picker-element"
import { mdToHtml, domToMarkdown, escapeMd, styleOf, STYLE_KINDS, STYLE_COLORS } from "../utils/inline_markdown"
import { parseDatePhrase } from "../utils/nl_dates"

// The row's count chips (note, comments, files), which close the title wrap.
const COUNT_CHIPS = "[data-task-note-chip], [data-task-comments-chip], [data-task-attachments-chip]"

const INDENT = 24
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
// Mirrors TasksController::PASTE_BATCH_LIMIT; lines beyond it fold into the
// final pasted task so nothing is silently dropped.
const PASTE_LIMIT = 500
// How long a deleted task can be brought back ("Task deleted · Undo").
const UNDO_DELETE_MS = 5000
// Last-used colour/highlight/badge picks, newest first (feedback #209). Per
// browser, like ClickUp's "Recent" row; ⌘⇧H re-applies the newest.
const RECENT_STYLES_KEY = "tasks-outline:recent-styles"
const RECENT_STYLES_MAX = 8
// Section headings ("Turn into"): the row's data-text-style. "" is a task.
const TEXT_STYLES = ["h1", "h2", "h3"]
const TEXT_STYLE_NAMES = { "": "Text", h1: "Heading 1", h2: "Heading 2", h3: "Heading 3" }

// The "/" menu's Priority choices, in the row menu's order.
const SLASH_PRIORITIES = [["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]]

export default class extends Controller {
    static values = { emptyList: Boolean, list: String, currentUserId: Number, shared: Boolean, prioritySorted: Boolean, serverFiltered: Boolean, emojiData: String, fields: Array }

    connect() {
        // Bootstrap fires these on the toggle button and they bubble, so one
        // listener covers server-rendered and client-built rows alike.
        // Decided BEFORE it opens (Bootstrap reads .dropup at show time): a menu
        // that will not fit below its button opens upward when there is more room
        // there. Then, once Popper has placed it - not on the event itself, and not a
        // frame later either - size it to the side it is on.
        this._onMenuShow = (event) => {
            const menu = event.target?.closest?.(".tasks-outline-dropdown")?.querySelector(".dropdown-menu")
            if (menu) this._tidyRowMenu(menu)
            this._chooseRowMenuSide(event.target)
        }
        this._onMenuShown = (event) => {
            const toggle = event.target
            setTimeout(() => this._fitRowMenu(toggle), 60)
        }
        this.element.addEventListener("show.bs.dropdown", this._onMenuShow)
        this.element.addEventListener("shown.bs.dropdown", this._onMenuShown)
        this._editingId = null
        this._tmpCounter = 0
        this._pendingDeletes = new Set()
        // "?" outside a text field opens the shortcut sheet (Todoist, Gmail,
        // Linear all use it).
        this._onShortcutKey = (e) => {
            if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return
            if (this._isTypingKey(e)) return
            e.preventDefault()
            this.openShortcuts()
        }
        document.addEventListener("keydown", this._onShortcutKey)
        this._connectMultiSelect()
        this._connectUndo()
        // The server labels a reminder in the account's zone; the browser's is
        // the one the person is in right now.
        this.element.querySelectorAll("[data-task-row] .tasks-outline-rail-remind-label").forEach((label) => {
            const row = label.closest("[data-task-row]")
            const text = this._remindLabel(row.dataset.remindAt, row.dataset.dueDate)
            if (text) label.textContent = text
        })
        this._onPageLeave = () => this._flushPendingDeletes()
        window.addEventListener("pagehide", this._onPageLeave)
        document.addEventListener("turbo:before-visit", this._onPageLeave)
        // @-mention autocomplete state (see the "Mention autocomplete" region).
        this._mention = null
        this._mentionDebounce = null
        this._draggingBlock = null
        this._touchDrag = null
        this._remoteEditors = new Map()
        // Live-sync pause (session-scoped, always starts live): while paused,
        // remote task.changed events are counted but not applied to the DOM.
        this._livePaused = false
        this._skippedRemoteCount = 0
        // Per-tab id so other tabs of the same user still sync; only this tab
        // skips its own optimistic task.changed / editing echoes.
        this._clientId = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
            ? crypto.randomUUID()
            : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
        // Published for the comment panel (task_comments_controller.js), which
        // lives in the detail sidebar outside this element's controller but must
        // send the same id so its writes' cable echoes are skipped here.
        this.element.dataset.taskListClientId = this._clientId
        this._onCommentsCount = (event) => {
            const { taskId, count } = event.detail || {}
            const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(String(taskId))}"]`)
            if (row) this._renderCommentChip(row, count)
        }
        window.addEventListener("jane:task-comments-count", this._onCommentsCount)
        // The same for files (utils/task_attachments.js): every upload or
        // removal, from a row or from the Files panel, redraws the chip.
        this._onAttachmentsCount = (event) => {
            const { taskId, count } = event.detail || {}
            const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(String(taskId))}"]`)
            if (row) this._renderAttachmentChip(row, count)
        }
        window.addEventListener("jane:task-attachments-count", this._onAttachmentsCount)
        // The task pane's writes and navigation (task_pane_controller.js).
        this._onPaneNotesBound = this._onPaneNotes.bind(this)
        this._onPaneToggleBound = this._onPaneToggle.bind(this)
        this._onPaneActionBound = this._onPaneAction.bind(this)
        this._onPaneOpenBound = this._onPaneOpen.bind(this)
        this._onPaneProbeBound = this._onPaneProbe.bind(this)
        this._onPaneUpdateBound = this._onPaneUpdate.bind(this)
        window.addEventListener("jane:task-pane-update", this._onPaneUpdateBound)
        window.addEventListener("jane:task-pane-probe", this._onPaneProbeBound)
        window.addEventListener("jane:task-pane-notes", this._onPaneNotesBound)
        window.addEventListener("jane:task-pane-toggle", this._onPaneToggleBound)
        window.addEventListener("jane:task-pane-action", this._onPaneActionBound)
        window.addEventListener("jane:task-pane-open", this._onPaneOpenBound)
        // Delegated (not per-handle) so dynamically built rows work, and
        // bound here rather than via data-action because touchmove must be
        // registered non-passive for preventDefault to stop page scroll.
        this._onTouchStart = this.onTouchStart.bind(this)
        this._onTouchMove = this.onTouchMove.bind(this)
        this._onTouchEnd = this.onTouchEnd.bind(this)
        this._onTouchCancel = this.onTouchCancel.bind(this)
        this.element.addEventListener("touchstart", this._onTouchStart, { passive: true })
        this.element.addEventListener("touchmove", this._onTouchMove, { passive: false })
        this.element.addEventListener("touchend", this._onTouchEnd)
        this.element.addEventListener("touchcancel", this._onTouchCancel)
        // Floating formatting toolbar: shown while a selection spans styled-
        // editable text. selectionchange only fires on document, and the
        // toolbar lives on document.body so it can escape overflow clipping.
        this._toolbar = null
        this._linkMode = false
        this._linkRange = null
        // "emoji" while the toolbar's emoji picker is open. The picker's search
        // field takes focus, so the editor's blur and the selectionchange it
        // causes must not tear editing down underneath it.
        this._panelMode = null
        this._panelRange = null
        this._onPanelOutside = (ev) => {
            if (!this._panelMode || !this._toolbar || this._toolbar.contains(ev.target)) return
            this._closeEmojiPanel(false)
        }
        document.addEventListener("mousedown", this._onPanelOutside, true)
        this._onSelectionChange = this._handleSelectionChange.bind(this)
        // A click INTO coloured text may leave the caret there without a fresh
        // selectionchange after the pointer lifts, so check once it does.
        this._onEditorPointerUp = (event) => {
            if (!event.target.closest?.("[data-task-input]")) return
            this._lastPointerUpAt = Date.now()
            requestAnimationFrame(() => this._handleSelectionChange())
        }
        this.element.addEventListener("pointerup", this._onEditorPointerUp)
        this._onWindowScroll = this._handleToolbarScroll.bind(this)
        document.addEventListener("selectionchange", this._onSelectionChange)
        window.addEventListener("scroll", this._onWindowScroll, true)
        // The side panel's "Unlink from task" lives outside this controller's
        // scope, so it reports what it did and the row drops its chip here.
        this._onCompanyUnlinked = (event) => this._removeCompanyChip(event.detail || {})
        window.addEventListener("jane:company-unlinked", this._onCompanyUnlinked)
        if (this.emptyListValue) this._ensurePlaceholder()
        this._focusTaskFromUrl()
        this._composeFromUrl()
        this._connectListCable()
    }

    // ?compose=1 — the search omnibox's "New task" quick action lands here and
    // wants a cursor in a fresh row, not just the list. Same trailing-blank-row
    // flow as clicking below the outline; the param is stripped so a reload or
    // Back doesn't append another empty row.
    _composeFromUrl() {
        let compose = null
        try { compose = new URLSearchParams(window.location.search).get("compose") } catch { return }
        if (!compose) return

        try {
            const url = new URL(window.location.href)
            url.searchParams.delete("compose")
            window.history.replaceState({}, "", url)
        } catch { /* ignore */ }

        requestAnimationFrame(() => this._appendTrailingRow())
    }

    // Deep link from another surface (e.g. "Due in your other lists"): ?focus=
    // names a task on this list. Scroll it into view and flash it so the eye
    // lands on the right row, then strip the param so a reload/back doesn't
    // re-flash.
    //
    // ?comments=1 alongside it (a comment notification's link) also opens the
    // task's thread, so "Reply" lands in the conversation, not just on the row.
    _focusTaskFromUrl() {
        let id = null
        let openComments = false
        try {
            const params = new URLSearchParams(window.location.search)
            id = params.get("focus")
            openComments = params.get("comments") === "1"
        } catch { return }
        if (!id) return

        const block = this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(id)}"]`)
        const row = block && block.querySelector("[data-task-row]")
        if (row) {
            // A collapsed parent hides the target row; expand every ancestor
            // first so it's actually visible before we scroll to it.
            this._expandAncestors(block)
            // Defer to the next frame so layout is settled before scrolling.
            requestAnimationFrame(() => {
                try { block.scrollIntoView({ behavior: "smooth", block: "center" }) } catch { block.scrollIntoView() }
                row.classList.remove("tasks-outline-row--flash")
                // Reflow so re-adding the class restarts the animation.
                void row.offsetWidth
                row.classList.add("tasks-outline-row--flash")
                setTimeout(() => row.classList.remove("tasks-outline-row--flash"), 2200)
                if (openComments) this._openComments(row)
            })
        }

        try {
            const url = new URL(window.location.href)
            url.searchParams.delete("focus")
            url.searchParams.delete("comments")
            window.history.replaceState({}, "", url)
        } catch { /* ignore */ }
    }

    // Walk up from a block, expanding any collapsed ancestor so a deep-linked
    // descendant is actually rendered/visible. _setCollapsed both toggles the
    // DOM (synchronously) and persists, so the reveal survives a reload too.
    _expandAncestors(block) {
        let container = block.parentElement && block.parentElement.closest("[data-children-for]")
        while (container && container.dataset.childrenFor !== "root") {
            const parentBlock = container.closest("[data-task-block]")
            if (!parentBlock) break
            const parentRow = parentBlock.querySelector(":scope > [data-task-row]")
            if (parentRow && parentRow.dataset.collapsed === "true") this._setCollapsed(parentRow, false)
            container = parentBlock.parentElement && parentBlock.parentElement.closest("[data-children-for]")
        }
    }

    // --- Blank placeholder row ---
    // An unsaved, ordinary-looking blank row is the only "add a task"
    // affordance: a truly-empty list starts with one, and clicking the space
    // below the outline appends one (or reuses a trailing empty row), so the
    // first keystroke lands in the exact same keyboard flow as any other row.
    // Nothing is persisted until the row first needs a real id (typing,
    // Enter with text, row actions).

    _ensurePlaceholder() {
        const container = this.element.querySelector('[data-children-for="root"]')
        if (!container || container.querySelector("[data-task-block]")) return
        container.appendChild(this._buildPlaceholderBlock())
    }

    // Click on the empty space below the last task: focus a trailing blank
    // row if one exists (never stack empties), otherwise append an unsaved
    // placeholder and start editing it.
    addAtEnd(e) {
        if (e.target.closest("[data-task-block]")) return
        this._appendTrailingRow()
    }

    _appendTrailingRow() {
        const container = this.element.querySelector('[data-children-for="root"]')
        if (!container) return
        const blocks = container.querySelectorAll(":scope > [data-task-block]")
        const last = blocks[blocks.length - 1]
        if (last) {
            const row = last.querySelector(":scope > [data-task-row]")
            if (row && row.dataset.completed !== "true" && !this._hasChildren(row) && this._rowTitle(row) === "") {
                this._setEditing(row.dataset.taskId)
                return
            }
        }
        const block = this._buildPlaceholderBlock()
        container.appendChild(block)
        this._setEditing(block.dataset.taskId)
    }

    _buildPlaceholderBlock() {
        const block = this._buildBlock({ id: `tmp-${++this._tmpCounter}`, title: "", parentId: "" })
        block.querySelector("[data-task-row]").dataset.placeholderPending = "true"
        const input = block.querySelector("[data-task-input]")
        if (input) input.dataset.placeholder = "Add a task"
        return block
    }

    // An abandoned (blurred while still empty) placeholder disappears again —
    // except in an otherwise-empty list, where it is the only affordance.
    _cleanupPlaceholder(row) {
        if (!row.dataset.placeholderPending) return
        const input = row.querySelector("[data-task-input]")
        if (String((input && input.textContent) || "").trim() !== "") return
        const container = this.element.querySelector('[data-children-for="root"]')
        if (!container) return
        if (container.querySelectorAll(":scope > [data-task-block]").length <= 1) return
        const block = row.closest("[data-task-block]")
        if (block) block.remove()
    }

    // Persists the placeholder as a real task, following the same real-id
    // promise contract as _createTask so every action path can await it.
    _promotePlaceholder(block) {
        if (block._realId) return block._realId
        const row = block.querySelector("[data-task-row]")
        const input = block.querySelector("[data-task-input]")
        const tempId = block.dataset.taskId
        delete row.dataset.placeholderPending
        if (input) delete input.dataset.placeholder
        const fd = new FormData()
        const postedTitle = input ? domToMarkdown(input).trim() : ""
        row._lastSavedTitle = postedTitle
        fd.set("title", postedTitle)
        fd.set("parent_id", "root")
        block._realId = this._post("/tasks.json", fd).then((data) => {
            if (!data?.ok) {
                delete block._realId
                row.dataset.placeholderPending = "true"
                if (input) input.dataset.placeholder = "Add a task"
                return null
            }
            const realId = String(data.task.id)
            this._replaceTempId(block, tempId, realId)
            delete block._realId
            return realId
        })
        return block._realId
    }

    disconnect() {
        this._flushPendingDeletes()
        document.removeEventListener("keydown", this._onShortcutKey)
        this._disconnectMultiSelect()
        document.removeEventListener("keydown", this._onUndoKey)
        if (this._closeShortcutSheet) this._closeShortcutSheet()
        this._shortcutSheet?.remove()
        window.removeEventListener("pagehide", this._onPageLeave)
        document.removeEventListener("turbo:before-visit", this._onPageLeave)
        this.element.removeEventListener("show.bs.dropdown", this._onMenuShow)
        this.element.removeEventListener("shown.bs.dropdown", this._onMenuShown)
        this._disconnectListCable()
        this.element.removeEventListener("touchstart", this._onTouchStart)
        this.element.removeEventListener("touchmove", this._onTouchMove)
        this.element.removeEventListener("touchend", this._onTouchEnd)
        this.element.removeEventListener("touchcancel", this._onTouchCancel)
        document.removeEventListener("selectionchange", this._onSelectionChange)
        this.element.removeEventListener("pointerup", this._onEditorPointerUp)
        document.removeEventListener("mousedown", this._onPanelOutside, true)
        window.removeEventListener("scroll", this._onWindowScroll, true)
        window.removeEventListener("jane:company-unlinked", this._onCompanyUnlinked)
        window.removeEventListener("jane:task-comments-count", this._onCommentsCount)
        window.removeEventListener("jane:task-attachments-count", this._onAttachmentsCount)
        window.removeEventListener("jane:task-pane-notes", this._onPaneNotesBound)
        window.removeEventListener("jane:task-pane-toggle", this._onPaneToggleBound)
        window.removeEventListener("jane:task-pane-action", this._onPaneActionBound)
        window.removeEventListener("jane:task-pane-open", this._onPaneOpenBound)
        window.removeEventListener("jane:task-pane-probe", this._onPaneProbeBound)
        window.removeEventListener("jane:task-pane-update", this._onPaneUpdateBound)
        if (this._toolbar) {
            this._toolbar.remove()
            this._toolbar = null
        }
        this._closeCompanyPicker()
        this._closeSendPicker()
        this._closeMention()
        this._closeSlash()
        this._closeTagPicker()
        this._closePopover()
        clearTimeout(this._paneFollowTimer)
        this.onTouchCancel()
    }

    startEdit(e) {
        if (e.target.closest("[data-task-input]")) return
        // ⌘-click on a link opens it in a new tab - not an edit, and not a
        // reason to drop a selection.
        if ((e.shiftKey || e.metaKey || e.ctrlKey) && e.target.closest("a[href]")) return
        // ⇧-click / ⌘-click select rows instead of editing (see "Multi-select").
        if ((e.shiftKey || e.metaKey || e.ctrlKey) && this._selectClick(e)) return
        this._clearSelection()
        // Feedback #132: the static title is now selectable text (see
        // _tasks_page.scss), so a drag-to-select ends with a "click" on the
        // same element a plain click would hit. A mousedown that doesn't
        // move leaves the selection collapsed (the browser default caret
        // placement); a selection drag leaves it non-collapsed. Bail in the
        // latter case so sweeping across text doesn't flip the row into
        // edit mode and swallow the selection.
        //
        // Exception: a selection that stays inside this one title (a
        // double-click on a word, a drag within the row) is the start of an
        // edit — select-then-⌘B. Bailing there left the text highlighted in
        // the static span with no editor behind it, so typing and ⌘B did
        // nothing until a second click. Open the editor with the same
        // selection instead; sweeps across rows still bail and stay copyable.
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const title = row.querySelector(".tasks-outline-title")
        const selection = window.getSelection()
        if (selection && !selection.isCollapsed) {
            const offsets = title ? this._selectionOffsets(title) : null
            if (!offsets) return
            this._setEditing(row.dataset.taskId)
            const input = row.querySelector("[data-task-input]")
            if (input && !input.hidden) this._setSelectionOffsets(input, offsets.start, offsets.end)
            return
        }
        const caretPos = title ? this._caretPosFromClick(title, e.clientX, e.clientY) : null
        this._setEditing(row.dataset.taskId, caretPos)
    }

    toggleComplete(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._toggleCompleteForRow(row)
    }

    // --- Touch swipe gestures (task-swipe controller on the same element) ---

    onSwipeToggle(e) {
        const row = e.detail?.row
        if (row) this._toggleCompleteForRow(row)
    }

    onSwipeDelete(e) {
        const row = e.detail?.row
        if (!row) return
        if (this._hasChildren(row)) {
            this._flashNotice("Delete subtasks first", "warning")
            return
        }
        // No focus shift: a swipe-delete must not pop the mobile keyboard.
        this._deleteTask(row, { focusNeighbor: false })
    }

    _toggleCompleteForRow(row, { record = true, spawned = { id: null } } = {}) {
        // An unsaved placeholder row has nothing to complete yet, and a section
        // heading is a label - never done (⌘↵, swipe and the menu all land here).
        if (row.dataset.placeholderPending || this._isHeading(row)) return
        const completed = row.dataset.completed === "true"
        // Optimistic toggle
        const next = !completed
        row.dataset.completed = String(next)
        row.classList.toggle("is-completed", next)
        // Completing a repeating task spawns its next occurrence; undoing the
        // completion takes that occurrence away again.
        // One holder across undo and redo: a redo that completes again spawns
        // a fresh occurrence, and the next undo must take THAT one away.
        // Completing a repeating task also moves its repeat onto the spawned
        // occurrence (Task#spawn_next_occurrence!), so undoing it puts the
        // repeat - and the date and reminder - back on this row.
        const fieldsBefore = record && next ? this._rowFields(row) : null
        if (record) {
            this._recordAction({
                label: next ? "Complete" : "Reopen",
                undo: () => {
                    this._toggleCompleteForRow(row, { record: false, spawned })
                    if (spawned.id) {
                        this._removeSpawned(spawned.id)
                        if (fieldsBefore) this._writeFields(row, fieldsBefore)
                    }
                    spawned.id = null
                },
                redo: () => this._toggleCompleteForRow(row, { record: false, spawned })
            })
        }

        this._queueRowWrite(row, () => this._resolveId(row.closest("[data-task-block]")).then((id) => {
            const url = next ? `/tasks/${id}/complete.json` : `/tasks/${id}/uncomplete.json`
            return this._post(url, new FormData()).then((data) => {
                if (data?.ok) {
                    if (next) nativeHaptic("light")
                    if (next && data.spawned) {
                        spawned.id = String(data.spawned.id)
                        this._insertSpawned(row.closest("[data-task-block]"), data.spawned)
                    }
                    if (next && data.task) this._applyTaskPayloadToRow(row, data.task)
                    if (next) celebrateDailyTasks(data.celebration)
                    return
                }
                // Revert on failure
                row.dataset.completed = String(completed)
                row.classList.toggle("is-completed", completed)
            })
        }))
    }

    // One row's complete / reopen / field writes reach the server in the order
    // they were made. Undoing a repeating task's completion reopens it AND
    // PATCHes its repeat back; sent side by side, the PATCH could read the task
    // while it was still completed and its reply marked the row done again, so
    // a quick ⌘⇧Z reopened instead of completing - and a redo that overtook the
    // PATCH completed a task with no repeat left to spawn.
    _queueRowWrite(row, write) {
        const run = (row._writeQueue || Promise.resolve()).then(write)
        row._writeQueue = run.catch(() => null)
        return run
    }

    toggleCollapse(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const collapsed = row.dataset.collapsed === "true"
        this._setCollapsed(row, !collapsed)
    }

    // Fold all / unfold all from the list header menu. _setCollapsed no-ops on
    // rows already in the requested state, so only actual changes are persisted.
    onFoldAll(event) {
        const folded = Boolean(event.detail?.folded)
        this.element.querySelectorAll("[data-task-row]").forEach((row) => {
            if (this._hasChildren(row)) this._setCollapsed(row, folded)
        })
    }

    // Copy the whole list as indented plain text (fired from the header •••
    // menu), same format as a single task's "Copy task and subtask text".
    onCopyAll() {
        const container = this.element.querySelector('[data-children-for="root"]')
        if (!container) return
        const blocks = Array.from(container.querySelectorAll(":scope > [data-task-block]"))
        const lines = []
        blocks.forEach((block) => this._appendTaskTextLines(block, 0, lines))
        if (lines.length === 0) {
            this._flashNotice("Nothing to copy yet", "warning")
            return
        }
        const html = this._wrapTaskList(this._taskItemsHtml(blocks))
        this._copyRich(lines.join("\n"), html).then((ok) => {
            if (ok) this._flashNotice("Copied to clipboard")
        })
    }

    // --- Ask Jane ---

    // The sparkle: the task pane on its comments, with "@Jane "
    // typed in. Her answer lands in the thread, where the team sees it
    // (TaskComment#asks_jane?, Tasks::JaneCommentReplyJob).
    taskJane(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeRowMenu(e)
        this._openPane(row, "comments", { ask: "jane" })
    }

    copyTitle(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const title = this._rowTitle(row)
        if (!title) return
        const html = this._wrapTaskList(`<li>${mdToHtml(title)}</li>`)
        this._copyRich(title, html).then((ok) => {
            if (ok) this._flashNotice("Copied to clipboard")
        })
    }

    // Copy a shareable deep link to this specific task. The link is the task's
    // own list URL with ?focus=<id>; opening it lets _focusTaskFromUrl scroll
    // the row into view and flash the existing yellow highlight. This changes
    // nothing about visibility or sharing — the link only navigates + flashes,
    // and access to the list is still enforced by the server exactly as today
    // (a viewer without access hits the normal request-access flow).
    copyLink(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const block = row.closest("[data-task-block]")
        if (!block) return
        const btn = e.target.closest("[data-task-copy-link]")
        this._resolveId(block).then((id) => {
            if (!id) return
            this._copyText(this._taskDeepLink(block, id)).then((ok) => {
                if (!ok) return
                this._flashNotice("Link copied")
                if (btn) this._flashCopyLinkButton(btn)
            })
        })
    }

    // Absolute deep link to a task, always pointing at the TASK'S OWN list — not
    // whatever list the current page is showing. Server-rendered rows carry the
    // canonical /tasks/:list_id/:slug?focus=<id> URL in data-task-deep-link, so a
    // link copied while viewing list A for a task on list B still opens list B.
    // Rows created client-side this session have no such attribute yet, but they
    // always live on the current list, so we rebuild against this list's id
    // (never the bare window.location, which a remembered-list /tasks page would
    // resolve to the recipient's own default list). Filters and any prior
    // focus/hash are dropped so the link stays a clean, canonical pointer.
    _taskDeepLink(block, id) {
        const canonical = block && block.dataset ? block.dataset.taskDeepLink : ""
        if (canonical) {
            try {
                const url = new URL(canonical, window.location.href)
                url.searchParams.set("focus", String(id))
                return url.toString()
            } catch { /* fall through to the current-list rebuild */ }
        }
        const url = new URL(window.location.href)
        url.hash = ""
        url.search = ""
        if (this.hasListValue && this.listValue) url.pathname = `/tasks/${this.listValue}`
        url.searchParams.set("focus", String(id))
        return url.toString()
    }

    // Brief "copied" affordance on the row button: swap the link glyph for a
    // check, then restore it. The toast is the primary confirmation; this is
    // the in-place echo.
    _flashCopyLinkButton(btn) {
        const icon = btn.querySelector("i")
        if (!icon) return
        const prev = icon.className
        icon.className = "bi bi-check2"
        btn.classList.add("is-copied")
        setTimeout(() => {
            icon.className = prev
            btn.classList.remove("is-copied")
        }, 1200)
    }

    copyTaskAndSubtasks(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const block = row.closest("[data-task-block]")
        if (!block) return
        const lines = []
        this._appendTaskTextLines(block, 0, lines)
        if (lines.length === 0) return
        const html = this._wrapTaskList(this._taskItemsHtml([block]))
        this._copyRich(lines.join("\n"), html).then((ok) => {
            if (ok) this._flashNotice("Copied to clipboard")
        })
    }

    addTaskAbove(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const block = row.closest("[data-task-block]")
        const parentId = row.dataset.parentId || ""
        const parentBlock = parentId
            ? this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(parentId)}"]`)
            : null
        this._createTask({ parentBlock, beforeBlock: block, title: "", focusNew: true, focusCaret: 0 })
    }

    addTaskBelow(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const block = row.closest("[data-task-block]")
        const parentId = row.dataset.parentId || ""
        const parentBlock = parentId
            ? this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(parentId)}"]`)
            : null
        this._createTask({ parentBlock, afterBlock: block, title: "", focusNew: true, focusCaret: 0 })
    }

    markCompleted(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row || row.dataset.completed === "true" || this._isHeading(row)) return
        this._markRowCompleted(row, false)
    }

    markAllCompleted(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const block = row.closest("[data-task-block]")
        if (!block) return
        // Section headings in the subtree stay standing - the server's cascade
        // passes through them to their tasks (Task#complete!).
        const rows = Array.from(block.querySelectorAll("[data-task-row]")).filter((r) => !this._isHeading(r))
        const prior = rows.map((r) => ({ row: r, completed: r.dataset.completed === "true" }))
        rows.forEach((r) => {
            if (r.dataset.completed !== "true") {
                r.dataset.completed = "true"
                r.classList.add("is-completed")
            }
        })
        this._resolveId(block).then((id) => {
            const fd = new FormData()
            fd.set("cascade", "1")
            this._post(`/tasks/${id}/complete.json`, fd).then((data) => {
                if (data?.ok) {
                    if (data.spawned) this._insertSpawned(block, data.spawned)
                    celebrateDailyTasks(data.celebration)
                    return
                }
                prior.forEach(({ row: r, completed }) => {
                    r.dataset.completed = String(completed)
                    r.classList.toggle("is-completed", completed)
                })
            })
        })
    }

    duplicateTask(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const block = row.closest("[data-task-block]")
        if (!block) return
        this._resolveId(block).then((id) => {
            this._post(`/tasks/${id}/duplicate.json`, new FormData()).then((data) => {
                if (!data?.ok || !Array.isArray(data.tasks) || data.tasks.length === 0) return
                this._insertDuplicatedSubtree(block, data.tasks)
            })
        })
    }

    deleteFromMenu(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        if (this._hasChildren(row)) {
            this._flashNotice("Delete subtasks first", "warning")
            return
        }
        this._deleteTask(row)
    }

    // --- Date and Assign popovers ---
    //
    // The pickers themselves are utils/task_popovers.js, shared with the task
    // pane. Date (due date, reminder, repeat) opens from the hover calendar
    // icon, the rail's date, repeat and reminder chips, "Due & repeat" and the
    // task pane; Assign from "Assign", the rail's avatars and the task pane.
    // Opened from the row, they write through the row - its rail, chips and
    // undo stay right.

    openDate(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeRowMenu(e)
        this._openDatePopover(row, this._popoverAnchor(e, row))
    }

    openAssign(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeRowMenu(e)
        this._openAssignPopover(row, this._popoverAnchor(e, row))
    }

    // Open beside the control that was clicked; a menu item's box goes away
    // with the menu, so from the menu it opens beside the row.
    _popoverAnchor(e, row) {
        const own = e.currentTarget instanceof Element ? e.currentTarget : null
        if (!own || own === this.element || own.closest(".dropdown-menu")) return row
        return own
    }

    _closePopover() {
        closeTaskPopover()
    }

    _openDatePopover(row, anchor) {
        const values = {
            dueDate: row.dataset.dueDate || "",
            remindTime: row.dataset.reminded === "true" ? "" : remindTimeOf(row.dataset.remindAt),
            recurrenceKind: row.dataset.recurrenceKind || "",
            recurrenceWeekday: row.dataset.recurrenceWeekday ?? "",
            recurrenceInterval: row.dataset.recurrenceInterval || ""
        }
        openDatePopover({
            anchor,
            fallback: row,
            values,
            onSave: (fd) => {
                const before = this._rowFields(row)
                this._resolveId(row.closest("[data-task-block]")).then((id) => {
                    if (!id) return
                    this._patch(`/tasks/${id}.json`, fd).then((data) => {
                        if (!data?.ok) {
                            this._flashNotice(data?.error || "Could not save", "warning")
                            return
                        }
                        this._applyTaskPayloadToRow(row, data.task)
                        // Undoable only when the date, repeat or reminder really changed.
                        if (JSON.stringify(this._rowFields(row)) !== JSON.stringify(before)) this._recordFields(row, "Due date", before)
                    })
                })
            }
        })
    }

    _openAssignPopover(row, anchor) {
        openAssignPopover({
            anchor,
            fallback: row,
            candidates: this._candidates(),
            selected: (row.dataset.assignees || "").split(",").filter(Boolean),
            chipFor: (candidate) => this._buildChipEl(candidate),
            onToggle: async (next, prev) => {
                row.dataset.assignees = next.join(",")
                const fd = new FormData()
                fd.set("assignees", next.join(","))
                const id = await this._resolveId(row.closest("[data-task-block]"))
                const data = id ? await this._patch(`/tasks/${id}.json`, fd) : null
                if (!data?.ok) {
                    row.dataset.assignees = prev.join(",")
                    this._flashNotice(data?.error || "Could not save", "warning")
                    return null
                }
                this._applyTaskPayloadToRow(row, data.task)
                this._acknowledgeVirtualAssignees(prev, data.task.assignees || [])
                return data.task.assignees || []
            }
        })
    }

    _acknowledgeVirtualAssignees(prevTokens, nextTokens) {
        if (!prevTokens.includes("jane") && nextTokens.includes("jane")) {
            this._flashNotice("Jane: on it - I'll keep an eye on this one.")
        }
    }

    _candidates() {
        if (this._candidatesCache) return this._candidatesCache
        let list = []
        try { list = JSON.parse(this.element.dataset.assigneeCandidates || "[]") } catch (_) { list = [] }
        this._candidatesCache = list
        return list
    }

    _buildChipEl(candidate) {
        if (candidate.avatar_url) {
            const img = document.createElement("img")
            img.className = "tasks-outline-chip tasks-outline-chip--img"
            img.src = candidate.avatar_url
            img.alt = candidate.label
            img.title = candidate.label
            return img
        }
        const span = document.createElement("span")
        span.className = "tasks-outline-chip"
        span.title = candidate.label
        // hue "contact" -> the gray tone that matches the .task-mention--contact
        // chip; null -> the default blue; a number -> that user's stable hue.
        span.style.background = candidate.hue === "contact"
            ? "hsl(215, 12%, 55%)"
            : `hsl(${candidate.hue == null ? 210 : candidate.hue}, 60%, 45%)`
        span.textContent = candidate.initials || "?"
        return span
    }

    _applyTaskPayloadToRow(row, task) {
        row.dataset.dueDate = task.due_date || ""
        row.dataset.recurrenceKind = task.recurrence_kind || ""
        row.dataset.recurrenceInterval = task.recurrence_interval == null ? "" : String(task.recurrence_interval)
        row.dataset.recurrenceWeekday = task.recurrence_weekday == null ? "" : String(task.recurrence_weekday)
        if ("remind_at" in task) {
            row.dataset.remindAt = task.remind_at || ""
            row.dataset.reminded = task.reminded ? "true" : "false"
        }
        row.dataset.assignees = (task.assignees || []).join(",")
        if ("notes" in task) row.dataset.notes = task.notes || ""
        this._renderNotes(row)
        this._renderRail(row)
    }

    // The note itself lives in the task pane (task_panels/_panel); the row only
    // carries a note chip saying there is one, before the comment and file
    // chips. Kept in step with the server-rendered chip in _outline_block.
    _renderNotes(row) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        let chip = wrap.querySelector("[data-task-note-chip]")
        if (!(row.dataset.notes || "").trim()) {
            chip?.remove()
            return
        }
        if (chip) return
        chip = document.createElement("button")
        chip.type = "button"
        chip.className = "tasks-outline-comments tasks-outline-note-chip"
        chip.dataset.taskNoteChip = "true"
        chip.dataset.action = "click->tasks-outline#openNote"
        chip.title = "Note"
        chip.setAttribute("aria-label", "Open note")
        chip.innerHTML = '<i class="bi bi-card-text" aria-hidden="true"></i>'
        const next = wrap.querySelector("[data-task-comments-chip], [data-task-attachments-chip]")
        if (next) next.before(chip)
        else wrap.appendChild(chip)
    }

    // The row's note chip.
    openNote(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (row) this._openPane(row, "notes")
    }

    // Rebuilds the trailing rail (pin dot, recurrence, due label, avatar
    // chips) from the row's data attributes. Client-side rows show the row's
    // own assignees; parent rollups come from the server render.
    _renderRail(row) {
        let rail = row.querySelector("[data-task-rail]")
        if (!rail) {
            rail = document.createElement("div")
            rail.className = "tasks-outline-rail"
            rail.setAttribute("data-task-rail", "true")
            const actions = row.querySelector(".tasks-outline-actions")
            if (actions) actions.before(rail)
            else row.appendChild(rail)
        }
        rail.innerHTML = ""

        if (row.dataset.pinned === "true") {
            const pin = document.createElement("i")
            pin.className = "bi bi-pin-angle-fill tasks-outline-rail-pin"
            pin.title = "Pinned"
            rail.appendChild(pin)
        }

        const kind = row.dataset.recurrenceKind || ""
        const dueDate = row.dataset.dueDate || ""
        if (kind) {
            const recur = document.createElement("span")
            recur.className = "tasks-outline-rail-recur"
            this._makeRailChipEditable(recur)
            const summary = this._recurrenceSummary(kind, row.dataset.recurrenceInterval, row.dataset.recurrenceWeekday)
            recur.title = summary
            recur.innerHTML = "<i class=\"bi bi-arrow-repeat\"></i>"
            if (!dueDate) {
                const label = document.createElement("span")
                label.className = "tasks-outline-rail-recur-label"
                label.textContent = summary
                recur.appendChild(label)
            }
            rail.appendChild(recur)
        }

        const due = this._dueLabel(dueDate)
        this._renderDueAction(row, due)
        if (due) {
            const span = document.createElement("span")
            span.className = `tasks-outline-rail-due is-${due.state}`
            this._makeRailChipEditable(span)
            span.textContent = due.text
            rail.appendChild(span)
        }

        const remind = row.dataset.reminded === "true" ? null : this._remindLabel(row.dataset.remindAt, dueDate)
        if (remind) {
            const chip = document.createElement("span")
            chip.className = "tasks-outline-rail-remind"
            chip.title = "Reminder - change"
            chip.dataset.action = "click->tasks-outline#openDate"
            chip.innerHTML = '<i class="bi bi-alarm"></i><span class="tasks-outline-rail-remind-label"></span>'
            chip.lastChild.textContent = remind
            rail.appendChild(chip)
        }

        const tokens = (row.dataset.assignees || "").split(",").filter(Boolean)
        if (tokens.length > 0) {
            const wrap = document.createElement("span")
            wrap.className = "tasks-outline-rail-avatars"
            // The avatars open Assign. Mirrors _outline_block.html.slim.
            wrap.setAttribute("role", "button")
            wrap.tabIndex = 0
            wrap.title = "Assignees - change"
            wrap.dataset.action = "click->tasks-outline#openAssign keydown.enter->tasks-outline#openAssign"
            const byToken = new Map(this._candidates().map((c) => [c.token, c]))
            tokens.slice(0, 3).forEach((token) => {
                const candidate = byToken.get(token)
                if (candidate) wrap.appendChild(this._buildChipEl(candidate))
            })
            if (tokens.length > 3) {
                const more = document.createElement("span")
                more.className = "tasks-outline-chip tasks-outline-chip--more"
                more.textContent = `+${tokens.length - 3}`
                wrap.appendChild(more)
            }
            rail.appendChild(wrap)
        }
    }

    // "2026-10-16" + "15:00" in the browser's zone, as the instant the server
    // stores.
    _localInstant(dateStr, time) {
        const [y, mo, d] = dateStr.split("-").map(Number)
        const [h, mi] = time.split(":").map(Number)
        return new Date(y, mo - 1, d, h, mi).toISOString()
    }

    _parseInstant(iso) {
        if (!iso) return null
        const at = new Date(iso)
        return Number.isNaN(at.getTime()) ? null : at
    }

    // The rail's alarm: "3 PM" on the due day, else "Oct 16, 3 PM".
    _remindLabel(iso, dueDate) {
        const at = this._parseInstant(iso)
        if (!at) return null
        const time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).replace(/:00(?=\s?[AP]M)/i, "")
        const day = this._isoDate(at)
        if (day === dueDate) return time
        return `${at.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`
    }

    _dueLabel(dateStr) {
        if (!dateStr) return null
        const parts = dateStr.split("-").map(Number)
        const date = new Date(parts[0], parts[1] - 1, parts[2])
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const dayMs = 86400000
        const diff = Math.round((date - today) / dayMs)
        const short = date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        if (diff < 0) return { text: diff === -1 ? "Yesterday" : short, state: "overdue" }
        if (diff === 0) return { text: "Today", state: "today" }
        if (diff === 1) return { text: "Tomorrow", state: "upcoming" }
        if (diff < 7) return { text: date.toLocaleDateString(undefined, { weekday: "short" }), state: "upcoming" }
        return { text: short, state: "upcoming" }
    }

    _recurrenceSummary(kind, interval, weekday) {
        if (kind === "daily") return "Every day"
        if (kind === "monthly") return "Every month"
        if (kind === "weekly") return `Every ${WEEKDAYS[parseInt(weekday || "0", 10)] || "week"}`
        if (kind === "every_n_days") {
            const n = parseInt(interval || "0", 10)
            return n === 1 ? "Every day" : `Every ${n} days`
        }
        return ""
    }

    _isoDate(d) {
        const pad = (n) => String(n).padStart(2, "0")
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    }

    // Inserts the next occurrence spawned by completing a recurring task.
    _insertSpawned(block, spawned) {
        if (!spawned) return
        const newBlock = this._buildBlockFromPayload(spawned)
        block.after(newBlock)
        this._recomputeDepth(newBlock)
        this._flashNotice("Recurring task: next occurrence added")
    }

    _setCollapsed(row, next) {
        const id = row.dataset.taskId
        if ((row.dataset.collapsed === "true") === next) return
        row.dataset.collapsed = String(next)

        const children = this.element.querySelector(`[data-children-for="${CSS.escape(id)}"]`)
        if (children) children.classList.toggle("is-collapsed", next)

        const fd = new FormData()
        fd.set("collapsed", next ? "1" : "0")
        this._resolveId(row.closest("[data-task-block]")).then((realId) => {
            this._patch(`/tasks/${realId}.json`, fd)
        })
    }

    onKeyDown(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return

        const input = row.querySelector("[data-task-input]")
        if (!input) return

        // While the @-mention dropdown is open it owns Arrow/Enter/Escape/Tab so
        // the keystroke picks a mention instead of navigating the outline.
        if (this._tagToken && this._handleTagTokenKeydown(e)) return
        if (this._mention && this._handleMentionKeydown(e)) return
        if (this._slash && this._handleSlashKeydown(e)) return

        const title = input.textContent || ""
        const mod = e.metaKey || e.ctrlKey
        const arrowVertical = e.key === "ArrowUp" || e.key === "ArrowDown"

        // Formatting shortcuts (Cmd/Ctrl+B/I/E/K, Cmd/Ctrl+Shift+X). Handled
        // explicitly so all five formats behave the same and serialize into
        // the markdown subset. Plain Cmd+X (cut) is left to the browser.
        // ⌘Z / ⌘⇧Z / Ctrl+Y: the outline's own history, not the browser's -
        // formatting rewrites the editor's DOM, which the native stack can't
        // follow (see "Undo").
        if (mod && !e.altKey && (e.key.toLowerCase() === "z" || (!e.shiftKey && e.key.toLowerCase() === "y" && !e.metaKey))) {
            e.preventDefault()
            const redo = e.key.toLowerCase() === "y" || e.shiftKey
            if (redo) this._redo(row)
            else this._undo(row)
            return
        }

        if (mod && !e.altKey) {
            const key = e.key.toLowerCase()
            if (!e.shiftKey && key === "b") { e.preventDefault(); this._applyFormat(row, "bold"); return }
            if (!e.shiftKey && key === "i") { e.preventDefault(); this._applyFormat(row, "italic"); return }
            if (e.shiftKey && key === "x") { e.preventDefault(); this._applyFormat(row, "strike"); return }
            if (!e.shiftKey && key === "e") { e.preventDefault(); this._applyFormat(row, "code"); return }
            if (!e.shiftKey && key === "k") { e.preventDefault(); this._enterLinkMode(row); return }
            if (e.shiftKey && key === "h") { e.preventDefault(); this._applyRecentStyle(row); return }
            if (e.shiftKey && key === "m") { e.preventDefault(); this._commentFromSelection(); return }
            // The browser would underline, but underline isn't in the title
            // format (Slack can't carry it), so it vanished on save. Do nothing
            // rather than something that silently disappears.
            if (!e.shiftKey && key === "u") { e.preventDefault(); return }
        }

        // ⌥⌘0-3 (ClickUp's): Text / Heading 1-3. Read off e.code - Option
        // rewrites e.key on a Mac ("¡" for ⌥1).
        // AltGr on Windows reports Ctrl+Alt - and is how AZERTY types @ and #.
        if (mod && e.altKey && !e.shiftKey && !e.getModifierState?.("AltGraph")) {
            const digit = (e.code || "").match(/^(?:Digit|Numpad)([0-3])$/)
            if (digit) {
                e.preventDefault()
                this._setRowTextStyle(row, digit[1] === "0" ? "" : `h${digit[1]}`)
                return
            }
        }

        // ⇧↑ with the caret at the start (⇧↓ at the end) has no more text to
        // select, so it goes on selecting ROWS - Notion's and Workflowy's way in.
        if (e.shiftKey && !mod && !e.altKey && arrowVertical && this._shiftArrowLeavesText(input, e.key)) {
            e.preventDefault()
            this._selectFromEditor(row, e.key === "ArrowUp" ? "prev" : "next")
            return
        }

        // Modifier combos on arrows must be handled before plain-arrow navigation.
        // Cmd/Ctrl+Shift+ArrowUp/Down reorders the task among its siblings.
        if (mod && e.shiftKey && arrowVertical) {
            e.preventDefault()
            this._moveAmongSiblings(row, e.key === "ArrowUp" ? "prev" : "next")
            return
        }

        // Cmd/Ctrl+ArrowUp collapses, Cmd/Ctrl+ArrowDown expands the current item's subtasks
        if (mod && arrowVertical) {
            e.preventDefault()
            if (this._hasChildren(row)) this._setCollapsed(row, e.key === "ArrowUp")
            return
        }

        // Cmd/Ctrl+Enter toggles complete/uncomplete on the current task
        if (e.key === "Enter" && mod) {
            e.preventDefault()
            if (e.repeat) return
            this._toggleCompleteForRow(row)
            return
        }

        // Shift+Enter is a deliberate no-op
        if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault()
            return
        }

        if (e.key === "Enter") {
            e.preventDefault()
            if (e.repeat) return
            this._handleEnter(row, input)
            return
        }

        if (e.key === "Backspace") {
            // At the very start of a heading, Backspace first turns it back
            // into an ordinary row (ClickUp/Notion) - a second Backspace merges
            // or deletes as usual. Without this a stray Backspace would fold a
            // section label into the task above it.
            const atStart = this._selectionOffsets(input)
            if (this._isHeading(row) && atStart && atStart.start === 0 && atStart.end === 0) {
                e.preventDefault()
                this._setRowTextStyle(row, "")
                return
            }
            if (title.trim() === "") {
                e.preventDefault()
                if (this._hasChildren(row)) {
                    // Never optimistically delete an item with children: the server
                    // refuses non-recursive deletes. Outdent instead (no-op at root).
                    if (row.dataset.parentId) this._outdent(row)
                    return
                }
                this._deleteTask(row)
                return
            }
            const sel = this._selectionOffsets(input)
            if (sel && sel.start === 0 && sel.end === 0) {
                if (!this._hasChildren(row)) {
                    e.preventDefault()
                    this._mergeIntoPrevious(row, input)
                }
                return
            }
            return
        }

        if (e.key === "Tab") {
            e.preventDefault()
            if (e.shiftKey) this._outdent(row)
            else this._indent(row)
            return
        }

        // Plain arrows only: leave Shift/Alt combos to the browser (text selection, etc.)
        if (mod || e.shiftKey || e.altKey) return

        if (arrowVertical) {
            e.preventDefault()
            const sel = this._selectionOffsets(input)
            this._focusNeighbor(row, e.key === "ArrowUp" ? "prev" : "next", sel ? sel.start : null)
            return
        }

        if (e.key === "ArrowLeft") {
            const sel = this._selectionOffsets(input)
            if (sel && sel.start === 0 && sel.end === 0) {
                const prev = this._neighborRow(row, "prev")
                if (prev) {
                    e.preventDefault()
                    this._setEditing(prev.dataset.taskId)
                }
            }
            return
        }

        if (e.key === "ArrowRight") {
            const sel = this._selectionOffsets(input)
            if (sel && sel.start === title.length && sel.end === title.length) {
                const next = this._neighborRow(row, "next")
                if (next) {
                    e.preventDefault()
                    this._setEditing(next.dataset.taskId, 0)
                }
            }
            return
        }

        if (e.key === "Escape") {
            e.preventDefault()
            this._stopEditing()
        }
    }

    _handleEnter(row, input) {
        const block = row.closest("[data-task-block]")
        // "Call Bob tomorrow" + Enter: the date lands on this task, not on the
        // new row the split creates.
        this._applyDatePhrase(row, input)
        const title = input.textContent || ""
        // Enter on the untouched empty-list placeholder: nothing to create yet.
        if (row.dataset.placeholderPending && title.trim() === "") return
        const parentId = row.dataset.parentId || ""
        const parentBlock = parentId
            ? this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(parentId)}"]`)
            : null

        // Empty indented item: outdent instead of creating a sibling
        if (title.trim() === "" && parentId) {
            this._outdent(row)
            return
        }

        // Empty root item: there is nothing to split off, so Enter must not
        // stack another blank sibling below it — that is a prime way spurious
        // empty rows pile up. The trailing "Add a task" placeholder remains the
        // one blank-row affordance.
        if (title.trim() === "") return

        const sel = this._selectionOffsets(input) || { start: title.length, end: title.length }

        // Enter at position 0 on a non-empty item: new empty task above, keep focus here
        if (sel.start === 0 && sel.end === 0 && title.trim() !== "") {
            const prevBlock = this._previousSiblingBlock(block)
            this._createTask({ parentBlock: parentBlock, afterBlock: prevBlock, beforeBlock: prevBlock ? null : block, title: "", focusNew: false })
            return
        }

        // Split at the cursor via DOM ranges so inline formatting survives on
        // both sides: the caret-to-end fragment seeds the new row, selected
        // text (if any) is dropped, exactly like the old textarea slice.
        const afterFragment = this._extractEditorRange(input, sel.end, title.length)
        if (sel.end > sel.start) this._extractEditorRange(input, sel.start, sel.end)
        this._saveTitle(row)

        // Expanded item with children: new item becomes its first child (Workflowy convention)
        const childBlocks = this._childBlocks(row)
        if (childBlocks.length > 0 && row.dataset.collapsed !== "true") {
            this._createTask({ parentBlock: block, beforeBlock: childBlocks[0], contentFragment: afterFragment, focusNew: true, focusCaret: 0 })
        } else {
            this._createTask({ parentBlock: parentBlock, afterBlock: block, contentFragment: afterFragment, focusNew: true, focusCaret: 0 })
        }
    }

    _mergeIntoPrevious(row, input) {
        const prevRow = this._neighborRow(row, "prev")
        if (!prevRow) return
        const prevInput = prevRow.querySelector("[data-task-input]")
        if (!prevInput) return

        const block = row.closest("[data-task-block]")
        const joinPoint = (prevInput.textContent || "").length

        if (row._saveTimer) {
            clearTimeout(row._saveTimer)
            row._saveTimer = null
        }

        // Move the row's editor DOM into the previous editor so formatting
        // survives the merge.
        while (input.firstChild) prevInput.appendChild(input.firstChild)
        this._saveTitle(prevRow)

        const parentId = row.dataset.parentId || ""
        const container = block.parentElement
        const anchor = block.nextElementSibling
        block.remove()
        this._refreshCaretFor(parentId)
        this._setEditing(prevRow.dataset.taskId, joinPoint)

        this._resolveId(block).then((id) => {
            this._delete(`/tasks/${id}.json`).then((data) => {
                if (data?.ok) return
                this._restoreBlock(block, container, anchor)
                this._refreshCaretFor(parentId)
            })
        })
    }

    _moveAmongSiblings(row, dir) {
        const block = row.closest("[data-task-block]")
        const container = block.parentElement
        if (!container) return
        const target = dir === "prev" ? this._previousSiblingBlock(block) : this._nextSiblingBlock(block)
        if (!target) return

        const input = row.querySelector("[data-task-input]")
        const sel = input ? this._selectionOffsets(input) : null
        const caret = sel ? sel.start : null

        const before = this._placeOf(block)
        if (dir === "prev") target.before(block)
        else target.after(block)
        this._recordMove(block, before, "Move")

        const parentId = row.dataset.parentId || ""
        const fd = new FormData()
        fd.set("parent_id", parentId === "" ? "root" : parentId)
        const anchorAfter = this._previousSiblingBlock(block)
        const anchorBefore = anchorAfter ? null : this._nextSiblingBlock(block)
        Promise.all([this._resolveId(block), this._resolveId(anchorAfter), this._resolveId(anchorBefore)]).then(([id, afterId, beforeId]) => {
            if (afterId) fd.set("after_id", afterId)
            else if (beforeId) fd.set("before_id", beforeId)
            this._post(`/tasks/${id}/move.json`, fd)
        })

        // Moving a focused element in the DOM blurs it; restore focus and caret
        this._setEditing(row.dataset.taskId, caret)
    }

    // --- Paste (multi-line text -> multiple tasks) ---
    // Pasted text is treated as inline-markdown source, so the outline's own
    // copy output round-trips with formatting; rich clipboard HTML is
    // translated into the subset (bold/italic/strike/code/links) instead of
    // being dumped raw into the contenteditable. Multi-line pastes split
    // Taskade-style: the first line merges into the current row at the caret,
    // and every following line becomes its own task — indentation (or list
    // nesting) mapped to subtasks, bullets / numbers / checkbox markers
    // stripped, "[x]" items created as completed. Everything after the first
    // line goes to the server as one batch request.

    onPaste(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const input = row.querySelector("[data-task-input]")
        if (!input || !e.clipboardData) return
        if (this._pasteFilesAsAttachments(e, row)) return
        const text = e.clipboardData.getData("text/plain")

        if (!/[\r\n]/.test(text)) {
            // Single-line paste: never let the browser insert arbitrary HTML.
            e.preventDefault()
            // A URL pasted over selected words links them (Notion, Slack,
            // Linear, Google Docs) instead of replacing them with the URL.
            const url = text.trim()
            const sel = window.getSelection()
            if (/^(https?:\/\/|mailto:)\S+$/i.test(url) && sel && !sel.isCollapsed && sel.rangeCount && input.contains(sel.getRangeAt(0).commonAncestorContainer)) {
                try { document.execCommand("createLink", false, url) } catch (_) {}
                input.querySelectorAll("a").forEach((a) => {
                    a.setAttribute("target", "_blank")
                    a.setAttribute("rel", "noopener noreferrer")
                })
                this._scheduleSave(row)
                return
            }
            const html = e.clipboardData.getData("text/html")
            // Our own copy flavors carry a data-jane-tasks marker; for those we
            // read text/plain so paste-back stays byte-for-byte identical.
            // External rich HTML still converts to the inline-markdown subset.
            const useHtml = html && !html.includes("data-jane-tasks")
            const md = useHtml ? this._markdownFromPastedHtml(html) : text
            this._insertMarkdownAtCaret(input, md)
            this._scheduleSave(row)
            return
        }

        const items = this._parsePastedOutline(text)
        if (items.length === 0) {
            e.preventDefault()
            return
        }
        e.preventDefault()

        // First line merges into the current row at the caret.
        const first = items[0]
        this._insertMarkdownAtCaret(input, first.title)
        this._saveTitle(row)

        const rest = items.slice(1)
        if (rest.length === 0) return

        const block = row.closest("[data-task-block]")
        this._resolveId(block).then((anchorId) => {
            if (!anchorId) return
            const fd = new FormData()
            fd.set("anchor_id", anchorId)
            fd.set("items", JSON.stringify(rest))
            this._post("/tasks/create_batch.json", fd).then((data) => {
                if (!data?.ok || !Array.isArray(data.tasks)) {
                    this._flashNotice(data?.error || "Could not paste tasks", "warning")
                    return
                }
                this._insertPastedTasks(block, data.tasks)
            })
        })
    }

    // Parses pasted plain text into { title, depth, completed } items.
    // Depth comes from leading whitespace (tab = 4 spaces) via an indent
    // stack, so 2-space, 4-space and tab-indented lists all map to levels
    // and irregular indents clamp instead of exploding. Blank lines are
    // skipped; lines past PASTE_LIMIT fold into one final task.
    _parsePastedOutline(text) {
        const items = []
        const indentStack = []
        text.replace(/\r\n?/g, "\n").split("\n").forEach((rawLine) => {
            if (rawLine.trim() === "") return
            const expanded = rawLine.replace(/\t/g, "    ")
            const indent = expanded.match(/^ */)[0].length
            const { title, completed, textStyle } = this._stripListMarker(expanded.trim())
            if (title === "") return
            if (items.length === 0) {
                indentStack.push(indent)
            } else {
                while (indentStack.length > 1 && indent < indentStack[indentStack.length - 1]) indentStack.pop()
                if (indent > indentStack[indentStack.length - 1]) indentStack.push(indent)
            }
            items.push(textStyle ? { title, depth: indentStack.length - 1, completed: false, text_style: textStyle } : { title, depth: indentStack.length - 1, completed })
        })
        if (items.length > PASTE_LIMIT) {
            const overflow = items.splice(PASTE_LIMIT - 1)
            items.push({ title: overflow.map((it) => it.title).join(" · "), depth: 0, completed: false })
        }
        return items
    }

    // Strips a leading list marker: "- [x] " / "[ ] " checkboxes (completed
    // state kept), "-", "*", "+", "•" bullets, "1." / "1)" numbering. Markers
    // need trailing whitespace so titles like "-5 degrees" survive intact.
    //
    // "# ", "## ", "### " (markdown headings, and what our own copy writes for
    // a heading row) become section headings - the trailing space is required,
    // so "#1 priority" and "#tag" stay plain titles.
    _stripListMarker(line) {
        const heading = line.match(/^(#{1,3})\s+(.*)$/)
        if (heading) return { title: heading[2].trim(), completed: false, textStyle: `h${heading[1].length}` }
        const checkbox = line.match(/^(?:[-*+•‣◦▪–]\s+)?\[( |x|X)\]\s+(.*)$/)
        if (checkbox) return { title: checkbox[2].trim(), completed: checkbox[1].toLowerCase() === "x" }
        const bullet = line.match(/^[-*+•‣◦▪–]\s+(.*)$/)
        if (bullet) return { title: bullet[1].trim(), completed: false }
        const numbered = line.match(/^\d{1,3}[.)]\s+(.*)$/)
        if (numbered) return { title: numbered[1].trim(), completed: false }
        return { title: line, completed: false }
    }

    // Renders the batch-created tasks. Payloads arrive in outline (preorder)
    // order, so each task's parent block — the anchor or an earlier payload —
    // is already in the DOM when its block is attached.
    _insertPastedTasks(anchorBlock, tasks) {
        const anchorRow = anchorBlock.querySelector("[data-task-row]")
        const anchorId = String(anchorBlock.dataset.taskId)
        const blocksById = new Map()
        const parentKeys = new Set()
        let lastSibling = anchorBlock
        tasks.forEach((task) => {
            const block = this._buildBlockFromPayload(task)
            const parentKey = task.parent_id == null ? "" : String(task.parent_id)
            if (blocksById.has(parentKey)) {
                blocksById.get(parentKey).querySelector("[data-children-for]").appendChild(block)
            } else if (parentKey === anchorId) {
                if (anchorRow.dataset.collapsed === "true") {
                    anchorRow.dataset.collapsed = "false"
                    this.element.querySelector(`[data-children-for="${CSS.escape(anchorId)}"]`)?.classList.remove("is-collapsed")
                }
                this.element.querySelector(`[data-children-for="${CSS.escape(anchorId)}"]`)?.appendChild(block)
            } else {
                lastSibling.after(block)
                lastSibling = block
            }
            this._recomputeDepth(block)
            blocksById.set(String(task.id), block)
            parentKeys.add(parentKey)
        })
        parentKeys.forEach((key) => this._refreshCaretFor(key))
    }

    // --- Copy (selection spanning task rows -> indented plain text) ---
    // When a selection covers two or more rows, the clipboard gets one line
    // per task indented two spaces per level — the exact format onPaste (and
    // the row menu's "Copy task and subtask text") parses back into the same
    // hierarchy. Selections inside a single row keep native copy behavior.

    onCopy(e) {
        if (!e.clipboardData) return
        if (e.target.closest("[data-task-input]")) return
        const selection = window.getSelection()
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) return
        const rows = Array.from(this.element.querySelectorAll("[data-task-row]"))
            .filter((r) => r.offsetParent !== null && selection.containsNode(r, true))
        if (rows.length < 2) return
        const minDepth = Math.min(...rows.map((r) => Number(r.dataset.depth || "0")))
        const lines = rows
            .map((r) => `${"  ".repeat(Math.max(0, Number(r.dataset.depth || "0") - minDepth))}${this._headingPrefix(r)}${this._rowTitle(r)}`)
            .filter((line) => line.trim() !== "")
        if (lines.length === 0) return
        e.preventDefault()
        e.clipboardData.setData("text/plain", lines.join("\n"))
    }

    // Debounced autosave while typing, so titles persist without pressing Enter
    onInput(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const input = row.querySelector("[data-task-input]")
        if (!input) return
        // Deleting the last character can leave a stray <br> that breaks the
        // :empty placeholder; normalize a visually-empty editor to truly empty.
        if ((input.textContent || "") === "" && input.innerHTML !== "") input.innerHTML = ""
        if (this._applyHeadingMarkdown(row, input)) return
        this._detectMention(input)
        this._detectTagToken(input)
        this._detectSlash(row, input)
        this._scheduleSave(row, { typing: true })
    }

    _scheduleSave(row, { typing = false } = {}) {
        this._noteText(row, { typing })
        // Every edit lands here - typing, paste, formatting, a mention, undo -
        // so the date chip never outlives (or misses) the words it is about.
        if (this._editingId === row.dataset.taskId) {
            const input = row.querySelector("[data-task-input]")
            if (input && !input.hidden) this._detectDatePhrase(row, input)
        }
        if (row._saveTimer) clearTimeout(row._saveTimer)
        row._saveTimer = setTimeout(() => {
            row._saveTimer = null
            this._saveTitle(row)
        }, 500)
    }

    onBlur(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const input = row.querySelector("[data-task-input]")
        if (!input) return
        // Focus moving into the floating toolbar (link URL input) is not a
        // real blur — editing continues once the link is applied.
        if (this._toolbar && (this._linkMode || this._panelMode || this._toolbar.contains(e.relatedTarget))) return
        // Blur dismisses the mention and tag dropdowns; picking an item uses
        // mousedown (preventDefault) so it fires before this and isn't lost.
        this._closeMention()
        this._closeTagToken()
        this._closeSlash()
        this._applyDatePhrase(row, input)
        this._saveTitle(row)
        // _setEditing fires a synchronous blur on the old input while moving
        // focus to another row; _editingId already points at the new row then,
        // and unconditionally stopping would hide the new input and cancel the
        // navigation (this is what broke ArrowUp: with the target row earlier
        // in DOM order the teardown ran after its input was shown). Only stop
        // editing when the blurred row is still the one being edited.
        if (this._editingId === row.dataset.taskId) this._stopEditing()
        this._cleanupPlaceholder(row)
    }

    // --- Drag to move ---

    onDragStart(e) {
        // Manual order is off, so a drop has nowhere to land — see the note in
        // _outline.html.slim. Refusing the drag beats accepting it and silently
        // discarding the result on the next render.
        if (this.prioritySortedValue) {
            e.preventDefault()
            this._flashNotice("Sorted by priority — switch to manual order to rearrange", "warning")
            return
        }
        const block = e.target.closest("[data-task-block]")
        if (!block) return
        this._draggingBlock = block
        block.classList.add("is-dragging")
        e.dataTransfer.effectAllowed = "move"
        try { e.dataTransfer.setData("text/plain", block.dataset.taskId) } catch (_) {}
    }

    onDragEnd() {
        if (this._draggingBlock) this._draggingBlock.classList.remove("is-dragging")
        this._clearDropTargets()
        this._draggingBlock = null
    }

    onDragOver(e) {
        // A file from the desktop, not a row being moved: attach it to the row
        // under the pointer (Taskade's drop-a-file-on-a-task).
        if (!this._draggingBlock && dragHasFiles(e)) {
            const row = e.target.closest("[data-task-row]")
            if (!row || row.dataset.placeholderPending) return
            e.preventDefault()
            e.dataTransfer.dropEffect = "copy"
            row.classList.add("is-file-drop-target")
            return
        }
        if (!this._draggingBlock) return
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const targetBlock = row.closest("[data-task-block]")
        if (!targetBlock || targetBlock === this._draggingBlock) return
        if (this._draggingBlock.contains(targetBlock)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"
        this._showDropIndicator(row, this._dropPosition(row, e.clientY))
    }

    onDrop(e) {
        if (!this._draggingBlock && dragHasFiles(e)) {
            const row = e.target.closest("[data-task-row]")
            if (!row) return
            e.preventDefault()
            row.classList.remove("is-file-drop-target")
            this._attachTo(row, Array.from(e.dataTransfer.files || []))
            return
        }
        if (!this._draggingBlock) return
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const targetBlock = row.closest("[data-task-block]")
        if (!targetBlock || targetBlock === this._draggingBlock) return
        if (this._draggingBlock.contains(targetBlock)) return
        e.preventDefault()
        this._dropAt(this._draggingBlock, targetBlock, this._dropPosition(row, e.clientY))
    }

    onDragLeave(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        row.classList.remove("is-drop-target", "is-drop-before", "is-drop-after")
        if (!row.contains(e.relatedTarget)) row.classList.remove("is-file-drop-target")
    }

    // Which side of the target row the pointer is over: the top half means the
    // dragged item should land above it, the bottom half below. This is what
    // makes dropping into the FIRST position possible (a bottom-only drop can
    // only ever reach 2nd place).
    _dropPosition(row, clientY) {
        const rect = row.getBoundingClientRect()
        return clientY < rect.top + rect.height / 2 ? "before" : "after"
    }

    // Insertion line on the top (before) or bottom (after) edge of the row.
    _showDropIndicator(row, position) {
        this._clearDropTargets()
        row.classList.add("is-drop-target", position === "before" ? "is-drop-before" : "is-drop-after")
    }

    // Reparent + reorder `dragging` so it sits directly before or after
    // `targetBlock`, then persist. Shared by HTML5 drop (desktop) and touch
    // drop (mobile). Anchor logic mirrors _moveAmongSiblings: prefer after_id
    // (the new previous sibling); fall back to before_id (the new next sibling)
    // so a drop into first place is expressed as before_id = the old first row.
    _dropAt(dragging, targetBlock, position) {
        const targetRow = targetBlock.querySelector("[data-task-row]")
        const parentId = targetRow.dataset.parentId || ""

        const before = this._placeOf(dragging)
        if (position === "before") targetBlock.before(dragging)
        else targetBlock.after(dragging)
        this._recordMove(dragging, before, "Move")
        this._clearDropTargets()

        const anchorAfter = this._previousSiblingBlock(dragging)
        const anchorBefore = anchorAfter ? null : this._nextSiblingBlock(dragging)
        Promise.all([this._resolveId(dragging), this._resolveId(anchorAfter), this._resolveId(anchorBefore)]).then(([id, afterId, beforeId]) => {
            const fd = new FormData()
            fd.set("parent_id", parentId === "" ? "root" : parentId)
            if (afterId) fd.set("after_id", afterId)
            else if (beforeId) fd.set("before_id", beforeId)
            this._post(`/tasks/${id}/move.json`, fd).then((data) => {
                if (!data?.ok) return
                this._applyParent(dragging, data.task.parent_id)
            })
        })
    }

    // --- Touch drag (mobile) ---
    // HTML5 drag events never fire from touch input, so the handle also
    // drives a manual touch drag: the finger carries a ghost pill, the row
    // under the finger gets the same is-drop-target insertion line, and
    // lifting the finger performs the same drop as desktop.

    onTouchStart(e) {
        const handle = e.target.closest(".tasks-outline-handle")
        if (!handle) return
        const block = handle.closest("[data-task-block]")
        if (!block) return
        const touch = e.touches[0]
        this._touchDrag = { block, targetBlock: null, position: null, ghost: null, startX: touch.clientX, startY: touch.clientY, active: false }
    }

    onTouchMove(e) {
        if (!this._touchDrag) return
        const touch = e.touches[0]
        if (!touch) return
        e.preventDefault()

        if (!this._touchDrag.active) {
            const moved = Math.hypot(touch.clientX - this._touchDrag.startX, touch.clientY - this._touchDrag.startY)
            if (moved < 6) return
            this._touchDrag.active = true
            this._touchDrag.block.classList.add("is-dragging")
            this._touchDrag.ghost = this._buildTouchGhost(this._touchDrag.block)
        }

        this._positionTouchGhost(touch)
        this._autoScrollForTouch(touch)

        const under = document.elementFromPoint(touch.clientX, touch.clientY)
        const row = under ? under.closest("[data-task-row]") : null
        this._clearDropTargets()
        this._touchDrag.targetBlock = null
        if (!row) return
        const targetBlock = row.closest("[data-task-block]")
        if (!targetBlock || targetBlock === this._touchDrag.block) return
        if (this._touchDrag.block.contains(targetBlock)) return
        const position = this._dropPosition(row, touch.clientY)
        this._showDropIndicator(row, position)
        this._touchDrag.targetBlock = targetBlock
        this._touchDrag.position = position
    }

    onTouchEnd() {
        if (!this._touchDrag) return
        const { block, targetBlock, position, ghost, active } = this._touchDrag
        this._touchDrag = null
        if (ghost) ghost.remove()
        block.classList.remove("is-dragging")
        if (!active) return
        if (targetBlock) {
            this._dropAt(block, targetBlock, position || "after")
        } else {
            this._clearDropTargets()
        }
    }

    onTouchCancel() {
        if (!this._touchDrag) return
        if (this._touchDrag.ghost) this._touchDrag.ghost.remove()
        this._touchDrag.block.classList.remove("is-dragging")
        this._touchDrag = null
        this._clearDropTargets()
    }

    _buildTouchGhost(block) {
        const title = this._rowTitlePlain(block.querySelector("[data-task-row]")) || "Task"
        const ghost = document.createElement("div")
        ghost.className = "tasks-outline-drag-ghost"
        ghost.textContent = title.length > 40 ? `${title.slice(0, 40)}…` : title
        document.body.appendChild(ghost)
        return ghost
    }

    _positionTouchGhost(touch) {
        const ghost = this._touchDrag?.ghost
        if (!ghost) return
        ghost.style.left = `${touch.clientX}px`
        ghost.style.top = `${touch.clientY}px`
    }

    // Nudge the page when the finger is near a viewport edge so long lists
    // can be reordered end to end in one gesture.
    _autoScrollForTouch(touch) {
        const margin = 80
        const step = 14
        if (touch.clientY < margin) window.scrollBy(0, -step)
        else if (touch.clientY > window.innerHeight - margin) window.scrollBy(0, step)
    }

    _clearDropTargets() {
        this.element.querySelectorAll(".is-drop-target").forEach((el) => el.classList.remove("is-drop-target", "is-drop-before", "is-drop-after"))
    }

    // --- Indent / outdent ---

    _indent(row) {
        const prev = this._neighborRow(row, "prev")
        if (!prev) return
        // Standard outliner Tab: parent is the row above at the current depth
        // (walk up from prev when it is nested deeper — e.g. root item below a
        // parent-with-children nests under that parent, not under its last child).
        const targetParentDepth = Number(row.dataset.depth || "0")
        const parentRow = this._rowAtDepthFrom(prev, targetParentDepth)
        if (!parentRow) return
        const block = row.closest("[data-task-block]")
        const parentBlock = parentRow.closest("[data-task-block]")
        const container = parentBlock.querySelector(`[data-children-for]`)
        const before = this._placeOf(block)
        if (container) container.appendChild(block)
        this._recordMove(block, before, "Indent")
        this._applyParent(block, parentRow.dataset.taskId)
        this._persistMove(block, parentRow.dataset.taskId)
        this._refreshCaretFor(parentRow.dataset.taskId)
        this._setEditing(row.dataset.taskId)
    }

    _rowAtDepthFrom(row, targetDepth) {
        let current = row
        while (current && Number(current.dataset.depth || "0") > targetDepth) {
            const parentId = current.dataset.parentId
            if (!parentId) return null
            current = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(parentId)}"]`)
        }
        if (!current || Number(current.dataset.depth || "0") !== targetDepth) return null
        return current
    }

    _outdent(row) {
        const parentId = row.dataset.parentId
        if (!parentId) return
        const parentRow = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(parentId)}"]`)
        if (!parentRow) return
        const grand = parentRow.dataset.parentId || ""
        const block = row.closest("[data-task-block]")
        const parentBlock = parentRow.closest("[data-task-block]")
        // Move directly after the former parent so it stays visually adjacent
        const before = this._placeOf(block)
        parentBlock.after(block)
        this._recordMove(block, before, "Outdent")
        this._applyParent(block, grand || null)
        this._persistMove(block, grand || null, parentRow.dataset.taskId)
        this._refreshCaretFor(parentId)
        this._setEditing(row.dataset.taskId)
    }

    _persistMove(block, parentId, afterId) {
        this._resolveId(block).then((id) => {
            const fd = new FormData()
            fd.set("parent_id", parentId ? String(parentId) : "root")
            const afterPromise = afterId
                ? this._resolveId(this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(String(afterId))}"]`))
                : Promise.resolve(null)
            afterPromise.then((realAfterId) => {
                if (realAfterId) fd.set("after_id", realAfterId)
                this._post(`/tasks/${id}/move.json`, fd)
            })
        })
    }

    _applyParent(block, parentId) {
        const row = block.querySelector("[data-task-row]")
        const pid = parentId ? String(parentId) : ""
        row.dataset.parentId = pid
        this._recomputeDepth(block)
    }

    _recomputeDepth(block) {
        const row = block.querySelector("[data-task-row]")
        const depth = this._depthForRow(row)
        row.dataset.depth = String(depth)
        row.style.marginLeft = `${depth * INDENT}px`
        // Recompute descendants
        const container = block.querySelector(`[data-children-for]`)
        if (container) {
            container.querySelectorAll(":scope > [data-task-block]").forEach((child) => this._recomputeDepth(child))
        }
    }

    // --- Delete ---

    _deleteTask(row, { focusNeighbor = true } = {}) {
        // The unsaved placeholder row is the empty list's only affordance —
        // there is no record to delete and the row should stay.
        if (row.dataset.placeholderPending) return
        const block = row.closest("[data-task-block]")
        if (!block) return
        const parentId = row.dataset.parentId || ""
        const prev = this._neighborRow(row, "prev")
        const next = this._neighborRow(row, "next")
        const container = block.parentElement
        const anchor = block.nextElementSibling
        // Optimistic remove
        block.remove()
        this._refreshCaretFor(parentId)
        const focusRow = prev || next
        if (focusNeighbor && focusRow) this._setEditing(focusRow.dataset.taskId)
        // Deleting the last task must leave exactly one blank affordance rather
        // than an empty outline (which otherwise only reappears on reload).
        // _ensurePlaceholder is a no-op while any task remains, so it can never
        // stack a second placeholder.
        this._ensurePlaceholder()

        const send = ({ keepalive = false } = {}) => {
            this._resolveId(block).then((id) => {
                this._delete(`/tasks/${id}.json`, { keepalive }).then((data) => {
                    if (data?.ok) return
                    // Server refused — most often "Task has subtasks". Restoring the
                    // block silently is what made this read as the row refusing to
                    // go away, so say why instead of just putting it back.
                    this._restoreBlock(block, container, anchor)
                    this._refreshCaretFor(parentId)
                    this._flashNotice(data?.error || "Couldn't delete that task", "warning")
                })
            })
        }

        // Clearing an empty row while typing (Backspace) is editing, not a
        // decision worth undoing - it goes straight away.
        if (!this._rowTitle(row).trim()) {
            send()
            return
        }

        // Undo, as Gmail / Todoist do: the row leaves at once, the DELETE waits
        // out the toast. Leaving the page sends it immediately (keepalive), so a
        // delete is never lost - only delayed.
        const pending = { send, timer: null }
        pending.timer = setTimeout(() => {
            this._pendingDeletes.delete(pending)
            send()
        }, UNDO_DELETE_MS)
        this._pendingDeletes.add(pending)
        const restore = () => {
            if (!this._pendingDeletes.has(pending)) return false
            clearTimeout(pending.timer)
            this._pendingDeletes.delete(pending)
            this._removeUntouchedPlaceholder()
            this._restoreBlock(block, container, anchor)
            this._refreshCaretFor(parentId)
            this._setEditing(row.dataset.taskId)
            return true
        }
        showSystemToast("Task deleted", {
            level: "info",
            duration: UNDO_DELETE_MS,
            action: {
                label: "Undo",
                onClick: () => restore()
            }
        })
        // ⌘Z takes the same Undo while it is still on offer.
        this._recordAction({ label: "Delete", undo: restore })
    }

    // Deletes waiting out their Undo window go now - the page is going away.
    _flushPendingDeletes() {
        this._pendingDeletes?.forEach((pending) => {
            clearTimeout(pending.timer)
            pending.send({ keepalive: true })
        })
        this._pendingDeletes?.clear()
    }

    // Undoing the delete of a list's last task: drop the blank row that
    // _ensurePlaceholder put in its place, if nobody has typed into it.
    _removeUntouchedPlaceholder() {
        this.element.querySelectorAll("[data-task-row][data-placeholder-pending]").forEach((placeholder) => {
            const text = placeholder.querySelector("[data-task-input]")?.textContent || ""
            if (!text.trim()) placeholder.closest("[data-task-block]")?.remove()
        })
    }

    _restoreBlock(block, container, anchor) {
        if (anchor && anchor.parentElement === container) anchor.before(block)
        else if (container) container.appendChild(block)
    }

    // --- Title ---
    // The contenteditable editor DOM is the live source of truth while a row
    // exists client-side; saving serializes it to inline markdown for the
    // server and mirrors its HTML into the display span.

    _saveTitle(row) {
        if (row._saveTimer) {
            clearTimeout(row._saveTimer)
            row._saveTimer = null
        }
        const input = row.querySelector("[data-task-input]")
        if (!input) return
        this._noteText(row)
        const markdown = domToMarkdown(input).trim()
        const span = row.querySelector(".tasks-outline-title")
        if (span) span.innerHTML = input.innerHTML
        // Skip redundant PATCHes (e.g. blur firing right after Enter already saved)
        if (row._lastSavedTitle === markdown) return
        row._lastSavedTitle = markdown
        this._resolveId(row.closest("[data-task-block]")).then((id) => {
            const fd = new FormData()
            fd.set("title", markdown)
            this._patch(`/tasks/${id}.json`, fd)
        })
    }

    // --- Create (optimistic) ---

    _createTask({ parentBlock, afterBlock, beforeBlock, title, contentFragment, focusNew, focusCaret, textStyle }) {
        const newTitle = title || ""
        const parentRow = parentBlock ? parentBlock.querySelector("[data-task-row]") : null
        const parentDomId = parentRow ? parentRow.dataset.taskId : "root"

        const container = this.element.querySelector(`[data-children-for="${CSS.escape(parentDomId)}"]`)
        if (!container) return Promise.resolve()

        // If creating a subtask, make sure the parent is expanded
        if (parentRow && parentRow.dataset.collapsed === "true") {
            parentRow.dataset.collapsed = "false"
            container.classList.remove("is-collapsed")
        }

        const tempId = `tmp-${++this._tmpCounter}`
        const block = this._buildBlock({ id: tempId, title: newTitle, parentId: parentRow ? parentRow.dataset.taskId : "", textStyle })
        // An Enter-split hands the caret-to-end DOM fragment over so inline
        // formatting survives into the new row.
        if (contentFragment) {
            const editor = block.querySelector("[data-task-input]")
            const span = block.querySelector(".tasks-outline-title")
            editor.appendChild(contentFragment)
            if (span) span.innerHTML = editor.innerHTML
        }

        if (afterBlock && afterBlock.parentElement === container) {
            afterBlock.after(block)
        } else if (beforeBlock && beforeBlock.parentElement === container) {
            beforeBlock.before(block)
        } else {
            container.appendChild(block)
        }
        this._recomputeDepth(block)
        this._refreshCaretFor(parentRow ? parentRow.dataset.taskId : "")

        if (focusNew) this._setEditing(tempId, focusCaret)

        const parentIdPromise = parentBlock ? this._resolveId(parentBlock) : Promise.resolve(null)
        const afterIdPromise = afterBlock ? this._resolveId(afterBlock) : Promise.resolve(null)
        const beforeIdPromise = beforeBlock ? this._resolveId(beforeBlock) : Promise.resolve(null)

        block._realId = Promise.all([parentIdPromise, afterIdPromise, beforeIdPromise]).then(([parentId, afterId, beforeId]) => {
            const fd = new FormData()
            // Use whatever the user has typed by the time we actually POST, so fast
            // typing right after Enter lands in the create instead of a placeholder.
            const liveInput = block.querySelector("[data-task-input]")
            const postedTitle = (liveInput ? domToMarkdown(liveInput) : newTitle || "").trim()
            const row = block.querySelector("[data-task-row]")
            if (row) row._lastSavedTitle = postedTitle
            fd.set("title", postedTitle)
            fd.set("parent_id", parentId ? String(parentId) : "root")
            if (afterId) fd.set("after_id", afterId)
            else if (beforeId) fd.set("before_id", beforeId)
            return this._post("/tasks.json", fd).then((data) => {
                if (!data?.ok) {
                    block.remove()
                    return null
                }
                const realId = String(data.task.id)
                this._replaceTempId(block, tempId, realId)
                delete block._realId
                return realId
            })
        })

        return block._realId
    }

    _replaceTempId(block, tempId, realId) {
        block.dataset.taskId = realId
        const row = block.querySelector("[data-task-row]")
        if (row) row.dataset.taskId = realId
        const container = block.querySelector(`[data-children-for="${CSS.escape(tempId)}"]`)
        if (container) container.dataset.childrenFor = realId
        // Update children that referenced the temp parent id
        this.element
            .querySelectorAll(`[data-task-row][data-parent-id="${CSS.escape(tempId)}"]`)
            .forEach((r) => (r.dataset.parentId = realId))
        if (this._editingId === tempId) {
            this._editingId = realId
            this._broadcastEditing(realId)
        }
        // The undo history and a ⇧-range follow the row to its real id.
        if (this._histRowId === tempId) this._histRowId = realId
        if (this._selAnchorId === tempId) this._selAnchorId = realId
        if (this._selFocusId === tempId) this._selFocusId = realId
        const pinBtn = block.querySelector("[data-controller~=\"pin\"]")
        if (pinBtn) pinBtn.dataset.pinPinableIdValue = realId
    }

    _markRowCompleted(row, skipRequest) {
        row.dataset.completed = "true"
        row.classList.add("is-completed")
        if (skipRequest) return
        this._resolveId(row.closest("[data-task-block]")).then((id) => {
            this._post(`/tasks/${id}/complete.json`, new FormData()).then((data) => {
                if (data?.ok) {
                    if (data.spawned) this._insertSpawned(row.closest("[data-task-block]"), data.spawned)
                    if (data.task) this._applyTaskPayloadToRow(row, data.task)
                    celebrateDailyTasks(data.celebration)
                    return
                }
                row.dataset.completed = "false"
                row.classList.remove("is-completed")
            })
        })
    }

    _appendTaskTextLines(block, depth, lines) {
        const row = block.querySelector(":scope > [data-task-row]")
        if (!row) return
        const title = this._rowTitle(row)
        if (title) lines.push(`${"  ".repeat(depth)}${this._headingPrefix(row)}${title}`)
        this._childBlocks(row).forEach((child) => this._appendTaskTextLines(child, depth + 1, lines))
    }

    // Nested <ul><li> mirroring the same L1/L2/L3 tree _appendTaskTextLines
    // encodes as indented plain text. Titles are markdown source, rendered to
    // the inline HTML subset (bold/italic/strike/code/links, text escaped) via
    // mdToHtml so rich targets (Google Docs, Slack's composer) show real
    // formatting. Companion to the text/plain flavor written by _copyRich; the
    // plain text is untouched, so Jane's own paste-back is byte-for-byte
    // identical. The data-jane-tasks marker lets onPaste recognise our own
    // clipboard HTML and prefer text/plain on single-line pastes.
    _taskItemsHtml(blocks) {
        return blocks.map((block) => {
            const row = block.querySelector(":scope > [data-task-row]")
            if (!row) return ""
            const title = this._rowTitle(row)
            const children = this._childBlocks(row)
            const childHtml = children.length ? `<ul>${this._taskItemsHtml(children)}</ul>` : ""
            const style = this._textStyle(row)
            // A heading pastes into Docs/Slack as bold section text in its slot.
            const label = title ? (style ? `<strong>${mdToHtml(title)}</strong>` : mdToHtml(title)) : ""
            return `<li>${label}${childHtml}</li>`
        }).join("")
    }

    _wrapTaskList(itemsHtml) {
        return itemsHtml ? `<ul data-jane-tasks="1">${itemsHtml}</ul>` : ""
    }

    _insertDuplicatedSubtree(afterBlock, tasks) {
        const root = tasks[0]
        const newBlock = this._buildBlockFromPayload(root)
        afterBlock.after(newBlock)

        const childrenByParent = new Map()
        tasks.slice(1).forEach((task) => {
            const parentId = task.parent_id
            if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, [])
            childrenByParent.get(parentId).push(task)
        })

        const attachChildren = (parentId, parentBlock) => {
            const kids = childrenByParent.get(parentId) || []
            const container = parentBlock.querySelector("[data-children-for]")
            kids.forEach((task) => {
                const childBlock = this._buildBlockFromPayload(task)
                container.appendChild(childBlock)
                attachChildren(task.id, childBlock)
            })
            if (kids.length > 0) this._refreshCaretFor(String(parentId))
        }
        attachChildren(root.id, newBlock)
        this._recomputeDepth(newBlock)
        const parentId = afterBlock.querySelector("[data-task-row]")?.dataset.parentId || ""
        this._refreshCaretFor(parentId)
    }

    _buildBlockFromPayload(task) {
        const block = this._buildBlock({ id: task.id, title: task.title, parentId: task.parent_id || "", textStyle: task.text_style || "" })
        // Prefer the server's canonical rendering when the payload carries it.
        if (task.title_html != null) {
            const span = block.querySelector(".tasks-outline-title")
            const editor = block.querySelector("[data-task-input]")
            if (span) span.innerHTML = task.title_html
            if (editor) editor.innerHTML = task.title_html
        }
        const row = block.querySelector("[data-task-row]")
        if (task.completed) {
            row.dataset.completed = "true"
            row.classList.add("is-completed")
        }
        if (task.collapsed) {
            row.dataset.collapsed = "true"
            const children = block.querySelector("[data-children-for]")
            if (children) children.classList.add("is-collapsed")
        }
        this._applyTaskPayloadToRow(row, task)
        return block
    }

    // The row menu is taller than some windows, and it used to open below its
    // button whatever the room, so a task near the bottom of the screen opened it
    // with the last rows - Delete - off the screen. The page often cannot scroll
    // them back: an absolutely placed menu does not extend the scrollable area.
    //
    // So the menu picks the side of its button with more room (a menu that fits
    // below stays below), is sized to that room, and scrolls inside itself when
    // it still does not fit. Delete is pinned to the bottom of the menu in CSS, so
    // whatever scrolls, it is never the thing that goes missing.
    _chooseRowMenuSide(toggle) {
        const dropdown = toggle?.closest?.(".tasks-outline-dropdown")
        if (!dropdown) return

        const button = toggle.getBoundingClientRect()
        const margin = 12
        const below = window.innerHeight - button.bottom - margin - 8
        const above = button.top - margin - 8
        // The menu has no height until it is open, so go by the last one measured.
        const need = this._rowMenuHeight || 520
        dropdown.classList.toggle("dropup", below < need && above > below)
    }

    _fitRowMenu(toggle) {
        const dropdown = toggle?.closest?.(".tasks-outline-dropdown")
        const menu = dropdown?.querySelector(".dropdown-menu")
        if (!menu || !menu.classList.contains("show")) return

        menu.style.maxHeight = ""
        const natural = menu.scrollHeight + 2
        this._rowMenuHeight = natural
        const button = toggle.getBoundingClientRect()
        const margin = 12
        const room = dropdown.classList.contains("dropup")
            ? button.top - margin - 8
            : window.innerHeight - button.bottom - margin - 8
        if (natural > room) menu.style.maxHeight = `${Math.max(room, 160)}px`
    }

    // The same menu as the server-rendered one in tasks/_outline_block.html.slim
    // (same groups, order and classes - see the comment there), for rows
    // created client-side.
    _buildDropdownMenu(pinableId) {
        const menu = document.createElement("div")
        menu.className = "dropdown-menu dropdown-menu-end network-toolbar-menu-panel"

        const divider = () => {
            const el = document.createElement("div")
            el.className = "network-toolbar-menu-divider"
            return el
        }

        const item = (label, action, { icon, extraClass } = {}) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = extraClass ? `network-toolbar-menu-item ${extraClass}` : "network-toolbar-menu-item"
            btn.dataset.action = action
            btn.innerHTML = icon ? `<i class="bi ${icon}"></i><span>${label}</span>` : `<span>${label}</span>`
            return btn
        }

        const group = (...children) => {
            const el = document.createElement("div")
            el.className = "tasks-menu-group"
            el.append(divider(), ...children)
            menu.appendChild(el)
        }

        const priorityEyebrow = document.createElement("div")
        priorityEyebrow.className = "tasks-menu-eyebrow tasks-outline-task-only"
        priorityEyebrow.textContent = "Priority"
        const pinToggle = this._buildPinToggle(pinableId, { asMenuItem: true })
        pinToggle.classList.add("tasks-outline-task-only")

        // -dup: repeats a hover-rail icon (hidden at 768px+); -subtasks: needs
        // subtasks; -task-only: not on a section heading. See _home_panel.scss.
        // The note, comments, files, tags, company and custom fields live in
        // the task pane (Open task); the menu keeps the quick details and what
        // acts on the row. Mirrors tasks/_outline_block.html.slim.
        group(
            item("Open task", "click->tasks-outline#openPane", { icon: "bi-layout-sidebar-inset-reverse", extraClass: "tasks-outline-menu-dup" }),
            item("Assign", "click->tasks-outline#openAssign", { icon: "bi-person-plus", extraClass: "tasks-outline-task-only" }),
            item("Due & repeat", "click->tasks-outline#openDate", { icon: "bi-calendar-event", extraClass: "tasks-outline-task-only" }),
            priorityEyebrow,
            this._buildPriorityOptions(),
            pinToggle
        )
        group(
            item("Mark completed", "click->tasks-outline#markCompleted", { icon: "bi-check2-circle", extraClass: "tasks-outline-task-only" }),
            item("Mark all completed", "click->tasks-outline#markAllCompleted", { icon: "bi-check2-all", extraClass: "tasks-outline-menu-subtasks" })
        )
        group(
            item("Add task above", "click->tasks-outline#addTaskAbove", { icon: "bi-arrow-up" }),
            item("Add task below", "click->tasks-outline#addTaskBelow", { icon: "bi-arrow-down" }),
            item("Duplicate", "click->tasks-outline#duplicateTask", { icon: "bi-copy" }),
            item("Send to list…", "click->tasks-outline#openSendPicker", { icon: "bi-send", extraClass: "tasks-outline-task-only" })
        )
        group(
            item("Ask Jane", "click->tasks-outline#taskJane", { icon: "bi-stars", extraClass: "tasks-outline-menu-dup tasks-outline-task-only" })
        )
        group(
            item("Copy link", "click->tasks-outline#copyLink", { icon: "bi-link-45deg", extraClass: "tasks-outline-menu-dup" }),
            item("Copy task text", "click->tasks-outline#copyTitle", { icon: "bi-clipboard" }),
            item("Copy task and subtask text", "click->tasks-outline#copyTaskAndSubtasks", { icon: "bi-files", extraClass: "tasks-outline-menu-subtasks" })
        )
        // Delete stays a direct child: it is sticky to the menu's bottom.
        menu.appendChild(divider())
        menu.appendChild(item("Delete", "click->tasks-outline#deleteFromMenu", { icon: "bi-trash", extraClass: "text-danger tasks-outline-menu-delete" }))
        return menu
    }

    // Urgent / High / Normal / Low, as tasks/_outline_block renders it. A new
    // row has no priority, so Normal starts active.
    _buildPriorityOptions() {
        const wrap = document.createElement("div")
        wrap.className = "tasks-priority-options tasks-outline-task-only"
        wrap.dataset.taskPriorityOptions = "true"
        wrap.setAttribute("role", "group")
        wrap.setAttribute("aria-label", "Priority")
        ;[["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]].forEach(([value, label]) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = value === "normal" ? "tasks-priority-option is-active" : "tasks-priority-option"
            btn.dataset.priority = value
            btn.dataset.action = "click->tasks-outline#setPriority"
            btn.textContent = label
            wrap.appendChild(btn)
        })
        return wrap
    }

    // As the menu opens: a group whose items are all hidden for this row and
    // width (rail duplicates on desktop, task-only actions on a heading,
    // subtask actions without subtasks) is hidden, and the first visible
    // group drops its leading divider - so dividers never stack or lead.
    _tidyRowMenu(menu) {
        let first = true
        menu.querySelectorAll(":scope > .tasks-menu-group").forEach((group) => {
            group.hidden = false
            const shown = Array.from(group.children).some((el) => !el.classList.contains("network-toolbar-menu-divider") && getComputedStyle(el).display !== "none")
            group.hidden = !shown
            const lead = group.querySelector(":scope > .network-toolbar-menu-divider")
            if (lead) lead.hidden = shown && first
            if (shown) first = false
        })
    }

    // Pin toggle for a client-built row. Mirrors shared/_pin_button: the
    // menu-item variant carries a data-pin-target="label" span so pin#toggle
    // can swap "Pin task" / "Unpin task" in place. Demo mode + the pins URL are
    // read off any already-rendered pin button on the page.
    _buildPinToggle(pinableId, { asMenuItem = false } = {}) {
        const pinRef = this.element.querySelector("[data-controller~=\"pin\"]")
        const createUrl = pinRef?.dataset.pinCreateUrlValue || "/pins"
        const demo = pinRef?.dataset.pinDemoValue === "true"

        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = asMenuItem ? "network-toolbar-menu-item pin-toggle--menu" : "pin-toggle"
        btn.dataset.controller = "pin"
        btn.dataset.pinPinnedValue = "false"
        btn.dataset.pinPinIdValue = ""
        btn.dataset.pinPinableTypeValue = "Task"
        btn.dataset.pinPinableIdValue = String(pinableId)
        btn.dataset.pinMetadataValue = "{}"
        btn.dataset.pinDemoValue = demo ? "true" : "false"
        btn.dataset.pinCreateUrlValue = demo ? "" : createUrl
        btn.dataset.action = "pin#toggle"
        btn.setAttribute("aria-label", "Pin")
        if (!asMenuItem) btn.setAttribute("title", "Pin")
        btn.innerHTML = "<i class=\"bi bi-pin\" data-pin-target=\"icon\"></i>"
        if (asMenuItem) {
            const label = document.createElement("span")
            label.dataset.pinTarget = "label"
            label.dataset.label = "Pin task"
            label.dataset.pinnedLabel = "Unpin task"
            label.textContent = "Pin task"
            btn.appendChild(label)
        }
        return btn
    }

    _buildRowActions(pinableId) {
        const actions = document.createElement("div")
        actions.className = "tasks-outline-actions"

        const addIcon = (label, action, icon, extraClass) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = extraClass ? `tasks-outline-menu ${extraClass}` : "tasks-outline-menu"
            btn.dataset.action = action
            btn.setAttribute("aria-label", label)
            btn.setAttribute("title", label)
            btn.innerHTML = `<i class="bi ${icon}"></i>`
            return btn
        }

        const linkBtn = addIcon("Copy link to task", "click->tasks-outline#copyLink", "bi-link-45deg", "tasks-outline-copy-link")
        linkBtn.dataset.taskCopyLink = "true"

        const dropdown = document.createElement("div")
        dropdown.className = "dropdown tasks-outline-dropdown"

        const menuBtn = document.createElement("button")
        menuBtn.type = "button"
        menuBtn.className = "tasks-outline-menu"
        menuBtn.setAttribute("data-bs-toggle", "dropdown")
        menuBtn.setAttribute("aria-expanded", "false")
        menuBtn.setAttribute("aria-label", "Task actions")
        menuBtn.innerHTML = "<i class=\"bi bi-three-dots\"></i>"

        const menu = this._buildDropdownMenu(pinableId)
        dropdown.appendChild(menuBtn)
        dropdown.appendChild(menu)
        const dueBtn = addIcon("Due date", "click->tasks-outline#openDate", "bi-calendar-event", "tasks-outline-due-action tasks-outline-task-only")
        dueBtn.dataset.taskDueAction = "true"
        actions.appendChild(dueBtn)
        // Same hover icons as the server row.
        actions.appendChild(linkBtn)
        actions.appendChild(addIcon("Ask Jane", "click->tasks-outline#taskJane", "bi-stars", "tasks-outline-task-only"))
        actions.appendChild(addIcon("Open task", "click->tasks-outline#openPane", "bi-layout-sidebar-inset-reverse", "tasks-outline-open-pane"))
        actions.appendChild(dropdown)
        return actions
    }

    // Markdown source of a row's title (for saves, copy, paste round-trips).
    _rowTitle(row) {
        const input = row.querySelector("[data-task-input]")
        if (input && !input.hidden) return domToMarkdown(input).trim()
        const span = row.querySelector(".tasks-outline-title")
        return span ? domToMarkdown(span).trim() : ""
    }

    // Rendered plain text (for tooltips, ghosts, modal headers).
    _rowTitlePlain(row) {
        const input = row.querySelector("[data-task-input]")
        const el = (input && !input.hidden) ? input : row.querySelector(".tasks-outline-title")
        return el ? String(el.textContent || "").trim() : ""
    }

    // Writes both a text/plain and a text/html clipboard flavor via the async
    // ClipboardItem API. Rich editors prefer text/html and render the nested
    // list as real bullets; plain-text targets and Jane's own paste-back read
    // text/plain, which stays identical to the text-only copy. Degrades
    // gracefully: if html is empty or ClipboardItem/clipboard.write is
    // unavailable (older browsers), falls back to the plain-text copy path.
    _copyRich(plainText, html) {
        if (html && typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
            try {
                const item = new ClipboardItem({
                    "text/plain": new Blob([plainText], { type: "text/plain" }),
                    "text/html": new Blob([html], { type: "text/html" }),
                })
                return navigator.clipboard.write([item]).then(() => true).catch(() => this._copyText(plainText))
            } catch (_) {
                return this._copyText(plainText)
            }
        }
        return this._copyText(plainText)
    }

    _copyText(text) {
        if (navigator.clipboard?.writeText) {
            return navigator.clipboard.writeText(text).then(() => true).catch(() => this._copyTextFallback(text))
        }
        return Promise.resolve(this._copyTextFallback(text))
    }

    _copyTextFallback(text) {
        const el = document.createElement("textarea")
        el.value = text
        el.setAttribute("readonly", "")
        el.style.position = "fixed"
        el.style.left = "-9999px"
        document.body.appendChild(el)
        el.select()
        let ok = false
        try { ok = document.execCommand("copy") } catch (_) {}
        el.remove()
        return ok
    }

    _flashNotice(message, level = "success") {
        showSystemToast(message, { level, duration: 2000 })
    }

    _buildBlock({ id, title, parentId, textStyle }) {
        const block = document.createElement("div")
        block.className = "tasks-outline-block"
        block.dataset.taskBlock = "true"
        block.dataset.taskId = String(id)

        const row = document.createElement("div")
        row.className = "tasks-outline-row"
        row.dataset.taskRow = "true"
        row.dataset.taskId = String(id)
        row.dataset.parentId = parentId ? String(parentId) : ""
        row.dataset.completed = "false"
        row.dataset.collapsed = "false"
        row.dataset.pinned = "false"
        row.dataset.dueDate = ""
        row.dataset.recurrenceKind = ""
        row.dataset.remindAt = ""
        row.dataset.reminded = "false"
        row.dataset.recurrenceInterval = ""
        row.dataset.recurrenceWeekday = ""
        row.dataset.assignees = ""
        row.dataset.textStyle = TEXT_STYLES.includes(textStyle) ? textStyle : ""
        row.dataset.depth = "0"
        row.style.marginLeft = "0px"
        row.dataset.action = "dragover->tasks-outline#onDragOver drop->tasks-outline#onDrop dragleave->tasks-outline#onDragLeave"

        const handle = document.createElement("span")
        handle.className = "tasks-outline-handle"
        handle.setAttribute("draggable", "true")
        handle.setAttribute("aria-label", "Move task")
        handle.dataset.action = "dragstart->tasks-outline#onDragStart dragend->tasks-outline#onDragEnd"
        handle.innerHTML = "<i class=\"bi bi-grip-vertical\"></i>"

        const caretSpacer = document.createElement("span")
        caretSpacer.className = "tasks-outline-caret-spacer"

        const check = document.createElement("button")
        check.type = "button"
        check.className = "tasks-outline-check"
        check.setAttribute("aria-label", "Complete")
        check.dataset.action = "click->tasks-outline#toggleComplete"

        const titleWrap = document.createElement("div")
        titleWrap.className = "tasks-outline-title-wrap"
        // Keep in sync with _outline_block.html.slim: chips claim the click first.
        titleWrap.dataset.action = "click->tasks-outline#onTitleWrapClick click->tasks-outline#startEdit"

        const titleHtml = mdToHtml(title)

        const titleSpan = document.createElement("span")
        titleSpan.className = "tasks-outline-title"
        titleSpan.innerHTML = titleHtml

        const input = document.createElement("div")
        input.className = "tasks-outline-input"
        input.setAttribute("contenteditable", "true")
        input.innerHTML = titleHtml
        // Hidden by default like server-rendered rows; otherwise the row shows the
        // title twice (span + input), which looks like a duplicate task.
        input.hidden = true
        input.setAttribute("data-task-input", "true")
        input.dataset.action = "input->tasks-outline#onInput keydown->tasks-outline#onKeyDown blur->tasks-outline#onBlur paste->tasks-outline#onPaste"

        titleWrap.appendChild(titleSpan)
        titleWrap.appendChild(input)

        const rail = document.createElement("div")
        rail.className = "tasks-outline-rail"
        rail.setAttribute("data-task-rail", "true")

        row.appendChild(handle)
        row.appendChild(caretSpacer)
        row.appendChild(check)
        row.appendChild(titleWrap)
        row.appendChild(rail)
        row.appendChild(this._buildRowActions(String(id)))

        const children = document.createElement("div")
        children.className = "tasks-outline-children"
        children.dataset.childrenFor = String(id)

        block.appendChild(row)
        block.appendChild(children)
        return block
    }

    _refreshCaretFor(parentId) {
        const pid = parentId || ""
        const children = this.element.querySelector(`[data-children-for="${CSS.escape(pid === "" ? "root" : pid)}"]`)
        if (!children) return

        const parentRow =
            pid === "" ? null : this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(pid)}"]`)
        if (!parentRow) return

        const hasChildren = children.querySelectorAll(":scope > [data-task-block]").length > 0
        const existingButton = parentRow.querySelector(".tasks-outline-caret")
        const existingSpacer = parentRow.querySelector(".tasks-outline-caret-spacer")

        if (hasChildren && !existingButton) {
            if (existingSpacer) existingSpacer.remove()
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = "tasks-outline-caret"
            btn.setAttribute("aria-label", "Toggle")
            btn.dataset.action = "click->tasks-outline#toggleCollapse"
            btn.innerHTML = "<i class=\"bi bi-chevron-right tasks-outline-caret-icon\"></i>"
            const handle = parentRow.querySelector(".tasks-outline-handle")
            if (handle) handle.after(btn)
            else parentRow.prepend(btn)
        }

        if (!hasChildren && !existingSpacer) {
            if (existingButton) existingButton.remove()
            const sp = document.createElement("span")
            sp.className = "tasks-outline-caret-spacer"
            const handle = parentRow.querySelector(".tasks-outline-handle")
            if (handle) handle.after(sp)
            else parentRow.prepend(sp)
        }
    }

    // caretPos: undefined/null places the caret at the end; a number is clamped to the text length
    _setEditing(id, caretPos) {
        const continuing = this._editingId === id
        this._editingId = id
        if (!continuing) this._paneFollow(id)
        const rows = this.element.querySelectorAll("[data-task-row]")
        rows.forEach((r) => {
            const isEditing = r.dataset.taskId === id
            r.classList.toggle("is-editing", isEditing)
            const input = r.querySelector("[data-task-input]")
            if (input) input.hidden = !isEditing
        })

        const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(id)}"]`)
        const input = row ? row.querySelector("[data-task-input]") : null
        if (input) {
            if (row._lastSavedTitle === undefined) row._lastSavedTitle = domToMarkdown(input).trim()
            // Re-focusing the same row (indent, a move, undo) is the same edit:
            // a date typed before Tab still counts as typed.
            if (!continuing) this._resetDatePhrase(row, input)
            input.focus()
            if (row._hist?.input !== input || this._histRowId !== id) this._startTextHistory(row, input)
            this._histRowId = id
            const len = (input.textContent || "").length
            const pos = (caretPos === undefined || caretPos === null) ? len : Math.max(0, Math.min(caretPos, len))
            this._setCaretOffset(input, pos)
        }
        this._broadcastEditing(id)
    }

    _stopEditing() {
        if (!this._editingId) return
        const id = this._editingId
        this._editingId = null
        this._hideToolbar()
        const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(id)}"]`)
        if (row) {
            row.classList.remove("is-editing")
            const input = row.querySelector("[data-task-input]")
            if (input) input.hidden = true
            this._clearDateChip(row)
        }
        this._broadcastEditing(null)
    }

    // --- Undo ------------------------------------------------------------------
    //
    // ⌘Z / ⌘⇧Z (Ctrl+Z / Ctrl+Y) undo and redo what you did in the outline,
    // the way every task app does. Two histories, and the newer one goes first:
    //
    //   - Text: the title being edited. Ours rather than the browser's, because
    //     bold, colours, links and the date chip rewrite the editor's DOM and
    //     the native stack loses track (⌘Z would skip or garble them). States
    //     are snapshots of the editor's HTML and caret, taken wherever the
    //     editor already schedules a save; typing coalesces into ~1s runs. It
    //     starts afresh each time a row is opened for editing.
    //   - Outline: indent / outdent / move / drag, complete, priority, text
    //     style, dates, and delete (while its toast's Undo is still on offer).
    //     Each entry knows how to undo and redo itself.
    //
    // Every entry carries `at`, when it happened; ⌘Z takes whichever history's
    // last entry is newer, redo the older of the two most recently undone.

    // Whether a keystroke is typing into a field - looking through shadow DOM,
    // where the emoji picker's search box lives (the event's target at the
    // document is only the <emoji-picker> host).
    _isTypingKey(e) {
        const origin = e.composedPath?.()[0] || e.target
        return !!origin?.closest?.("input, textarea, select, [contenteditable='true'], [contenteditable='']") ||
            !!e.target?.closest?.("emoji-picker")
    }

    _connectUndo() {
        this._actions = { past: [], future: [] }
        this._actionGroup = null
        this._replaying = false
        // Outside a text field (rows selected, or nothing focused) ⌘Z is the
        // outline's - the browser would otherwise do nothing at all.
        this._onUndoKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.altKey) return
            const key = e.key.toLowerCase()
            if (key !== "z" && !(key === "y" && !e.metaKey)) return
            if (this._isTypingKey(e)) return
            if (!this.element.isConnected || this.element.offsetParent === null) return
            e.preventDefault()
            if (key === "y" || e.shiftKey) this._redo(null)
            else this._undo(null)
        }
        document.addEventListener("keydown", this._onUndoKey)
    }

    _recordAction(action) {
        if (this._replaying || !this._actions) return
        const entry = { ...action, at: performance.now() }
        if (this._actionGroup) {
            this._actionGroup.push(entry)
            return
        }
        this._actions.past.push(entry)
        if (this._actions.past.length > 100) this._actions.past.shift()
        this._actions.future = []
    }

    // Everything recorded inside fn becomes one entry: a bulk action undoes
    // in one go.
    _groupActions(label, fn) {
        const outer = this._actionGroup
        const group = []
        this._actionGroup = group
        try { fn() } finally { this._actionGroup = outer }
        if (!group.length) return
        this._recordAction({
            label,
            undo: () => group.slice().reverse().forEach((a) => a.undo()),
            redo: group.every((a) => a.redo) ? () => group.forEach((a) => a.redo()) : null
        })
    }

    _undo(row) {
        const hist = row && this._editingId === row.dataset.taskId ? row._hist : null
        const text = hist?.past.at(-1)
        const action = this._actions?.past.at(-1)
        if (text && (!action || text.at >= action.at)) return this._stepText(row, "undo")
        if (!action) return
        this._actions.past.pop()
        this._replay(() => {
            const result = action.undo()
            if (result === false) {
                this._flashNotice(`Can't undo ${action.label.toLowerCase()} any more`, "warning")
                return
            }
            if (action.redo) this._actions.future.push(action)
            this._flashNotice(`Undone: ${action.label}`, "info")
        })
    }

    _redo(row) {
        const hist = row && this._editingId === row.dataset.taskId ? row._hist : null
        const text = hist?.future.at(-1)
        const action = this._actions?.future.at(-1)
        if (text && (!action || text.at <= action.at)) return this._stepText(row, "redo")
        if (!action) return
        this._actions.future.pop()
        this._replay(() => {
            action.redo()
            this._actions.past.push(action)
            this._flashNotice(`Redone: ${action.label}`, "info")
        })
    }

    _replay(fn) {
        this._replaying = true
        try { fn() } finally { this._replaying = false }
    }

    // --- Text history (the title being edited) ---

    _startTextHistory(row, input) {
        row._hist = { input, past: [], future: [], current: this._textState(input), lastTypingAt: 0 }
    }

    _textState(input) {
        return { html: input.innerHTML, sel: this._selectionOffsets(input) }
    }

    _noteText(row, { typing = false } = {}) {
        const hist = row._hist
        const input = row.querySelector("[data-task-input]")
        if (!hist || hist.input !== input || this._histRowId !== row.dataset.taskId) return
        const html = input.innerHTML
        if (html === hist.current.html) {
            hist.current.sel = this._selectionOffsets(input) || hist.current.sel
            return
        }
        const next = this._textState(input)
        if (this._replaying) {
            hist.current = next
            return
        }
        const now = performance.now()
        // A run of typing is one step; a pause, or anything that isn't typing
        // (bold, a colour, a link), starts a new one.
        if (!(typing && now - hist.lastTypingAt < 1000 && hist.past.length)) {
            hist.past.push({ ...hist.current, at: now })
            if (hist.past.length > 200) hist.past.shift()
        }
        hist.lastTypingAt = typing ? now : 0
        hist.current = next
        hist.future = []
    }

    _stepText(row, dir) {
        const hist = row._hist
        const input = row.querySelector("[data-task-input]")
        const from = dir === "undo" ? hist.past : hist.future
        const to = dir === "undo" ? hist.future : hist.past
        const state = from.pop()
        if (!state || !input) return
        to.push({ ...hist.current, at: state.at })
        hist.current = { html: state.html, sel: state.sel }
        hist.lastTypingAt = 0
        input.innerHTML = state.html
        const len = (input.textContent || "").length
        const sel = state.sel || { start: len, end: len }
        this._setSelectionOffsets(input, Math.min(sel.start, len), Math.min(sel.end, len))
        this._replay(() => this._scheduleSave(row))
        this._refreshToolbarState()
    }

    // --- Outline history helpers ---

    // Where a block sits: its container, the block after it, and the parent
    // that container belongs to.
    _placeOf(block) {
        const container = block.parentElement
        const pid = container?.dataset.childrenFor
        return { container, anchor: block.nextElementSibling, parentId: pid && pid !== "root" ? pid : "" }
    }

    _recordMove(block, before, label) {
        const after = this._placeOf(block)
        if (after.container === before.container && after.anchor === before.anchor) return
        this._recordAction({
            label,
            undo: () => this._moveBlockTo(block, before),
            redo: () => this._moveBlockTo(block, after)
        })
    }

    _moveBlockTo(block, place) {
        if (!place.container?.isConnected || !block.isConnected) return false
        const row = block.querySelector("[data-task-row]")
        const oldParent = row.dataset.parentId || ""
        const editing = this._editingId === row.dataset.taskId
        const input = row.querySelector("[data-task-input]")
        const caret = editing && input ? this._selectionOffsets(input)?.start : null
        if (place.anchor && place.anchor !== block && place.anchor.parentElement === place.container) place.anchor.before(block)
        else place.container.appendChild(block)
        this._applyParent(block, place.parentId || null)
        this._refreshCaretFor(oldParent)
        this._refreshCaretFor(place.parentId)
        const anchorAfter = this._previousSiblingBlock(block)
        const anchorBefore = anchorAfter ? null : this._nextSiblingBlock(block)
        Promise.all([this._resolveId(block), this._resolveId(anchorAfter), this._resolveId(anchorBefore)]).then(([id, afterId, beforeId]) => {
            const fd = new FormData()
            fd.set("parent_id", place.parentId || "root")
            if (afterId) fd.set("after_id", afterId)
            else if (beforeId) fd.set("before_id", beforeId)
            this._post(`/tasks/${id}/move.json`, fd)
        })
        if (editing) this._setEditing(row.dataset.taskId, caret)
        return true
    }

    // The fields a row's date / priority / style edits touch, as the row
    // shows them now.
    _rowFields(row) {
        const fields = {
            due_date: row.dataset.dueDate || "",
            recurrence_kind: row.dataset.recurrenceKind || "",
            recurrence_interval: row.dataset.recurrenceInterval || "",
            recurrence_weekday: row.dataset.recurrenceWeekday || "",
            priority: row.dataset.priority || "normal",
            remind_at: row.dataset.remindAt || "",
            text_style: this._textStyle(row)
        }
        // Custom-field values, so an undo of a points change puts the old
        // value back (blank clears).
        const values = this._rowFieldValues(row)
        this.fieldsValue.forEach((field) => {
            fields[`field_values[${field.id}]`] = this._formatFieldValue(values[String(field.id)])
        })
        return fields
    }

    // Snapshot now; undo writes the snapshot back, redo writes back what the
    // row showed when it was undone.
    _recordFields(row, label, before = this._rowFields(row)) {
        if (this._replaying) return
        let after = null
        this._recordAction({
            label,
            undo: () => {
                after = this._rowFields(row)
                return this._writeFields(row, before)
            },
            redo: () => this._writeFields(row, after || before)
        })
    }

    _writeFields(row, fields) {
        if (!row.isConnected) return false
        const fd = new FormData()
        Object.entries(fields).forEach(([k, v]) => fd.set(k, v))
        this._queueRowWrite(row, () => this._resolveId(row.closest("[data-task-block]")).then((id) => {
            return this._patch(`/tasks/${id}.json`, fd).then((data) => {
                if (!data?.ok || !data.task) return
                // Fields only: an undo restoring a title has its own PATCH in
                // flight, and this answer may predate it - as it may a redo's
                // optimistic complete.
                const { title, title_html: titleHtml, completed, ...fieldsOnly } = data.task
                this._remotePatch(fieldsOnly)
            })
        }))
        return true
    }

    _setTitleMarkdown(row, markdown) {
        const input = row.querySelector("[data-task-input]")
        if (!input || !row.isConnected) return false
        input.innerHTML = mdToHtml(markdown)
        this._saveTitle(row)
        return true
    }

    _removeSpawned(id) {
        const block = this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(id)}"]`)
        const parentId = block?.querySelector("[data-task-row]")?.dataset.parentId || ""
        block?.remove()
        this._refreshCaretFor(parentId)
        this._delete(`/tasks/${id}.json`)
    }

    // --- Multi-select ----------------------------------------------------------
    //
    // Pick several rows and act on them at once (Todoist, Linear, Things):
    // ⇧-click selects a range, ⌘/Ctrl-click adds or drops one row, ⇧↑/⇧↓ from
    // the edge of a title (or with rows already selected) extends the range.
    // A bar at the bottom completes, dates, prioritises or deletes the lot;
    // Backspace deletes, ⌘↵ completes, Esc lets go.
    //
    // The rows' `is-selected` class is the selection - nothing to drift out of
    // step when a row is deleted or re-rendered. The anchor is where a ⇧-range
    // starts; the focus is the end the arrows move.

    _connectMultiSelect() {
        this._selAnchorId = null
        this._selFocusId = null
        // A modifier mousedown would extend the browser's text selection across
        // rows before the click lands; this click is about rows, not text.
        this._onSelectMouseDown = (e) => {
            if (!(e.shiftKey || e.metaKey || e.ctrlKey)) return
            if (!e.target.closest(".tasks-outline-title-wrap") || e.target.closest("[data-task-input], a, button, input")) return
            e.preventDefault()
        }
        this.element.addEventListener("mousedown", this._onSelectMouseDown)
        // Back/Forward restores a cached snapshot: never one with a selection
        // and an inert bulk bar baked into it.
        this._onSelectBeforeCache = () => this._clearSelection()
        document.addEventListener("turbo:before-cache", this._onSelectBeforeCache)
        // A plain click anywhere else lets go (the bar and its menus excepted).
        this._onSelectOutside = (e) => {
            if (!this._hasSelection()) return
            if (e.target.closest?.(".tasks-bulk-bar")) return
            if ((e.shiftKey || e.metaKey || e.ctrlKey) && e.target.closest?.("[data-task-row]")) return
            this._clearSelection()
        }
        document.addEventListener("mousedown", this._onSelectOutside, true)
        this._onSelectKey = (e) => this._handleSelectionKey(e)
        document.addEventListener("keydown", this._onSelectKey)
    }

    _disconnectMultiSelect() {
        this.element.removeEventListener("mousedown", this._onSelectMouseDown)
        document.removeEventListener("turbo:before-cache", this._onSelectBeforeCache)
        document.removeEventListener("mousedown", this._onSelectOutside, true)
        document.removeEventListener("keydown", this._onSelectKey)
        this._bulkBar?.remove()
        this._bulkBar = null
    }

    _hasSelection() {
        return !!this.element.querySelector("[data-task-row].is-selected")
    }

    _selectedRows() {
        return this._selectableRows().filter((r) => r.classList.contains("is-selected"))
    }

    // Visible, real rows in outline order (a folded subtree's rows are not
    // selectable - what you can't see, you can't be acting on).
    _selectableRows() {
        return Array.from(this.element.querySelectorAll("[data-task-row]"))
            .filter((r) => r.offsetParent !== null && !r.dataset.placeholderPending)
    }

    _rowById(id) {
        return id ? this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(id)}"]`) : null
    }

    _selectClick(e) {
        const row = e.target.closest("[data-task-row]")
        if (!row || row.dataset.placeholderPending || e.target.closest("a, button, input")) return false
        e.preventDefault()
        window.getSelection()?.removeAllRanges()
        const editing = this._rowById(this._editingId)
        this._leaveEditor()
        if (e.shiftKey) {
            const anchor = this._rowById(this._selAnchorId) || editing || row
            this._selectRange(anchor, row)
        } else {
            row.classList.toggle("is-selected")
            // ⌘-clicking into a selection while editing another row takes that
            // row along - it is where you were.
            if (editing && editing !== row && !this._selAnchorId) editing.classList.add("is-selected")
            this._selAnchorId = row.dataset.taskId
            this._selFocusId = row.dataset.taskId
            this._renderBulkBar()
        }
        return true
    }

    _selectRange(anchor, focus) {
        const rows = this._selectableRows()
        let a = rows.indexOf(anchor)
        let b = rows.indexOf(focus)
        if (a === -1) a = b
        if (b === -1) return
        const [lo, hi] = a <= b ? [a, b] : [b, a]
        rows.forEach((r, i) => r.classList.toggle("is-selected", i >= lo && i <= hi))
        this._selAnchorId = rows[a].dataset.taskId
        this._selFocusId = rows[b].dataset.taskId
        this._renderBulkBar()
    }

    _clearSelection() {
        if (!this._hasSelection() && !this._bulkBar) return
        this.element.querySelectorAll("[data-task-row].is-selected").forEach((r) => r.classList.remove("is-selected"))
        this._selAnchorId = null
        this._selFocusId = null
        this._renderBulkBar()
    }

    // Save and close the editor without moving focus into another row.
    _leaveEditor() {
        const row = this._rowById(this._editingId)
        if (!row) return
        const input = row.querySelector("[data-task-input]")
        if (input && document.activeElement === input) input.blur()
        else {
            this._saveTitle(row)
            this._stopEditing()
        }
    }

    _shiftArrowLeavesText(input, key) {
        const len = (input.textContent || "").length
        const sel = this._selectionOffsets(input) || { start: len, end: len }
        const all = sel.start === 0 && sel.end === len
        if (key === "ArrowUp") return all || (sel.start === 0 && sel.end === 0)
        return all || (sel.start === len && sel.end === len)
    }

    _selectFromEditor(row, dir) {
        this._leaveEditor()
        this._selectRange(row, this._neighborRow(row, dir) || row)
    }

    _handleSelectionKey(e) {
        if (!this._hasSelection()) return
        // Typing somewhere (Jane's composer, a row being edited) is not
        // talking to the selection.
        if (this._isTypingKey(e)) return
        const mod = e.metaKey || e.ctrlKey
        const rows = this._selectableRows()
        const focus = this._rowById(this._selFocusId) || this._selectedRows().at(-1)
        const step = (dir) => {
            const i = rows.indexOf(focus) + (dir === "prev" ? -1 : 1)
            return rows[Math.max(0, Math.min(rows.length - 1, i))]
        }
        if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !mod && !e.altKey) {
            e.preventDefault()
            const next = step(e.key === "ArrowUp" ? "prev" : "next")
            if (e.shiftKey) this._selectRange(this._rowById(this._selAnchorId) || focus, next)
            else this._selectRange(next, next)
            next?.scrollIntoView({ block: "nearest" })
        } else if (e.key === "Escape") {
            e.preventDefault()
            this._clearSelection()
        } else if (e.key === "Backspace" || e.key === "Delete") {
            e.preventDefault()
            this._bulkDelete()
        } else if (e.key === "Enter" && mod) {
            e.preventDefault()
            this._bulkComplete()
        } else if (e.key === "Enter") {
            e.preventDefault()
            this._clearSelection()
            if (focus) this._setEditing(focus.dataset.taskId)
        } else if (mod && e.key.toLowerCase() === "a" && !e.shiftKey) {
            e.preventDefault()
            if (rows.length) this._selectRange(rows[0], rows.at(-1))
        }
    }

    // --- The bulk bar ---

    _renderBulkBar() {
        const count = this._selectedRows().length
        if (!count) {
            this._bulkBar?.remove()
            this._bulkBar = null
            return
        }
        if (!this._bulkBar) {
            const bar = document.createElement("div")
            bar.className = "tasks-bulk-bar"
            bar.setAttribute("role", "toolbar")
            bar.setAttribute("aria-label", "Selected tasks")
            bar.innerHTML = `
                <span class="tasks-bulk-count" aria-live="polite"></span>
                <button type="button" class="tasks-bulk-btn" data-bulk="complete" title="Complete (${this._modLabel()} ↵)"><i class="bi bi-check2-circle" aria-hidden="true"></i><span>Complete</span></button>
                <button type="button" class="tasks-bulk-btn" data-bulk="date" aria-haspopup="true"><i class="bi bi-calendar-event" aria-hidden="true"></i><span>Date</span></button>
                <button type="button" class="tasks-bulk-btn" data-bulk="priority" aria-haspopup="true"><i class="bi bi-flag" aria-hidden="true"></i><span>Priority</span></button>
                <button type="button" class="tasks-bulk-btn is-danger" data-bulk="delete" title="Delete (Backspace)"><i class="bi bi-trash3" aria-hidden="true"></i><span>Delete</span></button>
                <button type="button" class="tasks-bulk-btn tasks-bulk-clear" data-bulk="clear" aria-label="Clear selection" title="Clear selection (Esc)"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
                <div class="tasks-bulk-menu" hidden></div>`
            // mousedown, so a click on the bar never blurs or deselects first.
            bar.addEventListener("mousedown", (e) => {
                if (!e.target.closest("input")) e.preventDefault()
            })
            bar.addEventListener("click", (e) => this._onBulkClick(e))
            bar.addEventListener("change", (e) => {
                if (e.target.matches("[data-bulk-date-input]") && e.target.value) this._bulkSetDate(e.target.value)
            })
            document.body.appendChild(bar)
            this._bulkBar = bar
        }
        this._bulkBar.querySelector(".tasks-bulk-count").textContent = `${count} selected`
        const tasks = this._selectedRows().filter((r) => !this._isHeading(r))
        const allDone = tasks.length > 0 && tasks.every((r) => r.dataset.completed === "true")
        this._bulkBar.querySelector("[data-bulk='complete'] span").textContent = allDone ? "Reopen" : "Complete"
    }

    _modLabel() {
        return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘" : "Ctrl"
    }

    _onBulkClick(e) {
        const option = e.target.closest("[data-bulk-option]")
        if (option) {
            const { bulkOption: kind, value } = option.dataset
            if (kind === "date") this._bulkSetDate(value)
            if (kind === "priority") this._bulkSetPriority(value)
            return
        }
        const btn = e.target.closest("[data-bulk]")
        if (!btn) return
        const action = btn.dataset.bulk
        if (action === "complete") this._bulkComplete()
        else if (action === "delete") this._bulkDelete()
        else if (action === "clear") this._clearSelection()
        else if (action === "date" || action === "priority") this._toggleBulkMenu(action)
    }

    _toggleBulkMenu(kind) {
        const menu = this._bulkBar?.querySelector(".tasks-bulk-menu")
        if (!menu) return
        if (!menu.hidden && menu.dataset.kind === kind) {
            menu.hidden = true
            return
        }
        menu.dataset.kind = kind
        if (kind === "date") {
            const today = new Date()
            const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
            const plus = (n) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + n)
            const nextMonday = plus(((8 - today.getDay()) % 7) || 7)
            const options = [["Today", iso(today), "bi-sun"], ["Tomorrow", iso(plus(1)), "bi-sunrise"], ["Next week", iso(nextMonday), "bi-calendar-week"], ["No date", "", "bi-slash-circle"]]
            menu.innerHTML = options.map(([label, value, icon]) =>
                `<button type="button" class="tasks-bulk-option" data-bulk-option="date" data-value="${value}"><i class="bi ${icon}" aria-hidden="true"></i>${label}</button>`).join("") +
                '<label class="tasks-bulk-date-pick"><span>Pick a date</span><input type="date" data-bulk-date-input></label>'
        } else {
            menu.innerHTML = '<div class="tasks-priority-options" role="group" aria-label="Priority">' +
                [["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]].map(([value, label]) =>
                    `<button type="button" class="tasks-priority-option" data-bulk-option="priority" data-value="${value}" data-priority="${value}">${label}</button>`).join("") +
                "</div>"
        }
        menu.hidden = false
    }

    // Complete them all - or, when every one is already done, reopen them all.
    _bulkComplete() {
        const rows = this._selectedRows().filter((r) => !this._isHeading(r))
        if (!rows.length) return
        const reopen = rows.every((r) => r.dataset.completed === "true")
        this._groupActions(reopen ? "Reopen" : "Complete", () => {
            rows.filter((r) => (r.dataset.completed === "true") === reopen).forEach((r) => this._toggleCompleteForRow(r))
        })
        this._flashNotice(`${rows.length} ${rows.length === 1 ? "task" : "tasks"} ${reopen ? "reopened" : "completed"}`)
        this._renderBulkBar()
    }

    _bulkSetDate(value) {
        const rows = this._selectedRows().filter((r) => !this._isHeading(r))
        this._groupActions("Date", () => rows.forEach((row) => this._recordFields(row, "Date")))
        rows.forEach((row) => {
            const fd = new FormData()
            fd.set("due_date", value || "")
            // A date set by hand ends any repeat, as the Due & repeat dialog does
            // when its repeat is cleared; "No date" clears both.
            fd.set("recurrence_kind", "")
            this._resolveId(row.closest("[data-task-block]")).then((id) => {
                this._patch(`/tasks/${id}.json`, fd).then((data) => {
                    if (data?.ok && data.task) this._applyTaskPayloadToRow(row, data.task)
                })
            })
        })
        const menu = this._bulkBar?.querySelector(".tasks-bulk-menu")
        if (menu) menu.hidden = true
        if (rows.length) this._flashNotice(value ? `Date set on ${rows.length} ${rows.length === 1 ? "task" : "tasks"}` : `Date cleared on ${rows.length} ${rows.length === 1 ? "task" : "tasks"}`)
    }

    _bulkSetPriority(value) {
        const rows = this._selectedRows().filter((r) => !this._isHeading(r))
        this._groupActions("Priority", () => rows.forEach((row) => this._writePriority(row, value)))
        const menu = this._bulkBar?.querySelector(".tasks-bulk-menu")
        if (menu) menu.hidden = true
    }

    // One toast and one Undo for the lot, like _deleteTask's for one row. A row
    // whose subtasks are not all selected stays - the server refuses to orphan
    // them, and putting rows back after the fact read as a glitch.
    _bulkDelete() {
        const selected = this._selectedRows()
        if (!selected.length) return
        const chosen = new Set(selected)
        const keep = selected.filter((row) => this._descendantRows(row).some((d) => !chosen.has(d)))
        const going = selected.filter((row) => !keep.includes(row) && !this._ancestorRows(row).some((a) => keep.includes(a)))
        if (!going.length) {
            this._flashNotice("Select their subtasks too, or delete those first", "warning")
            return
        }
        // Only the top-most blocks leave the DOM (they carry their subtrees);
        // every row gets its DELETE, deepest first so no parent goes before
        // its children.
        const goingSet = new Set(going)
        const tops = going.filter((row) => !this._ancestorRows(row).some((a) => goingSet.has(a)))
        const removed = tops.map((row) => {
            const block = row.closest("[data-task-block]")
            return { row, block, container: block.parentElement, anchor: block.nextElementSibling, parentId: row.dataset.parentId || "" }
        })
        const ordered = going.slice().sort((a, b) => this._ancestorRows(b).length - this._ancestorRows(a).length)
        const blocks = ordered.map((row) => row.closest("[data-task-block]"))
        removed.forEach(({ block, parentId }) => {
            block.remove()
            this._refreshCaretFor(parentId)
        })
        this._clearSelection()
        this._ensurePlaceholder()

        const send = async ({ keepalive = false } = {}) => {
            for (const block of blocks) {
                const id = await this._resolveId(block)
                const data = await this._delete(`/tasks/${id}.json`, { keepalive })
                if (!data?.ok && !keepalive) {
                    this._flashNotice(data?.error || "Couldn't delete every task - reload to see what's left", "warning")
                    return
                }
            }
        }
        const pending = { send, timer: null }
        pending.timer = setTimeout(() => {
            this._pendingDeletes.delete(pending)
            send()
        }, UNDO_DELETE_MS)
        this._pendingDeletes.add(pending)
        const restore = () => {
            if (!this._pendingDeletes.has(pending)) return false
            clearTimeout(pending.timer)
            this._pendingDeletes.delete(pending)
            this._removeUntouchedPlaceholder()
            removed.slice().reverse().forEach(({ block, container, anchor, parentId }) => {
                this._restoreBlock(block, container, anchor)
                this._refreshCaretFor(parentId)
            })
            removed.forEach(({ row }) => row.classList.add("is-selected"))
            this._renderBulkBar()
            return true
        }
        const n = going.length
        showSystemToast(`${n} ${n === 1 ? "task" : "tasks"} deleted${keep.length ? ` (${keep.length} kept: they have subtasks)` : ""}`, {
            level: "info",
            duration: UNDO_DELETE_MS,
            action: {
                label: "Undo",
                onClick: () => restore()
            }
        })
        this._recordAction({ label: "Delete", undo: restore })
    }

    _descendantRows(row) {
        const container = this.element.querySelector(`[data-children-for="${CSS.escape(row.dataset.taskId)}"]`)
        return container ? Array.from(container.querySelectorAll("[data-task-row]")).filter((r) => !r.dataset.placeholderPending) : []
    }

    _ancestorRows(row) {
        const out = []
        let id = row.dataset.parentId
        while (id) {
            const parent = this._rowById(id)
            if (!parent) break
            out.push(parent)
            id = parent.dataset.parentId
        }
        return out
    }

    // --- Natural-language dates (utils/nl_dates.js) --------------------------
    //
    // Typing "Call Bob tomorrow" or "Standup every monday" shows a chip with
    // what the phrase means; committing the title (Enter or leaving the row)
    // takes the phrase out of the title and sets the due date / repeat. The
    // chip's x keeps the words as words (Todoist's behaviour).
    //
    // Only a phrase TYPED in this edit counts: the title as it was when
    // editing began is the baseline, so clicking into an old "Plan Friday
    // offsite" and out again never rewrites it. Headings carry no dates.

    _resetDatePhrase(row, input) {
        row._nlBaseline = (input.textContent || "").toLowerCase()
        row._nlMatch = null
        row._nlDismissed = null
        this._clearDateChip(row)
    }

    _detectDatePhrase(row, input) {
        const match = this._isHeading(row) ? null : parseDatePhrase(input.textContent || "")
        const phrase = match?.phrase.toLowerCase()
        const fresh = match && phrase !== row._nlDismissed && !(row._nlBaseline || "").includes(phrase)
        row._nlMatch = fresh ? match : null
        if (fresh) this._showDateChip(row, match)
        else this._clearDateChip(row)
    }

    _applyDatePhrase(row, input) {
        const pending = row._nlMatch
        if (!pending) return
        row._nlMatch = null
        this._clearDateChip(row)
        // A heading carries no date (the server would drop it, after the words
        // were already gone).
        if (this._isHeading(row)) return
        // Re-read: the caret may have moved and the text changed since the
        // chip was drawn. Only the same phrase is applied.
        const match = parseDatePhrase(input.textContent || "")
        if (!match || match.phrase.toLowerCase() !== pending.phrase.toLowerCase()) return
        const text = input.textContent || ""
        // A title that is ONLY the date ("Friday") stays words: there would be
        // nothing left to name the task. Decided before touching the DOM, so
        // the caret and formatting survive.
        if (!(text.slice(0, match.start) + text.slice(match.end)).trim()) return
        const titleBefore = domToMarkdown(input).trim()
        const fieldsBefore = this._rowFields(row)
        // Take the phrase out with the space in front of it (or after it, when
        // it opens the title), so "Call Bob tomorrow" becomes "Call Bob".
        let start = match.start
        let end = match.end
        if (start > 0 && /\s/.test(text[start - 1])) start -= 1
        else if (/\s/.test(text[end] || "")) end += 1
        this._extractEditorRange(input, start, end)
        row._nlBaseline = (input.textContent || "").toLowerCase()
        this._saveTitle(row)

        const fd = new FormData()
        fd.set("due_date", match.date)
        // "tomorrow 3pm": the time is the reminder. Without one, the server
        // moves any existing reminder along with the date.
        if (match.time) {
            fd.set("remind_at", this._localInstant(match.date, match.time))
            row.dataset.remindAt = fd.get("remind_at")
            row.dataset.reminded = "false"
        }
        const recur = match.recurrence
        fd.set("recurrence_kind", recur?.kind || "")
        fd.set("recurrence_weekday", recur?.weekday == null ? "" : String(recur.weekday))
        fd.set("recurrence_interval", recur?.interval == null ? "" : String(recur.interval))
        // Optimistic: the rail shows the date straight away.
        row.dataset.dueDate = match.date
        row.dataset.recurrenceKind = recur?.kind || ""
        row.dataset.recurrenceWeekday = fd.get("recurrence_weekday")
        row.dataset.recurrenceInterval = fd.get("recurrence_interval")
        this._renderRail(row)
        this._resolveId(row.closest("[data-task-block]")).then((id) => {
            this._patch(`/tasks/${id}.json`, fd).then((data) => {
                if (data?.ok && data.task) this._applyTaskPayloadToRow(row, data.task)
                else this._flashNotice(data?.error || "Could not set the date", "warning")
            })
        })
        // ⌘Z puts the words back AND takes the date off.
        const titleAfter = domToMarkdown(input).trim()
        const fieldsAfter = this._rowFields(row)
        this._recordAction({
            label: "Date from title",
            undo: () => { this._setTitleMarkdown(row, titleBefore); this._writeFields(row, fieldsBefore) },
            redo: () => { this._setTitleMarkdown(row, titleAfter); this._writeFields(row, fieldsAfter) }
        })
    }

    _showDateChip(row, match) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        let chip = wrap.querySelector("[data-nl-date]")
        if (!chip) {
            chip = document.createElement("span")
            chip.className = "tasks-nl-date"
            chip.dataset.nlDate = ""
            chip.innerHTML = '<i aria-hidden="true"></i><span class="tasks-nl-date-label"></span>' +
                '<button type="button" class="tasks-nl-date-dismiss" aria-label="Keep as text" title="Keep as text">&times;</button>'
            // mousedown, not click: a click would blur the editor first, and
            // the blur would apply the date the x is meant to refuse.
            chip.addEventListener("mousedown", (e) => {
                e.preventDefault()
                if (!e.target.closest(".tasks-nl-date-dismiss")) return
                row._nlDismissed = row._nlMatch?.phrase.toLowerCase() || null
                row._nlMatch = null
                this._clearDateChip(row)
            })
            wrap.appendChild(chip)
        }
        chip.querySelector("i").className = `bi ${match.recurrence ? "bi-arrow-repeat" : "bi-calendar-event"}`
        chip.querySelector(".tasks-nl-date-label").textContent = match.label
        chip.title = `"${match.phrase}" sets the ${match.recurrence ? "repeat" : "due date"} when you press Enter`
        row.classList.add("has-nl-date")
    }

    _clearDateChip(row) {
        row.querySelector("[data-nl-date]")?.remove()
        row.classList.remove("has-nl-date")
    }

    // --- Shared-list realtime (TaskListChannel) ------------------------------

    _connectListCable() {
        if (!this.sharedValue || !this.listValue) return
        if (typeof createConsumer !== "function") return
        try {
            this._listConsumer = createConsumer()
            this._listSubscription = this._listConsumer.subscriptions.create(
                { channel: "TaskListChannel", task_list_id: this.listValue, client_id: this._clientId },
                {
                    received: (data) => this._onListCableReceived(data),
                    rejected: () => this._disconnectListCable()
                }
            )
        } catch (_error) {
            this._disconnectListCable()
        }
    }

    _disconnectListCable() {
        this._broadcastEditing(null)
        try { this._listSubscription?.unsubscribe() } catch (_error) { /* ignore */ }
        try { this._listConsumer?.disconnect() } catch (_error) { /* ignore */ }
        this._listSubscription = null
        this._listConsumer = null
        this._remoteEditors?.clear()
        this._clearEditingBadges()
    }

    _broadcastEditing(taskId) {
        if (!this._listSubscription) return
        // Skip temp/placeholder ids — peers can't resolve them.
        const id = (taskId && !String(taskId).startsWith("tmp-")) ? taskId : null
        try {
            this._listSubscription.perform("editing", { task_id: id, client_id: this._clientId })
        } catch (_error) { /* ignore */ }
    }

    _onListCableReceived(data) {
        if (!data || !data.type) return
        if (data.type === "editing") {
            this._applyRemoteEditing(data)
            return
        }
        if (data.type === "task.changed") this._applyRemoteTaskChanged(data)
        if (data.type === "comment.changed") this._applyRemoteCommentChanged(data)
        if (data.type === "attachment.changed") this._applyRemoteAttachmentChanged(data)
    }

    _applyRemoteEditing(data) {
        // Skip only this tab's own echo — other tabs of the same user still show.
        if (data.client_id && data.client_id === this._clientId) return
        const userId = data.user_id
        if (userId == null) return

        const key = data.client_id ? String(data.client_id) : `user:${userId}`
        const taskId = data.task_id
        if (taskId == null || taskId === "") {
            this._remoteEditors.delete(key)
        } else {
            this._remoteEditors.set(key, {
                taskId: String(taskId),
                name: data.name || "",
                avatarUrl: data.avatar_url || "",
                initials: data.initials || "?",
                hue: data.hue == null ? 210 : data.hue
            })
        }
        this._renderEditingBadges()
    }

    _clearEditingBadges() {
        this.element.querySelectorAll("[data-editing-badge]").forEach((el) => el.remove())
    }

    _renderEditingBadges() {
        this._clearEditingBadges()
        // One badge per row; if two remotes edit the same row, last writer wins.
        // Mount after the title (not inside its HTML, not on the rail) so
        // remote/local title innerHTML patches cannot wipe the badge.
        const byTask = new Map()
        this._remoteEditors.forEach((info) => byTask.set(info.taskId, info))
        byTask.forEach((info, taskId) => {
            const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(taskId)}"]`)
            if (!row) return
            const title = row.querySelector(".tasks-outline-title-wrap > .tasks-outline-title")
            if (!title) return
            const badge = document.createElement("span")
            badge.className = "tasks-outline-editing-badge"
            badge.dataset.editingBadge = "true"
            badge.title = info.name ? `${info.name} is editing` : "Editing"
            if (info.avatarUrl) {
                const img = document.createElement("img")
                img.className = "task-share-avatar"
                img.src = info.avatarUrl
                img.alt = info.name || ""
                badge.appendChild(img)
            } else {
                const span = document.createElement("span")
                span.className = "task-share-avatar task-share-avatar--initials"
                span.style.background = `hsl(${info.hue}, 60%, 45%)`
                span.textContent = info.initials
                badge.appendChild(span)
            }
            title.insertAdjacentElement("afterend", badge)
        })
    }

    // Toolbar's live-sync toggle (a separate Stimulus element) reaches us via
    // this window event, same bridge as onFoldAll/onCopyAll.
    // Pausing doesn't touch the subscription — presence/editing badges still
    // work — it only gates task.changed application below. Resuming replays
    // anything skipped with a full refresh; if nothing was skipped, no reload.
    onLiveToggle(event) {
        this._livePaused = !!event.detail?.paused
        const hadSkips = this._skippedRemoteCount > 0
        this._skippedRemoteCount = 0
        if (!this._livePaused && hadSkips) this._refreshAfterCatchUp()
    }

    _refreshAfterCatchUp() {
        if (window.Turbo) {
            window.Turbo.visit(window.location.href, { action: "replace" })
        } else {
            window.location.reload()
        }
    }

    _applyRemoteTaskChanged(data) {
        // Skip only the originating tab's echo (optimistic already applied).
        // Same-user other tabs and nil-client_id (jobs/assistant) always apply.
        if (data.client_id && data.client_id === this._clientId) return
        // An open task pane on this task re-reads itself too.
        if (data.task?.id != null) window.dispatchEvent(new CustomEvent("jane:task-changed", { detail: { taskId: Number(data.task.id) } }))
        if (this._livePaused) {
            this._skippedRemoteCount += 1
            this.dispatch("liveskip", { target: window, detail: { count: this._skippedRemoteCount } })
            return
        }
        const task = data.task
        if (!task || task.id == null) return
        // The server owns membership for search/tag/priority/due filters and,
        // for grouped task trees, the ancestor context around every match. A
        // client-side upsert cannot reproduce that predicate safely. Refetch a
        // filtered outline on remote mutations so a non-match is never inserted
        // and a newly matching task is never missed.
        if (this.serverFilteredValue) {
            this._refreshAfterCatchUp()
            return
        }
        const op = data.op
        const id = String(task.id)

        if (op === "destroy") {
            this._remoteDestroy(id)
            return
        }
        if (op === "create") {
            this._remoteUpsert(task, { forceInsert: true })
            return
        }
        if (op === "move") {
            this._remoteMove(task)
            return
        }
        // complete / uncomplete / update
        this._remotePatch(task, { op })
    }

    _remoteDestroy(id) {
        const block = this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(id)}"]`)
        if (!block) return
        const row = block.querySelector("[data-task-row]")
        const parentId = row?.dataset.parentId || ""
        if (this._editingId === id) this._stopEditing()
        block.remove()
        this._refreshCaretFor(parentId)
        this._remoteEditors.forEach((info, key) => {
            if (info.taskId === id) this._remoteEditors.delete(key)
        })
        this._renderEditingBadges()
        const root = this.element.querySelector('[data-children-for="root"]')
        if (root && !root.querySelector("[data-task-block]")) this._ensurePlaceholder()
    }

    _remotePatch(task, { op } = {}) {
        const id = String(task.id)
        let block = this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(id)}"]`)
        if (!block) {
            // Uncomplete (or create-like update) of a filtered-out row: insert it.
            if (op === "uncomplete" || (task.completed === false)) {
                this._remoteUpsert(task, { forceInsert: true })
            }
            return
        }
        const row = block.querySelector("[data-task-row]")
        if (!row) return

        if ("completed" in task) {
            row.dataset.completed = task.completed ? "true" : "false"
            row.classList.toggle("is-completed", Boolean(task.completed))
        }
        if ("collapsed" in task) {
            row.dataset.collapsed = task.collapsed ? "true" : "false"
            const children = block.querySelector("[data-children-for]")
            if (children) children.classList.toggle("is-collapsed", Boolean(task.collapsed))
        }
        if ("pinned" in task) row.dataset.pinned = task.pinned ? "true" : "false"
        if ("text_style" in task) row.dataset.textStyle = TEXT_STYLES.includes(task.text_style) ? task.text_style : ""
        // Tags and priority on a shared list: a collaborator's change used to
        // stay invisible until reload — the payload carried tags that nothing
        // read, and no priority at all.
        if ("tags" in task) {
            const tags = task.tags || []
            row.dataset.tags = JSON.stringify(tags)
            this._renderRowChips(row, this._chipsForSlugs(tags), tags.length)
        }
        if ("field_values" in task) {
            row.dataset.fieldValues = JSON.stringify(task.field_values || {})
            this._renderFieldBadges(row)
        }
        if ("priority" in task) {
            row.dataset.priority = task.priority || "normal"
            this._renderPriorityMarker(row, task.priority)
        }

        // Don't overwrite a title the local user is mid-edit on.
        const titleLocked = this._editingId === id
        if (!titleLocked && ("title" in task || "title_html" in task)) {
            const html = task.title_html != null ? task.title_html : mdToHtml(task.title || "")
            const span = row.querySelector(".tasks-outline-title")
            const editor = row.querySelector("[data-task-input]")
            if (span) span.innerHTML = html
            if (editor) editor.innerHTML = html
            row._lastSavedTitle = task.title != null ? String(task.title) : domToMarkdown(editor || span).trim()
        }

        this._applyTaskPayloadToRow(row, task)
        this._renderEditingBadges()
    }

    _remoteMove(task) {
        const id = String(task.id)
        let block = this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(id)}"]`)
        if (!block) {
            this._remoteUpsert(task, { forceInsert: true })
            return
        }
        const oldParentId = block.querySelector("[data-task-row]")?.dataset.parentId || ""
        if (!this._placeBlock(block, task)) return
        this._applyParent(block, task.parent_id || "")
        this._refreshCaretFor(oldParentId)
        this._refreshCaretFor(task.parent_id || "")
        this._remotePatch(task, { op: "move" })
    }

    _remoteUpsert(task, { forceInsert } = {}) {
        const id = String(task.id)
        let block = this.element.querySelector(`[data-task-block][data-task-id="${CSS.escape(id)}"]`)
        if (block && !forceInsert) {
            this._remotePatch(task)
            return
        }
        if (block) {
            // Recreate placement for an existing id (rare).
            const oldParentId = block.querySelector("[data-task-row]")?.dataset.parentId || ""
            if (!this._placeBlock(block, task)) return
            this._applyParent(block, task.parent_id || "")
            this._refreshCaretFor(oldParentId)
            this._refreshCaretFor(task.parent_id || "")
            this._remotePatch(task)
            return
        }
        // Drop the empty-list placeholder before inserting a real remote task.
        const root = this.element.querySelector('[data-children-for="root"]')
        if (root) {
            root.querySelectorAll(":scope > [data-task-block]").forEach((b) => {
                const row = b.querySelector("[data-task-row]")
                if (row?.dataset.placeholderPending) b.remove()
            })
        }
        block = this._buildBlockFromPayload(task)
        if (!this._placeBlock(block, task)) {
            return
        }
        this._applyParent(block, task.parent_id || "")
        this._refreshCaretFor(task.parent_id || "")
        this._renderEditingBadges()
    }

    // Inserts `block` under the right parent using after_id when possible.
    // Returns false when the parent container isn't in the DOM yet.
    _placeBlock(block, task) {
        const parentKey = task.parent_id ? String(task.parent_id) : "root"
        const container = this.element.querySelector(`[data-children-for="${CSS.escape(parentKey)}"]`)
        if (!container) return false

        if (block.parentElement) block.remove()

        const afterId = (task.after_id != null && task.after_id !== "") ? String(task.after_id) : null
        if (afterId) {
            const afterBlock = container.querySelector(`:scope > [data-task-block][data-task-id="${CSS.escape(afterId)}"]`)
            if (afterBlock) {
                afterBlock.after(block)
                return true
            }
            // Sibling missing from this view — append as best-effort.
            container.appendChild(block)
            return true
        }
        // First among siblings (after_id null): top of the list/parent.
        container.prepend(block)
        return true
    }

    _caretPosFromClick(element, clientX, clientY) {
        const text = element.textContent || ""
        if (!text.length) return 0

        let range = null
        if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(clientX, clientY)
        } else if (document.caretPositionFromPoint) {
            const pos = document.caretPositionFromPoint(clientX, clientY)
            if (pos) {
                range = document.createRange()
                range.setStart(pos.offsetNode, pos.offset)
                range.collapse(true)
            }
        }
        if (!range || !element.contains(range.startContainer)) return null

        const pre = document.createRange()
        pre.selectNodeContents(element)
        pre.setEnd(range.startContainer, range.startOffset)
        return Math.min(pre.toString().length, text.length)
    }

    // --- Contenteditable caret / range utilities ---
    // The keyboard model works in linear text offsets over the editor's
    // textContent — the contenteditable equivalent of selectionStart/End.

    _selectionOffsets(editor) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return null
        const range = sel.getRangeAt(0)
        if (!editor.contains(range.startContainer) || !editor.contains(range.endContainer)) return null
        const pre = range.cloneRange()
        pre.selectNodeContents(editor)
        pre.setEnd(range.startContainer, range.startOffset)
        const start = pre.toString().length
        return { start, end: start + range.toString().length }
    }

    _positionForOffset(editor, offset) {
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT)
        let acc = 0
        let node
        while ((node = walker.nextNode())) {
            const len = node.data.length
            if (offset <= acc + len) return { node, offset: offset - acc }
            acc += len
        }
        return null
    }

    _setCaretOffset(editor, offset) {
        const sel = window.getSelection()
        if (!sel) return
        const range = document.createRange()
        const pos = this._positionForOffset(editor, offset)
        if (pos) {
            range.setStart(pos.node, pos.offset)
            range.collapse(true)
        } else {
            range.selectNodeContents(editor)
            range.collapse(false)
        }
        sel.removeAllRanges()
        sel.addRange(range)
    }

    _setSelectionOffsets(editor, start, end) {
        const sel = window.getSelection()
        const startPos = this._positionForOffset(editor, start)
        const endPos = this._positionForOffset(editor, end)
        if (!sel || !startPos || !endPos) return
        const range = document.createRange()
        range.setStart(startPos.node, startPos.offset)
        range.setEnd(endPos.node, endPos.offset)
        sel.removeAllRanges()
        sel.addRange(range)
    }

    // Removes [start, end) from the editor and returns it as a DOM fragment
    // (inline formatting intact on both sides of the cut).
    _extractEditorRange(editor, start, end) {
        if (end <= start) return document.createDocumentFragment()
        const range = document.createRange()
        const startPos = this._positionForOffset(editor, start)
        const endPos = this._positionForOffset(editor, end)
        if (startPos) range.setStart(startPos.node, startPos.offset)
        else range.setStart(editor, editor.childNodes.length)
        if (endPos) range.setEnd(endPos.node, endPos.offset)
        else range.setEnd(editor, editor.childNodes.length)
        const fragment = range.extractContents()
        this._pruneEmptyInline(editor)
        this._pruneEmptyInline(fragment)
        return fragment
    }

    // Splitting/extracting can strand empty inline wrappers (e.g. an empty
    // <strong> when the caret sat at its start); typing into those would
    // resurrect unwanted formatting.
    _pruneEmptyInline(root) {
        if (!root.querySelectorAll) return
        root.querySelectorAll("strong, em, del, b, i, s, strike, code, a").forEach((el) => {
            if ((el.textContent || "") === "") el.remove()
        })
    }

    // Replaces the current selection (or appends at the end when the caret is
    // elsewhere) with rendered markdown, leaving the caret after the insert.
    _insertMarkdownAtCaret(editor, md) {
        const sel = window.getSelection()
        let range
        if (sel && sel.rangeCount > 0 && editor.contains(sel.getRangeAt(0).startContainer)) {
            range = sel.getRangeAt(0)
        } else {
            range = document.createRange()
            range.selectNodeContents(editor)
            range.collapse(false)
        }
        range.deleteContents()
        const temp = document.createElement("div")
        temp.innerHTML = mdToHtml(md)
        const fragment = document.createDocumentFragment()
        let last = null
        while (temp.firstChild) {
            last = temp.firstChild
            fragment.appendChild(temp.firstChild)
        }
        range.insertNode(fragment)
        if (last && sel) {
            const after = document.createRange()
            after.setStartAfter(last)
            after.collapse(true)
            sel.removeAllRanges()
            sel.addRange(after)
        }
    }

    // Rich clipboard HTML -> inline markdown subset (DOMParser documents are
    // inert, so pasted markup can't execute anything).
    _markdownFromPastedHtml(html) {
        const doc = new DOMParser().parseFromString(html, "text/html")
        return domToMarkdown(doc.body).replace(/\s+/g, " ").trim()
    }

    // --- Floating formatting toolbar ---

    _editingRow() {
        if (!this._editingId) return null
        return this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(this._editingId)}"]`)
    }

    _activeEditor() {
        const row = this._editingRow()
        if (!row) return null
        const input = row.querySelector("[data-task-input]")
        return input && !input.hidden ? input : null
    }

    _handleSelectionChange() {
        if (this._linkMode || this._panelMode) return
        const editor = this._activeEditor()
        if (!editor) {
            this._hideToolbar()
            return
        }
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) {
            this._hideToolbar()
            return
        }
        // A caret CLICKED into coloured text (not one moved there by typing)
        // shows the toolbar too, aimed at that coloured span, so its colour
        // can be changed or removed without selecting it exactly first.
        if (sel.isCollapsed) {
            const clicked = Date.now() - (this._lastPointerUpAt || 0) < 600
            const span = clicked ? this._styledSpanAt(sel.getRangeAt(0), editor) : null
            if (!span) {
                this._hideToolbar()
                return
            }
            const range = document.createRange()
            range.selectNodeContents(span)
            this._showToolbar(range)
            return
        }
        const range = this._clampToEditor(sel.getRangeAt(0), editor)
        if (!range || range.collapsed) {
            this._hideToolbar()
            return
        }
        this._showToolbar(range)
    }

    // The part of `range` inside the editor, or null when none of it is. A
    // triple-click (Safari especially) can end the selection just past the
    // editor; trimming it beats silently ignoring the toolbar.
    _clampToEditor(range, editor) {
        const startIn = editor.contains(range.startContainer)
        const endIn = editor.contains(range.endContainer)
        if (startIn && endIn) return range
        if (!range.intersectsNode(editor)) return null
        const clamped = document.createRange()
        clamped.selectNodeContents(editor)
        if (startIn) clamped.setStart(range.startContainer, range.startOffset)
        if (endIn) clamped.setEnd(range.endContainer, range.endOffset)
        return clamped
    }

    // The outermost colour/highlight/badge span holding a collapsed caret.
    _styledSpanAt(range, editor) {
        let node = range.startContainer
        let el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
        let found = null
        while (el && el !== editor) {
            if (styleOf(el)) found = el
            el = el.parentElement
        }
        return found && editor.contains(found) ? found : null
    }

    // What a colour action applies to: the selection (trimmed to the editor),
    // or - with just a caret - the whole coloured span it sits in. null when
    // there is nothing to colour.
    _styleRange(input) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return null
        const raw = sel.getRangeAt(0)
        if (raw.collapsed) {
            const span = this._styledSpanAt(raw, input)
            if (!span) return null
            const range = document.createRange()
            range.setStartBefore(span)
            range.setEndAfter(span)
            return range
        }
        const range = this._clampToEditor(raw, input)
        return range && !range.collapsed ? range : null
    }

    // { "color:red": true, "badge:green": true, ... } for the styles on the
    // target text - enclosing it or inside it - so the menu can show them.
    _activeStyles(input) {
        const range = this._styleRange(input)
        const active = {}
        if (!range) return active
        let el = range.commonAncestorContainer
        el = el.nodeType === Node.ELEMENT_NODE ? el : el.parentElement
        while (el && el !== input) {
            const style = styleOf(el)
            if (style) active[`${style.kind}:${style.color}`] = true
            el = el.parentElement
        }
        input.querySelectorAll("span").forEach((span) => {
            const style = styleOf(span)
            if (style && range.intersectsNode(span)) active[`${style.kind}:${style.color}`] = true
        })
        return active
    }

    _showToolbar(range) {
        const bar = this._ensureToolbar()
        this._setLinkPanel(false)
        bar.hidden = false
        this._positionToolbarAt(range)
        this._refreshToolbarState()
        this._refreshToolbarTextStyle()
    }

    _positionToolbarAt(range) {
        const bar = this._toolbar
        const rect = range.getBoundingClientRect()
        const barRect = bar.getBoundingClientRect()
        let top = rect.top - barRect.height - 8
        if (top < 8) top = rect.bottom + 8
        let left = rect.left + rect.width / 2 - barRect.width / 2
        left = Math.max(8, Math.min(left, window.innerWidth - barRect.width - 8))
        bar.style.top = `${top}px`
        bar.style.left = `${left}px`
    }

    // Capture-phase scroll sees every scroller on the page, not just the one
    // holding the editor. Hiding on all of them made the toolbar vanish while
    // the pointer travelled to it whenever something unrelated scrolled (a
    // chat panel following new messages, a lazy list). Only a scroll that
    // moves the editor matters, and then the toolbar follows the selection.
    _handleToolbarScroll(e) {
        if (!this._toolbar || this._toolbar.hidden) return
        const editor = this._activeEditor()
        const target = e.target
        const movesEditor = !editor || target === document || target === document.documentElement ||
            target === document.body || (target.contains && target.contains(editor))
        if (!movesEditor) return
        const sel = window.getSelection()
        const range = this._linkMode ? this._linkRange : (sel && sel.rangeCount ? sel.getRangeAt(0) : null)
        if (editor && range && !range.collapsed && editor.contains(range.startContainer)) {
            this._positionToolbarAt(range)
        } else {
            this._hideToolbar()
        }
    }

    _hideToolbar() {
        if (!this._toolbar || this._linkMode || this._panelMode) return
        this._toolbar.hidden = true
        this._setColorPanel(false)
        this._setTextStylePanel(false)
    }

    _ensureToolbar() {
        if (this._toolbar) return this._toolbar
        const bar = document.createElement("div")
        bar.className = "tasks-richtext-toolbar"
        bar.hidden = true

        const mkButton = (cmd, icon, label) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.dataset.rtCmd = cmd
            btn.title = label
            btn.setAttribute("aria-label", label)
            btn.innerHTML = `<i class="bi ${icon}"></i>`
            return btn
        }

        const buttons = document.createElement("div")
        buttons.className = "tasks-richtext-toolbar-group"
        buttons.setAttribute("data-rt-buttons", "true")
        const commentButton = document.createElement("button")
        commentButton.type = "button"
        commentButton.dataset.rtCmd = "comment"
        commentButton.className = "tasks-richtext-comment-button"
        commentButton.title = "Comment (⇧⌘M)"
        commentButton.setAttribute("aria-label", "Comment")
        commentButton.innerHTML = '<i class="bi bi-chat-left-text"></i><span>Comment</span>'
        buttons.appendChild(commentButton)
        const textStyleButton = document.createElement("button")
        textStyleButton.type = "button"
        textStyleButton.dataset.rtCmd = "text-style"
        textStyleButton.className = "tasks-richtext-text-style-button"
        textStyleButton.title = "Turn into"
        textStyleButton.setAttribute("aria-label", "Turn into")
        textStyleButton.innerHTML = '<span data-rt-text-style-label="true">Text</span><i class="bi bi-chevron-down"></i>'
        buttons.appendChild(textStyleButton)
        const colorButton = mkButton("color", "", "Text color & highlight")
        colorButton.innerHTML = '<span class="tasks-richtext-color-glyph">A</span>'
        colorButton.classList.add("tasks-richtext-color-button")
        buttons.appendChild(colorButton)
        buttons.appendChild(mkButton("bold", "bi-type-bold", "Bold (⌘B)"))
        buttons.appendChild(mkButton("italic", "bi-type-italic", "Italic (⌘I)"))
        buttons.appendChild(mkButton("strike", "bi-type-strikethrough", "Strikethrough (⌘⇧X)"))
        buttons.appendChild(mkButton("code", "bi-code", "Code (⌘E)"))
        buttons.appendChild(mkButton("link", "bi-link-45deg", "Link (⌘K)"))
        if (this.emojiDataValue) buttons.appendChild(mkButton("emoji", "bi-emoji-smile", "Emoji"))

        const linkPanel = document.createElement("div")
        linkPanel.className = "tasks-richtext-toolbar-group tasks-richtext-toolbar-linkpanel"
        linkPanel.setAttribute("data-rt-link-panel", "true")
        linkPanel.hidden = true
        const urlInput = document.createElement("input")
        urlInput.type = "text"
        urlInput.placeholder = "Paste or type a link…"
        urlInput.setAttribute("data-rt-link-input", "true")
        linkPanel.appendChild(urlInput)
        linkPanel.appendChild(mkButton("link-apply", "bi-check-lg", "Apply link"))
        linkPanel.appendChild(mkButton("link-remove", "bi-x-lg", "Remove link"))

        bar.appendChild(buttons)
        bar.appendChild(linkPanel)
        bar.appendChild(this._buildColorPanel())
        bar.appendChild(this._buildTextStylePanel())

        const emojiPanel = document.createElement("div")
        emojiPanel.className = "tasks-richtext-dropdown tasks-richtext-emoji-panel"
        emojiPanel.setAttribute("data-rt-emoji-panel", "true")
        emojiPanel.hidden = true
        emojiPanel.addEventListener("keydown", (ev) => {
            if (ev.key !== "Escape") return
            ev.preventDefault()
            ev.stopPropagation()
            this._closeEmojiPanel(true)
        })
        bar.appendChild(emojiPanel)

        // Keep the editor's selection alive when clicking buttons; the URL
        // input and the emoji picker (its search field) legitimately take focus.
        bar.addEventListener("mousedown", (ev) => {
            if (!ev.target.closest("[data-rt-link-input], emoji-picker")) ev.preventDefault()
        })
        bar.addEventListener("click", (ev) => {
            const styleItem = ev.target.closest("[data-rt-text-style]")
            if (styleItem) {
                ev.preventDefault()
                const row = this._editingRow()
                if (row) this._setRowTextStyle(row, styleItem.dataset.rtTextStyle)
                this._setTextStylePanel(false)
                return
            }
            const swatch = ev.target.closest("[data-rt-style]")
            if (swatch) {
                ev.preventDefault()
                const [kind, color] = swatch.dataset.rtStyle.split(":")
                // Clicking the colour that is already on the text takes it off,
                // as in Notion and Google Docs.
                const off = color === "none" || swatch.classList.contains("is-active")
                this._applyStyle(kind, off ? null : color)
                this._setColorPanel(false)
                return
            }
            const btn = ev.target.closest("[data-rt-cmd]")
            if (!btn) return
            ev.preventDefault()
            const cmd = btn.dataset.rtCmd
            if (cmd === "color") {
                this._setTextStylePanel(false)
                this._setColorPanel(this._colorPanel().hidden)
                return
            }
            if (cmd === "text-style") {
                this._setTextStylePanel(this._textStylePanel().hidden)
                return
            }
            if (cmd === "comment") {
                this._commentFromSelection()
                return
            }
            if (cmd === "emoji") {
                this._openEmojiPanel()
                return
            }
            if (cmd === "link-apply") {
                this._applyLink(urlInput.value)
                return
            }
            if (cmd === "link-remove") {
                this._removeLink()
                return
            }
            const row = this._editingRow()
            if (!row) return
            if (cmd === "link") this._enterLinkMode(row)
            else this._applyFormat(row, cmd)
        })
        urlInput.addEventListener("keydown", (ev) => {
            ev.stopPropagation()
            if (ev.key === "Enter") {
                ev.preventDefault()
                this._applyLink(urlInput.value)
            }
            if (ev.key === "Escape") {
                ev.preventDefault()
                this._exitLinkMode(true)
            }
        })

        document.body.appendChild(bar)
        this._toolbar = bar
        return bar
    }

    _setLinkPanel(showLink) {
        if (!this._toolbar) return
        this._toolbar.querySelector("[data-rt-buttons]").hidden = showLink
        this._toolbar.querySelector("[data-rt-link-panel]").hidden = !showLink
    }

    _refreshToolbarState() {
        if (!this._toolbar || this._toolbar.hidden) return
        const editor = this._activeEditor()
        const sel = window.getSelection()
        const states = { bold: false, italic: false, strike: false, code: false, link: false }
        try {
            states.bold = document.queryCommandState("bold")
            states.italic = document.queryCommandState("italic")
            states.strike = document.queryCommandState("strikeThrough")
        } catch (_) {}
        // Only a real <strong> counts as bold: a heading's weight or a badge's
        // semibold would otherwise light B up (and make ⌘B un-bold).
        if (editor && sel && sel.anchorNode) {
            states.bold = Boolean(this._closestInEditor(sel.anchorNode, "strong, b", editor))
        }
        if (editor && sel && sel.anchorNode) {
            states.code = Boolean(this._closestInEditor(sel.anchorNode, "code", editor))
            states.link = Boolean(this._closestInEditor(sel.anchorNode, "a", editor))
        }
        this._toolbar.querySelectorAll("[data-rt-cmd]").forEach((btn) => {
            const cmd = btn.dataset.rtCmd
            if (cmd in states) btn.classList.toggle("is-active", states[cmd])
        })
    }

    _closestInEditor(node, selector, editor) {
        let el = node && node.nodeType === Node.ELEMENT_NODE ? node : (node ? node.parentElement : null)
        while (el && el !== editor) {
            if (el.matches(selector)) return el
            el = el.parentElement
        }
        return null
    }

    // --- Formatting commands (toolbar buttons + keyboard shortcuts) ---

    _applyFormat(row, kind) {
        const input = row.querySelector("[data-task-input]")
        if (!input || input.hidden) return
        input.focus()
        try { document.execCommand("styleWithCSS", false, false) } catch (_) {}
        // Bold toggles a real <strong> rather than execCommand("bold"): inside
        // a heading or a badge the browser already sees bold (their weight) and
        // would "un-bold" with an inline font-weight span that never saves.
        if (kind === "bold") this._toggleStrong(input)
        else if (kind === "italic") document.execCommand("italic")
        else if (kind === "strike") document.execCommand("strikeThrough")
        else if (kind === "code") this._toggleCode(input)
        this._scheduleSave(row)
        this._refreshToolbarState()
    }

    _toggleStrong(input) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return
        const range = sel.getRangeAt(0)
        if (!input.contains(range.startContainer)) return
        const strong = this._closestInEditor(range.startContainer, "strong, b", input)
        if (strong) {
            const first = strong.firstChild
            const last = strong.lastChild
            strong.replaceWith(...strong.childNodes)
            if (first && last) {
                const restore = document.createRange()
                restore.setStartBefore(first)
                restore.setEndAfter(last)
                sel.removeAllRanges()
                sel.addRange(restore)
            }
            return
        }
        if (range.collapsed) return
        const wrap = document.createElement("strong")
        wrap.appendChild(range.extractContents())
        range.insertNode(wrap)
        this._pruneEmptyInline(input)
        const inner = document.createRange()
        inner.selectNodeContents(wrap)
        sel.removeAllRanges()
        sel.addRange(inner)
    }

    _toggleCode(input) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return
        const range = sel.getRangeAt(0)
        if (!input.contains(range.startContainer)) return
        const codeEl = this._closestInEditor(range.startContainer, "code", input)
        if (codeEl) {
            // Unwrap and select the freed content
            const restore = document.createRange()
            const parent = codeEl.parentNode
            const first = codeEl.firstChild
            const last = codeEl.lastChild
            while (codeEl.firstChild) parent.insertBefore(codeEl.firstChild, codeEl)
            codeEl.remove()
            if (first && last) {
                restore.setStartBefore(first)
                restore.setEndAfter(last)
                sel.removeAllRanges()
                sel.addRange(restore)
            }
            return
        }
        if (range.collapsed) return
        // Code spans are atomic: nested formatting collapses to plain text.
        const content = range.toString()
        if (content === "") return
        range.deleteContents()
        const code = document.createElement("code")
        code.textContent = content
        range.insertNode(code)
        this._pruneEmptyInline(input)
        const inner = document.createRange()
        inner.selectNodeContents(code)
        sel.removeAllRanges()
        sel.addRange(inner)
    }

    // --- Section headings ("Turn into") ---
    // A heading is a label, not a to-do: data-text-style="h1|h2|h3" on the
    // row, and CSS does the rest (no checkbox, no rail, task-only menu items
    // hidden). The server clears due date / repeat / pin / priority / assignees
    // when a row becomes one and says what it cleared.

    _textStyle(row) {
        const style = row?.dataset.textStyle || ""
        return TEXT_STYLES.includes(style) ? style : ""
    }

    _isHeading(row) {
        return this._textStyle(row) !== ""
    }

    _headingPrefix(row) {
        const style = this._textStyle(row)
        return style ? `${"#".repeat(Number(style[1]))} ` : ""
    }

    _setRowTextStyle(row, style) {
        const next = TEXT_STYLES.includes(style) ? style : ""
        if (this._textStyle(row) === next) return
        this._recordFields(row, TEXT_STYLE_NAMES[next] || "Text style")
        // A pending date chip has nothing to land on once this is a heading.
        if (next) {
            row._nlMatch = null
            this._clearDateChip(row)
        }
        row.dataset.textStyle = next
        this._refreshToolbarTextStyle()
        const fd = new FormData()
        fd.set("text_style", next)
        this._resolveId(row.closest("[data-task-block]")).then((id) => {
            if (!id) return
            this._patch(`/tasks/${id}.json`, fd).then((data) => {
                if (!data?.ok) {
                    this._flashNotice(data?.error || "Could not change the row style", "warning")
                    return
                }
                // Becoming a heading may have cleared due date, priority, pin...
                // so redraw the row from the server's answer.
                if (data.task) this._remotePatch(data.task)
                const cleared = Array.isArray(data.cleared) ? data.cleared : []
                if (cleared.length) this._flashNotice(`${TEXT_STYLE_NAMES[next]}: removed ${cleared.join(", ")}`)
            })
        })
    }

    // Typing "# ", "## " or "### " at the very start of a row turns it into
    // that heading and drops the marker, the habit ClickUp and Notion teach.
    // Only when the caret sits right after the marker - i.e. the space was just
    // typed - so editing text that merely begins with "# " never converts.
    _applyHeadingMarkdown(row, input) {
        const text = input.textContent || ""
        const m = text.match(/^(#{1,3})[ \u00a0]/)
        if (!m) return false
        const sel = this._selectionOffsets(input)
        if (!sel || sel.start !== m[0].length || sel.end !== m[0].length) return false
        this._extractEditorRange(input, 0, m[0].length)
        this._setCaretOffset(input, 0)
        this._setRowTextStyle(row, `h${m[1].length}`)
        this._scheduleSave(row)
        return true
    }

    // The toolbar's "Text ▾" label follows the row being edited.
    _refreshToolbarTextStyle() {
        const label = this._toolbar?.querySelector("[data-rt-text-style-label]")
        if (!label) return
        label.textContent = TEXT_STYLE_NAMES[this._textStyle(this._editingRow())] || "Text"
        this._toolbar.querySelectorAll("[data-rt-text-style]").forEach((btn) => {
            btn.classList.toggle("is-active", btn.dataset.rtTextStyle === this._textStyle(this._editingRow()))
        })
    }

    _textStylePanel() {
        return this._ensureToolbar().querySelector("[data-rt-text-style-panel]")
    }

    _setTextStylePanel(open) {
        if (!this._toolbar) return
        const panel = this._toolbar.querySelector("[data-rt-text-style-panel]")
        if (!panel) return
        if (open) {
            this._setColorPanel(false)
            this._refreshToolbarTextStyle()
        }
        panel.hidden = !open
        this._toolbar.querySelector('[data-rt-cmd="text-style"]')?.classList.toggle("is-open", open)
        if (open) this._placeDropdown(panel)
    }

    _buildTextStylePanel() {
        const panel = document.createElement("div")
        panel.className = "tasks-richtext-dropdown tasks-richtext-text-style-panel"
        panel.setAttribute("data-rt-text-style-panel", "true")
        panel.hidden = true
        const heading = document.createElement("div")
        heading.className = "tasks-richtext-color-label"
        heading.textContent = "Turn into"
        panel.appendChild(heading)
        ;["", ...TEXT_STYLES].forEach((value) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = `tasks-richtext-text-style-item tasks-richtext-text-style-item--${value || "text"}`
            btn.dataset.rtTextStyle = value
            const key = value ? value[1] : "0"
            btn.innerHTML = `<span class="tasks-richtext-text-style-glyph">${value ? `H<sub>${value[1]}</sub>` : "T"}</span><span class="tasks-richtext-text-style-name">${TEXT_STYLE_NAMES[value]}</span><kbd>⌥⌘${key}</kbd>`
            panel.appendChild(btn)
        })
        return panel
    }

    // --- Text colour, highlight and badge (feedback #209) ---
    // A ClickUp-style dropdown under the toolbar: Recent, Text colors,
    // Text highlights, Badges, Remove color. Each pick wraps the selection in
    // a <span class="tj-<kind> tj-<kind>--<color>">, which domToMarkdown
    // serializes to {kind:color}...{/kind}.

    _buildColorPanel() {
        const panel = document.createElement("div")
        panel.className = "tasks-richtext-dropdown tasks-richtext-color-panel"
        panel.setAttribute("data-rt-color-panel", "true")
        panel.hidden = true

        const section = (label, attrs = {}) => {
            const wrap = document.createElement("div")
            wrap.className = "tasks-richtext-color-section"
            Object.entries(attrs).forEach(([k, v]) => wrap.setAttribute(k, v))
            const heading = document.createElement("div")
            heading.className = "tasks-richtext-color-label"
            heading.textContent = label
            const row = document.createElement("div")
            row.className = "tasks-richtext-swatches"
            wrap.appendChild(heading)
            wrap.appendChild(row)
            panel.appendChild(wrap)
            return row
        }

        const recent = section("Recent  ⌘⇧H", { "data-rt-recent": "true" })
        recent.setAttribute("data-rt-recent-swatches", "true")
        const text = section("Text colors")
        text.appendChild(this._swatch("color", null))
        STYLE_COLORS.forEach((c) => text.appendChild(this._swatch("color", c)))
        const highlights = section("Text highlights")
        STYLE_COLORS.forEach((c) => highlights.appendChild(this._swatch("highlight", c)))
        highlights.appendChild(this._swatch("highlight", null))
        const badges = section("Badges")
        STYLE_COLORS.forEach((c) => badges.appendChild(this._swatch("badge", c)))
        badges.appendChild(this._swatch("badge", null))

        const remove = document.createElement("button")
        remove.type = "button"
        remove.className = "tasks-richtext-remove-color"
        remove.dataset.rtStyle = "all:none"
        remove.innerHTML = '<i class="bi bi-slash-circle"></i> Remove color'
        panel.appendChild(remove)
        return panel
    }

    // One swatch button. color null is "Default" for text and "None" for
    // highlight/badge (a slashed circle).
    _swatch(kind, color) {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = `tasks-richtext-swatch tasks-richtext-swatch--${kind}`
        btn.dataset.rtStyle = `${kind}:${color || "none"}`
        const name = color ? color[0].toUpperCase() + color.slice(1) : (kind === "color" ? "Default" : "None")
        const label = kind === "color" ? `${name} text` : kind === "highlight" ? `${name} highlight` : `${name} badge`
        btn.title = label
        btn.setAttribute("aria-label", label)
        if (kind === "color") {
            btn.innerHTML = color ? `<span class="tj-color tj-color--${color}">A</span>` : "<span>A</span>"
        } else if (color) {
            btn.innerHTML = `<span class="tj-${kind} tj-${kind}--${color}"></span>`
        } else {
            btn.innerHTML = '<i class="bi bi-slash-circle"></i>'
        }
        return btn
    }

    _colorPanel() {
        return this._ensureToolbar().querySelector("[data-rt-color-panel]")
    }

    _setColorPanel(open) {
        if (!this._toolbar) return
        const panel = this._toolbar.querySelector("[data-rt-color-panel]")
        if (!panel) return
        if (open) {
            this._renderRecentSwatches(panel)
            // Ring the colour, highlight or badge already on the text; clicking
            // a ringed swatch takes it off (see the swatch click handler).
            const input = this._activeEditor()
            const active = input ? this._activeStyles(input) : {}
            panel.querySelectorAll("[data-rt-style]").forEach((btn) => {
                btn.classList.toggle("is-active", Boolean(active[btn.dataset.rtStyle]))
            })
        }
        panel.hidden = !open
        this._toolbar.querySelector('[data-rt-cmd="color"]')?.classList.toggle("is-open", open)
        if (open) this._placeDropdown(panel)
    }

    // Dropdowns hang below the toolbar, or above it when the toolbar sits low
    // in the window.
    _placeDropdown(panel) {
        panel.classList.remove("is-above")
        const rect = panel.getBoundingClientRect()
        if (rect.bottom > window.innerHeight - 8) panel.classList.add("is-above")
    }

    _recentStyles() {
        try {
            const list = JSON.parse(localStorage.getItem(RECENT_STYLES_KEY) || "[]")
            return Array.isArray(list) ? list.filter((v) => typeof v === "string" && /^(color|highlight|badge):[a-z]+$/.test(v)) : []
        } catch {
            return []
        }
    }

    _rememberStyle(kind, color) {
        const key = `${kind}:${color}`
        const list = [key, ...this._recentStyles().filter((v) => v !== key)].slice(0, RECENT_STYLES_MAX)
        try { localStorage.setItem(RECENT_STYLES_KEY, JSON.stringify(list)) } catch { /* ignore */ }
    }

    _renderRecentSwatches(panel) {
        const recent = this._recentStyles().filter((v) => STYLE_COLORS.includes(v.split(":")[1]))
        const wrap = panel.querySelector("[data-rt-recent]")
        const row = panel.querySelector("[data-rt-recent-swatches]")
        row.replaceChildren(...recent.map((v) => {
            const [kind, color] = v.split(":")
            return this._swatch(kind, color)
        }))
        wrap.hidden = recent.length === 0
    }

    _applyRecentStyle(row) {
        const recent = this._recentStyles()[0]
        if (!recent) return
        const [kind, color] = recent.split(":")
        if (this._editingRow() !== row) return
        this._applyStyle(kind, color)
    }

    // Applies (color set) or clears (color null) one kind - or, with kind
    // "all", clears every kind - across the current selection in the editor.
    _applyStyle(kind, color) {
        const row = this._editingRow()
        const input = this._activeEditor()
        if (!row || !input) return
        const range = this._styleRange(input)
        if (!range) return
        // Focus only if needed, then put the range back: in WebKit, focus()
        // on a contenteditable that isn't focused resets the caret to its
        // start, which made the colour menu do nothing.
        if (document.activeElement !== input) input.focus()
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
        // A badge is its own fill and text colour, so it replaces a highlight
        // or colour and is replaced by either; colour and highlight stack.
        const kinds = (kind === "all" || kind === "badge") ? STYLE_KINDS : [kind, "badge"]

        // A span of the same kind wrapping the selection would still colour
        // it from outside; split it so the selection stands on its own.
        this._splitStyleAncestors(range, input, kinds)

        const fragment = range.extractContents()
        fragment.querySelectorAll("span").forEach((span) => {
            const style = styleOf(span)
            if (style && kinds.includes(style.kind)) span.replaceWith(...span.childNodes)
        })

        let first
        let last
        if (color && kind !== "all") {
            const span = document.createElement("span")
            span.className = `tj-${kind} tj-${kind}--${color}`
            span.appendChild(fragment)
            range.insertNode(span)
            first = span
            last = span
            this._rememberStyle(kind, color)
        } else {
            first = fragment.firstChild
            last = fragment.lastChild
            range.insertNode(fragment)
        }
        this._pruneEmptyInline(input)

        if (first && last && input.contains(first) && input.contains(last)) {
            const next = document.createRange()
            next.setStartBefore(first)
            next.setEndAfter(last)
            sel.removeAllRanges()
            sel.addRange(next)
        }
        this._scheduleSave(row)
        this._refreshToolbarState()
    }

    // Splits every style span of `kinds` that encloses `range` into
    // before / selection / after, then unwraps the selection part, leaving
    // `range` around exactly the selected nodes.
    _splitStyleAncestors(range, input, kinds) {
        const enclosing = () => {
            let el = range.commonAncestorContainer
            if (el.nodeType !== Node.ELEMENT_NODE) el = el.parentElement
            while (el && el !== input) {
                const style = styleOf(el)
                if (style && kinds.includes(style.kind)) return el
                el = el.parentElement
            }
            return null
        }
        let el
        while ((el = enclosing())) {
            const before = document.createRange()
            before.setStart(el, 0)
            before.setEnd(range.startContainer, range.startOffset)
            const beforeFragment = before.extractContents()
            const after = document.createRange()
            after.setStart(range.endContainer, range.endOffset)
            after.setEnd(el, el.childNodes.length)
            const afterFragment = after.extractContents()
            if (beforeFragment.textContent !== "") {
                const head = el.cloneNode(false)
                head.appendChild(beforeFragment)
                el.before(head)
            }
            if (afterFragment.textContent !== "") {
                const tail = el.cloneNode(false)
                tail.appendChild(afterFragment)
                el.after(tail)
            }
            const children = Array.from(el.childNodes)
            if (children.length === 0) {
                el.remove()
                return
            }
            el.replaceWith(...children)
            range.setStartBefore(children[0])
            range.setEndAfter(children[children.length - 1])
        }
    }

    // --- Emoji (toolbar picker) ---
    // The shared <emoji-picker> (emoji-picker-element, locally served data -
    // the same one reactions use). The pick lands at the end of the selection,
    // so choosing an emoji never overwrites the text you had selected.

    _openEmojiPanel() {
        const input = this._activeEditor()
        const sel = window.getSelection()
        if (!input || !sel || sel.rangeCount === 0) return
        const bar = this._ensureToolbar()
        const panel = bar.querySelector("[data-rt-emoji-panel]")
        if (!panel) return
        this._setColorPanel(false)
        this._setTextStylePanel(false)
        this._panelRange = sel.getRangeAt(0).cloneRange()
        this._panelMode = "emoji"
        if (!this._emojiPicker) {
            this._emojiPicker = new Picker({ dataSource: this.emojiDataValue })
            const dark = document.documentElement.getAttribute("data-bs-theme") === "dark"
            this._emojiPicker.classList.add(dark ? "dark" : "light")
            this._emojiPicker.addEventListener("emoji-click", (event) => {
                const detail = event.detail || {}
                const emoji = detail.unicode || (detail.emoji && detail.emoji.unicode)
                if (emoji) this._insertEmoji(emoji)
            })
            panel.appendChild(this._emojiPicker)
        }
        panel.hidden = false
        bar.querySelector('[data-rt-cmd="emoji"]')?.classList.add("is-open")
        this._placeDropdown(panel)
    }

    // refocus: put the caret back in the editor (Escape / after a pick).
    // Without it - a click elsewhere on the page - editing ends the way a blur
    // would have, since the editor's own blur was suppressed while open.
    _closeEmojiPanel(refocus) {
        if (!this._panelMode) return
        this._panelMode = null
        const panel = this._toolbar?.querySelector("[data-rt-emoji-panel]")
        if (panel) panel.hidden = true
        this._toolbar?.querySelector('[data-rt-cmd="emoji"]')?.classList.remove("is-open")
        const row = this._editingRow()
        const input = this._activeEditor()
        const range = this._panelRange
        this._panelRange = null
        if (refocus && input) {
            input.focus()
            if (range) {
                const sel = window.getSelection()
                sel.removeAllRanges()
                sel.addRange(range)
            }
            return
        }
        this._hideToolbar()
        if (row && document.activeElement !== input) {
            this._saveTitle(row)
            this._stopEditing()
        }
    }

    _insertEmoji(emoji) {
        const row = this._editingRow()
        const input = this._activeEditor()
        const range = this._panelRange
        this._closeEmojiPanel(true)
        if (!row || !input) return
        const sel = window.getSelection()
        if (range && input.contains(range.endContainer)) {
            const caret = range.cloneRange()
            caret.collapse(false)
            sel.removeAllRanges()
            sel.addRange(caret)
        }
        try { document.execCommand("insertText", false, emoji) } catch (_) {}
        this._scheduleSave(row)
    }

    // --- Link editing (inline URL panel in the toolbar) ---

    _enterLinkMode(row) {
        const input = row.querySelector("[data-task-input]")
        const sel = window.getSelection()
        if (!input || !sel || sel.rangeCount === 0) return
        const range = sel.getRangeAt(0)
        if (!input.contains(range.startContainer)) return
        const anchor = this._closestInEditor(range.startContainer, "a", input)
        if (range.collapsed && !anchor) return
        const bar = this._ensureToolbar()
        this._linkMode = true
        this._linkRange = range.cloneRange()
        bar.hidden = false
        this._setLinkPanel(true)
        this._positionToolbarAt(range)
        const urlInput = bar.querySelector("[data-rt-link-input]")
        urlInput.value = anchor ? (anchor.getAttribute("href") || "") : ""
        urlInput.focus()
        urlInput.select()
    }

    _exitLinkMode(refocus) {
        this._linkMode = false
        this._setLinkPanel(false)
        this._hideToolbar()
        const row = this._editingRow()
        const input = row ? row.querySelector("[data-task-input]") : null
        if (refocus && input) {
            input.focus()
            if (this._linkRange) {
                const sel = window.getSelection()
                sel.removeAllRanges()
                sel.addRange(this._linkRange)
            }
        }
        this._linkRange = null
    }

    _applyLink(rawUrl) {
        const row = this._editingRow()
        const input = row ? row.querySelector("[data-task-input]") : null
        if (!input) {
            this._exitLinkMode(false)
            return
        }
        let url = String(rawUrl || "").trim()
        if (url === "") {
            this._removeLink()
            return
        }
        if (!/^(https?:\/\/|mailto:)/i.test(url)) url = `https://${url}`
        input.focus()
        const sel = window.getSelection()
        if (this._linkRange) {
            sel.removeAllRanges()
            sel.addRange(this._linkRange)
        }
        const anchor = sel.anchorNode ? this._closestInEditor(sel.anchorNode, "a", input) : null
        if (anchor && sel.isCollapsed) {
            anchor.setAttribute("href", url)
        } else {
            try { document.execCommand("createLink", false, url) } catch (_) {}
        }
        input.querySelectorAll("a").forEach((a) => {
            a.setAttribute("target", "_blank")
            a.setAttribute("rel", "noopener noreferrer")
        })
        this._exitLinkMode(true)
        this._scheduleSave(row)
    }

    _removeLink() {
        const row = this._editingRow()
        const input = row ? row.querySelector("[data-task-input]") : null
        if (!input) {
            this._exitLinkMode(false)
            return
        }
        input.focus()
        const sel = window.getSelection()
        if (this._linkRange) {
            sel.removeAllRanges()
            sel.addRange(this._linkRange)
        }
        const anchor = sel.anchorNode ? this._closestInEditor(sel.anchorNode, "a", input) : null
        if (anchor) {
            const contents = document.createRange()
            contents.selectNodeContents(anchor)
            sel.removeAllRanges()
            sel.addRange(contents)
        }
        try { document.execCommand("unlink") } catch (_) {}
        this._exitLinkMode(true)
        this._scheduleSave(row)
    }

    // Only considers visible rows so navigation skips inside collapsed containers
    _neighborRow(row, dir) {
        const rows = Array.from(this.element.querySelectorAll("[data-task-row]")).filter((r) => r === row || r.offsetParent !== null)
        const idx = rows.indexOf(row)
        if (idx === -1) return null
        if (dir === "prev") return rows[idx - 1] || null
        return rows[idx + 1] || null
    }

    _focusNeighbor(row, dir, caretPos) {
        const next = this._neighborRow(row, dir)
        if (!next) return
        this._setEditing(next.dataset.taskId, caretPos)
    }

    _hasChildren(row) {
        return this._childBlocks(row).length > 0
    }

    _childBlocks(row) {
        const container = this.element.querySelector(`[data-children-for="${CSS.escape(row.dataset.taskId)}"]`)
        if (!container) return []
        return Array.from(container.querySelectorAll(":scope > [data-task-block]"))
    }

    // Sibling blocks within the same children container, skipping guide elements
    _previousSiblingBlock(block) {
        let el = block ? block.previousElementSibling : null
        while (el && !el.hasAttribute("data-task-block")) el = el.previousElementSibling
        return el
    }

    _nextSiblingBlock(block) {
        let el = block ? block.nextElementSibling : null
        while (el && !el.hasAttribute("data-task-block")) el = el.nextElementSibling
        return el
    }

    _depthForRow(row) {
        const parentId = row.dataset.parentId
        if (!parentId) return 0
        const parentRow = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(parentId)}"]`)
        return parentRow ? Number(parentRow.dataset.depth || "0") + 1 : 0
    }

    // Resolve a block to its real (server) id, waiting for a pending create if
    // needed. The empty-list placeholder persists lazily on first use.
    _resolveId(block) {
        if (!block) return Promise.resolve(null)
        if (block._realId) return block._realId
        const row = block.querySelector("[data-task-row]")
        if (row && row.dataset.placeholderPending) return this._promotePlaceholder(block)
        return Promise.resolve(block.dataset.taskId)
    }


    // The server resolves the target task list from params[:list] and falls
    // back to the default list, so every outline request must carry the list
    // id — otherwise actions on a non-default list silently hit the default.
    _withList(url) {
        if (!this.hasListValue || !this.listValue) return url
        return `${url}${url.includes("?") ? "&" : "?"}list=${encodeURIComponent(this.listValue)}`
    }

    // --- Mention autocomplete (@) ---
    // Typing "@" in a task title opens a typeahead (GET
    // /tasks/mention_candidates): blue users (list members + your connections)
    // first, then — once you type a query — your gray contacts, then companies.
    //
    // People insert a chip that serializes back to the @[Name](user:ID) /
    // @[Name](contact:ID) token domToMarkdown understands, which the server
    // reconciles into TaskMentions.
    //
    // COMPANIES DO NOT. Picking one writes a TaskLink row and leaves the title
    // text clean, exactly the way the "#" shortcut writes tasks.tags instead of
    // leaving a token behind. That is the whole reason to do it this way: the
    // pipeline tools resolve threads from TaskLink, and a link whose lifetime is
    // the lifetime of a word in a title is one they cannot trust — retyping a
    // title would silently drop the agent's links and the tools would go quiet
    // with nothing to find. Same row, same meaning, whoever created it.
    //
    // The last row is "Add <name>" when nothing matched: asserting a company
    // from a task row is how a target nobody has emailed gets into Jane at all
    // (Companies::Upsert, the same path companies.create takes).

    // Consumes navigation keys while the dropdown is open; returns true when the
    // key was handled so onKeyDown stops.
    _handleMentionKeydown(e) {
        if (e.key === "Escape") { e.preventDefault(); this._closeMention(); return true }
        const items = (this._mention && this._mention.items) || []
        if (items.length === 0) return false
        if (e.key === "ArrowDown") { e.preventDefault(); this._moveMention(1); return true }
        if (e.key === "ArrowUp") { e.preventDefault(); this._moveMention(-1); return true }
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault()
            this._pickMention(this._mention.index)
            return true
        }
        return false
    }

    // Look just left of a collapsed caret for an "@query" run at a word
    // boundary; open/refresh the dropdown for it, or close it otherwise.
    _detectMention(input, { companiesOnly = false } = {}) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return this._closeMention()
        const node = sel.anchorNode
        if (!node || node.nodeType !== Node.TEXT_NODE || !input.contains(node)) return this._closeMention()

        const offset = sel.anchorOffset
        // A query may run to three words ("@Jane Doe", "@Acme Legal Group") so a
        // two-part name survives the space; a contenteditable turns a trailing
        // space into a no-break space, which is mapped back 1:1 before matching.
        const match = node.data.slice(0, offset).replace(/ /g, " ").match(/(?:^|\s)@([^\s@]+(?: [^\s@]+){0,2} ?|)$/)
        if (!match) return this._closeMention()

        const query = match[1]
        this._mention = {
            input,
            node,
            atOffset: offset - query.length - 1,
            caretOffset: offset,
            query,
            companiesOnly,
            items: (this._mention && this._mention.query === query) ? this._mention.items : [],
            index: (this._mention && this._mention.query === query) ? this._mention.index : -1
        }
        this._ensureMentionMenu()
        this._positionMentionMenu()
        this._renderMentionItems()
        this._scheduleMentionFetch(query)
    }

    _scheduleMentionFetch(query) {
        if (this._mentionDebounce) clearTimeout(this._mentionDebounce)
        this._mentionDebounce = setTimeout(() => this._fetchMentions(query), 180)
    }

    async _fetchMentions(query) {
        try {
            const url = this._withList(`/tasks/mention_candidates?q=${encodeURIComponent(query)}`)
            const res = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!res.ok) throw new Error("mention candidates failed")
            const payload = await res.json()
            if (!this._mention || this._mention.query !== query) return
            const companiesOnly = this._mention.companiesOnly
            const people = companiesOnly ? [] : (payload.people || [])
            const companies = payload.companies || []
            const items = people.concat(companies)
            // Offer to create only once the query is worth a name and nothing
            // came back for it - never as a first suggestion on an empty "@".
            if (query.trim().length >= 2 && companies.length === 0) {
                items.push({ kind: "new_company", token: "new_company", name: query.trim() })
            }
            this._mention.items = items
            this._mention.index = this._mention.items.length ? 0 : -1
            this._renderMentionItems()
        } catch (_error) {
            this._closeMention()
        }
    }

    _ensureMentionMenu() {
        if (this._mentionMenu) return
        const menu = document.createElement("div")
        menu.className = "tasks-mention-menu"
        menu.setAttribute("role", "listbox")
        document.body.appendChild(menu)
        this._mentionMenu = menu
    }

    _renderMentionItems() {
        const menu = this._mentionMenu
        if (!menu || !this._mention) return
        const items = this._mention.items || []
        menu.replaceChildren()
        if (items.length === 0) {
            menu.hidden = true
            return
        }
        items.forEach((person, index) => {
            const isContact = person.kind === "contact"
            const isCompany = person.kind === "company" || person.kind === "new_company"
            // Divider ahead of the first row of each block, separating the blue
            // user block (members + connections) from contacts, and both from
            // companies — which are a different KIND of pick, not just a
            // different colour, so the label matters more here.
            const prev = items[index - 1]
            if (isContact && (!prev || prev.kind !== "contact")) {
                const divider = document.createElement("div")
                divider.className = "tasks-mention-divider"
                divider.textContent = "Contacts"
                menu.appendChild(divider)
            }
            if (isCompany && (!prev || (prev.kind !== "company" && prev.kind !== "new_company"))) {
                const divider = document.createElement("div")
                divider.className = "tasks-mention-divider"
                divider.textContent = "Companies"
                menu.appendChild(divider)
            }
            const row = document.createElement("button")
            row.type = "button"
            row.className = "tasks-mention-item"
            row.classList.toggle("tasks-mention-item--contact", isContact)
            row.classList.toggle("tasks-mention-item--company", isCompany)
            row.classList.toggle("is-active", index === this._mention.index)
            if (person.kind === "new_company") {
                const plus = document.createElement("i")
                plus.className = "bi bi-plus-circle tasks-mention-item-icon"
                row.appendChild(plus)
            } else if (isCompany && person.avatar_url) {
                const logo = document.createElement("img")
                logo.className = "tasks-mention-item-logo"
                logo.src = person.avatar_url
                logo.alt = ""
                // A favicon that fails to load falls back to the building icon
                // rather than a broken-image glyph.
                logo.addEventListener("error", () => {
                    const building = document.createElement("i")
                    building.className = "bi bi-building tasks-mention-item-icon"
                    logo.replaceWith(building)
                }, { once: true })
                row.appendChild(logo)
            } else if (isCompany) {
                const building = document.createElement("i")
                building.className = "bi bi-building tasks-mention-item-icon"
                row.appendChild(building)
            } else {
                row.appendChild(this._buildChipEl({ avatar_url: person.avatar_url, label: person.name, initials: person.initials, hue: isContact ? "contact" : null }))
            }
            // Name over a second line of context (title and company for a
            // person, industry and domain for a company), LinkedIn-style: two
            // "Kristof De ..." rows are told apart by the line under the name.
            const text = document.createElement("span")
            text.className = "tasks-mention-item-text"
            const name = document.createElement("span")
            name.className = "tasks-mention-item-name"
            name.textContent = person.kind === "new_company" ? `Add "${person.name}"` : person.name
            text.appendChild(name)
            const metaText = person.kind === "new_company" ? "New company" : person.meta
            if (metaText) {
                const meta = document.createElement("span")
                meta.className = "tasks-mention-item-meta"
                meta.textContent = metaText
                text.appendChild(meta)
            }
            row.appendChild(text)
            // mousedown (not click) so the pick lands before the input's blur.
            row.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickMention(index) })
            row.addEventListener("mouseenter", () => this._setMentionIndex(index))
            menu.appendChild(row)
        })
        menu.hidden = false
    }

    _positionMentionMenu() {
        const menu = this._mentionMenu
        if (!menu) return
        const sel = window.getSelection()
        let rect = null
        if (sel && sel.rangeCount > 0) rect = sel.getRangeAt(0).getBoundingClientRect()
        if (!rect || (rect.top === 0 && rect.left === 0)) {
            rect = this._mention.input.getBoundingClientRect()
        }
        menu.style.position = "fixed"
        menu.style.top = `${Math.round(rect.bottom + 4)}px`
        menu.style.left = `${Math.round(rect.left)}px`
    }

    _moveMention(delta) {
        const items = (this._mention && this._mention.items) || []
        if (items.length === 0) return
        let next = this._mention.index + delta
        if (next < 0) next = items.length - 1
        if (next >= items.length) next = 0
        this._setMentionIndex(next)
    }

    _setMentionIndex(index) {
        if (!this._mention) return
        this._mention.index = index
        if (!this._mentionMenu) return
        // Only .tasks-mention-item buttons map to items indices; the "Contacts"
        // divider is skipped so highlighting stays aligned with the item list.
        Array.from(this._mentionMenu.querySelectorAll(".tasks-mention-item")).forEach((row, i) => row.classList.toggle("is-active", i === index))
    }

    _pickMention(index) {
        const m = this._mention
        if (!m) return
        const person = (m.items || [])[index]
        if (!person) { this._closeMention(); return }
        if (person.kind === "company" || person.kind === "new_company") {
            this._pickInlineCompanyMention(m, person)
            return
        }
        const parsed = String(person.token).match(/^(user|contact):(\d+)$/)
        if (!parsed) { this._closeMention(); return }
        const kind = parsed[1]
        const id = parsed[2]

        // Replace the typed "@query" run with a chip + trailing space.
        const range = document.createRange()
        try {
            range.setStart(m.node, m.atOffset)
            range.setEnd(m.node, Math.min(m.caretOffset, m.node.data.length))
        } catch (_error) {
            this._closeMention()
            return
        }
        range.deleteContents()

        // User -> blue profile-link anchor; contact -> gray non-link span with
        // data-mention (the domToMarkdown serialization anchor). Both are
        // contenteditable=false so the chip stays atomic in the editor.
        let chip
        if (kind === "contact") {
            chip = document.createElement("span")
            chip.className = "task-mention task-mention--contact"
            chip.setAttribute("data-mention", `contact:${id}`)
        } else {
            chip = document.createElement("a")
            chip.href = `/u/${id}`
            chip.className = "task-mention"
        }
        chip.setAttribute("contenteditable", "false")
        chip.textContent = `@${person.name}`
        const space = document.createTextNode(" ")
        range.insertNode(space)
        range.insertNode(chip)

        const after = document.createRange()
        after.setStartAfter(space)
        after.collapse(true)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(after)

        const input = m.input
        this._closeMention()
        const row = input.closest("[data-task-row]")
        if (row) this._saveTitle(row)
    }

    // A company pick: strip the typed "@query" run, save the (now clean) title,
    // then write the link and drop the returned chip into the row. The chip HTML
    // comes from the server so this and the server-rendered row can never drift
    // apart. Failure leaves the text stripped but shows nothing - the row is
    // still correct, just unlinked, and the pick can be repeated.
    async _pickCompanyMention(m, person) {
        const input = m.input
        const row = input.closest("[data-task-row]")
        const range = document.createRange()
        try {
            range.setStart(m.node, m.atOffset)
            range.setEnd(m.node, Math.min(m.caretOffset, m.node.data.length))
        } catch (_error) {
            this._closeMention()
            return
        }
        range.deleteContents()
        const sel = window.getSelection()
        sel.removeAllRanges()
        range.collapse(true)
        sel.addRange(range)
        this._closeMention()
        if (!row) return

        this._saveTitle(row)
        const body = person.kind === "new_company" ? { name: person.name } : { company_id: String(person.token).split(":")[1] }
        try {
            const res = await fetch(`/tasks/${row.dataset.taskId}/company_link`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
                credentials: "same-origin",
                body: JSON.stringify(body)
            })
            if (!res.ok) throw new Error(`link failed: ${res.status}`)
            const payload = await res.json()
            if (!payload.ok || !payload.chip_html) return

            this._insertCompanyChip(row, payload.chip_html)
        } catch (error) {
            console.warn("company link failed", error)
        }
    }

    // A company picked in a TITLE: an inline chip serialized as
    // @[Name](company:ID). Saving the title is what links it - Tasks::MentionSync
    // writes the TaskLink for every company token - so the only extra step is
    // for "Add <name>", which has no id until the company exists: that one is
    // created (and linked) first through the same company_link endpoint.
    async _pickInlineCompanyMention(m, person) {
        const input = m.input
        const row = input.closest("[data-task-row]")
        const range = document.createRange()
        try {
            range.setStart(m.node, m.atOffset)
            range.setEnd(m.node, Math.min(m.caretOffset, m.node.data.length))
        } catch (_error) {
            this._closeMention()
            return
        }
        range.deleteContents()
        this._closeMention()
        if (!row) return

        let id = person.kind === "company" ? String(person.token).split(":")[1] : null
        let name = person.name
        if (!id) {
            try {
                const res = await fetch(`/tasks/${row.dataset.taskId}/company_link`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
                    credentials: "same-origin",
                    body: JSON.stringify({ name: person.name })
                })
                if (!res.ok) throw new Error(`link failed: ${res.status}`)
                const payload = await res.json()
                if (!payload.ok || !payload.company) throw new Error("no company")
                id = String(payload.company.id)
                name = payload.company.name
            } catch (error) {
                console.warn("company create failed", error)
                return
            }
        }

        const chip = document.createElement("span")
        chip.className = "task-mention task-mention--company"
        chip.setAttribute("data-mention", `company:${id}`)
        chip.setAttribute("contenteditable", "false")
        chip.textContent = name
        const space = document.createTextNode(" ")
        range.insertNode(space)
        range.insertNode(chip)

        const after = document.createRange()
        after.setStartAfter(space)
        after.collapse(true)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(after)

        // The trailing chip would now repeat the inline one; the server stops
        // rendering it on the next load, so drop it from the live row too.
        row.querySelectorAll(`.tasks-outline-company[data-company-id="${id}"]`).forEach((el) => el.remove())
        this._saveTitle(row)
    }

    _removeCompanyChip({ taskId }) {
        if (!taskId) return

        const row = this.element.querySelector(`[data-task-row][data-task-id="${taskId}"]`)
        if (!row) return

        row.querySelectorAll(".tasks-outline-company").forEach((chip) => chip.remove())
    }

    // The chip belongs in the title wrap next to the tag chips, and there is
    // only ever one: a row about two companies is a row that wants splitting.
    _insertCompanyChip(row, html) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return

        wrap.querySelectorAll(".tasks-outline-company").forEach((el) => el.remove())
        const holder = document.createElement("div")
        holder.innerHTML = html.trim()
        const chip = holder.firstElementChild
        if (!chip) return

        // The server-rendered row has it after the tags and before the note,
        // comment and file chips, so insert it there.
        const tags = wrap.querySelector("[data-task-tags]")
        const counts = wrap.querySelector(COUNT_CHIPS)
        if (tags) tags.after(chip)
        else if (counts) counts.before(chip)
        else wrap.appendChild(chip)
    }

    _closeMention() {
        if (this._mentionDebounce) { clearTimeout(this._mentionDebounce); this._mentionDebounce = null }
        this._mention = null
        if (this._mentionMenu) {
            this._mentionMenu.remove()
            this._mentionMenu = null
        }
    }

    // --- Slash menu (/) ---
    // A row that holds nothing but "/" and a filter opens a menu of what can
    // go INTO this row: a block (Tasks::FromTemplate, which takes the row's
    // place), a heading, a date, an assignee, a priority, a person, a
    // company, a tag, Jane: everything the row's own menu can set. What you do WITH a
    // row - duplicate, move, send, delete - stays in the row's own menu.
    // Each item opens the picker the row already has and names its shortcut,
    // so "/" teaches "@", "#" and "##" rather than competing with them. Only a
    // row that starts with "/" opens it, so a URL or "and/or" never does.
    // The menu is the @-mention menu's (.tasks-mention-*), so the two match.

    _slashItems() {
        const blocks = (this._slashBlocks || []).map((b) => ({ group: "Blocks", label: b.label, meta: b.summary, icon: "bi-file-earmark-plus", run: (row) => this._slashAddBlock(row, b) }))
        return blocks.concat([
            { group: "Blocks", label: "Browse blocks…", meta: "See one filled in before adding it", icon: "bi-grid", run: () => this._slashBrowseBlocks() },
            { group: "Turn into", label: "Text", hint: "⌥⌘0", icon: "bi-fonts", run: (row) => this._setRowTextStyle(row, "") },
            { group: "Turn into", label: "Heading 1", hint: "#", icon: "bi-type-h1", run: (row) => this._setRowTextStyle(row, "h1") },
            { group: "Turn into", label: "Heading 2", hint: "##", icon: "bi-type-h2", run: (row) => this._setRowTextStyle(row, "h2") },
            { group: "Turn into", label: "Heading 3", hint: "###", icon: "bi-type-h3", run: (row) => this._setRowTextStyle(row, "h3") },
            { group: "Add to this task", label: "Date or repeat", hint: "tomorrow", icon: "bi-calendar-event", run: (row) => this._openDatePopover(row, row) },
            { group: "Add to this task", label: "Assign", icon: "bi-person-check", run: (row) => this._openAssignPopover(row, row) },
            { group: "Add to this task", label: "Priority", icon: "bi-flag", submenu: SLASH_PRIORITIES.map(([value, label]) => ({ group: "Priority", label, icon: "bi-flag", run: (row) => this._writePriority(row, value) })) },
            { group: "Add to this task", label: "Person", hint: "@", meta: "Mention someone in the title", icon: "bi-at", run: (row, input) => this._slashType(input, "@", () => this._detectMention(input)) },
            { group: "Add to this task", label: "Company", hint: "@", icon: "bi-building", run: (row) => this._openCompanyPicker(row) },
            { group: "Add to this task", label: "Tag", hint: "#", icon: "bi-hash", run: (row, input) => this._slashType(input, "#", () => this._detectTagToken(input)) },
            { group: "Jane", label: "Ask Jane", icon: "bi-stars", run: (row) => this._openPane(row, "comments", { ask: "jane" }) }
        ])
    }

    _detectSlash(row, input) {
        const text = (input.textContent || "").replace(/\u00a0/g, " ")
        const match = text.match(/^\/([^\s/]*)$/)
        const sel = this._selectionOffsets(input)
        if (!match || !sel || sel.start !== text.length || sel.end !== text.length) return this._closeSlash()
        const query = match[1].toLowerCase()
        this._slash = { row, input, query, index: 0, items: [] }
        this._loadSlashBlocks()
        this._renderSlash()
    }

    // The blocks come from the server once per page: every template the
    // member can use (TaskListsController#blocks).
    async _loadSlashBlocks() {
        if (this._slashBlocks || this._slashBlocksLoading || !this.listValue) return
        this._slashBlocksLoading = true
        try {
            const res = await fetch(`/task_lists/${this.listValue}/blocks`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            const data = res.ok ? await res.json() : null
            this._slashBlocks = data?.blocks || []
        } catch (_error) {
            this._slashBlocks = []
        } finally {
            this._slashBlocksLoading = false
        }
        if (this._slash) this._renderSlash()
    }

    _renderSlash() {
        const slash = this._slash
        if (!slash) return
        const query = slash.query
        slash.items = slash.submenu || this._slashItems().filter((item) => !query || item.label.toLowerCase().includes(query) || item.group.toLowerCase().startsWith(query))
        if (slash.index >= slash.items.length) slash.index = 0
        if (!this._slashMenu) {
            const menu = document.createElement("div")
            menu.className = "tasks-mention-menu tasks-slash-menu"
            menu.setAttribute("role", "listbox")
            menu.setAttribute("aria-label", "Insert")
            document.body.appendChild(menu)
            this._slashMenu = menu
        }
        const menu = this._slashMenu
        menu.replaceChildren()
        if (slash.items.length === 0) {
            menu.hidden = true
            return
        }
        slash.items.forEach((item, index) => {
            if (index === 0 || slash.items[index - 1].group !== item.group) {
                const divider = document.createElement("div")
                divider.className = "tasks-mention-divider"
                divider.textContent = item.group
                menu.appendChild(divider)
            }
            const button = document.createElement("button")
            button.type = "button"
            button.className = "tasks-mention-item"
            button.setAttribute("role", "option")
            button.classList.toggle("is-active", index === slash.index)
            const icon = document.createElement("i")
            icon.className = `bi ${item.icon} tasks-mention-item-icon`
            button.appendChild(icon)
            const text = document.createElement("span")
            text.className = "tasks-mention-item-text"
            const name = document.createElement("span")
            name.className = "tasks-mention-item-name"
            name.textContent = item.label
            text.appendChild(name)
            if (item.meta) {
                const meta = document.createElement("span")
                meta.className = "tasks-mention-item-meta"
                meta.textContent = item.meta
                text.appendChild(meta)
            }
            button.appendChild(text)
            if (item.hint) {
                const hint = document.createElement("span")
                hint.className = "tasks-slash-hint"
                hint.textContent = item.hint
                button.appendChild(hint)
            }
            // mousedown (not click) so the pick lands before the input's blur.
            button.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickSlash(index) })
            button.addEventListener("mouseenter", () => this._setSlashIndex(index))
            menu.appendChild(button)
        })
        const rect = slash.input.getBoundingClientRect()
        menu.style.position = "fixed"
        menu.style.top = `${Math.round(rect.bottom + 4)}px`
        menu.style.left = `${Math.round(rect.left)}px`
        menu.hidden = false
    }

    _handleSlashKeydown(e) {
        if (e.key === "Escape") { e.preventDefault(); this._closeSlash(); return true }
        const items = this._slash.items || []
        if (items.length === 0) return false
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault()
            const step = e.key === "ArrowDown" ? 1 : -1
            this._setSlashIndex((this._slash.index + step + items.length) % items.length)
            return true
        }
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault()
            this._pickSlash(this._slash.index)
            return true
        }
        return false
    }

    _setSlashIndex(index) {
        if (!this._slash) return
        this._slash.index = index
        this._slashMenu?.querySelectorAll(".tasks-mention-item").forEach((button, i) => button.classList.toggle("is-active", i === index))
    }

    // Every pick first clears the "/query" it was opened with, so the row is
    // left holding only what the item puts there.
    _pickSlash(index) {
        const slash = this._slash
        const item = slash?.items?.[index]
        // An item with choices of its own (Priority) shows them in the same menu.
        if (item?.submenu) {
            slash.submenu = item.submenu
            slash.index = 0
            this._renderSlash()
            return
        }
        this._closeSlash()
        if (!item) return
        slash.input.innerHTML = ""
        this._scheduleSave(slash.row)
        item.run(slash.row, slash.input)
    }

    _slashType(input, character, then) {
        input.focus()
        input.textContent = character
        this._setCaretOffset(input, 1)
        then()
    }

    _slashBrowseBlocks() {
        window.dispatchEvent(new CustomEvent("tasks:browse-blocks"))
    }

    // The block takes this row's place; the page reloads to show it, since
    // the outline only draws rows it did not make itself on a fresh render.
    async _slashAddBlock(row, block) {
        if (row._saveTimer) { clearTimeout(row._saveTimer); row._saveTimer = null }
        const id = await this._resolveId(row.closest("[data-task-block]"))
        const body = new FormData()
        body.set("template_id", block.id)
        if (id) body.set("replace_task_id", id)
        try {
            const res = await fetch(`/task_lists/${this.listValue}/from_template`, {
                method: "POST",
                headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
                credentials: "same-origin",
                body
            })
            const data = await res.json()
            if (!res.ok || !data.ok) throw new Error(data.error || "Could not add the block")
            window.location.reload()
        } catch (error) {
            this._flashNotice(error.message || "Could not add the block", "warning")
        }
    }

    _closeSlash() {
        this._slash = null
        if (this._slashMenu) {
            this._slashMenu.remove()
            this._slashMenu = null
        }
    }

    // --- Inline "#" tag shortcut ------------------------------------------
    // Typing "#" in a task title opens the same tag typeahead the picker uses.
    // On accept the token is lifted OUT of the title and the tag is attached to
    // the task, so the field stays the single source of truth and the title
    // never carries tag syntax. A "#" dismissed with Esc stays literal text,
    // exactly like an unaccepted "@".
    //
    // Deliberately no change to inline_markdown.js or inline_markdown.rb: "#"
    // is not a title token. Teaching the parser one would make tags a derived
    // field that drifts from the array, and would leak raw syntax to viewers of
    // a shared list who don't own the tag.

    _handleTagTokenKeydown(e) {
        if (e.key === "Escape") { e.preventDefault(); this._closeTagToken(); return true }
        const items = (this._tagToken && this._tagToken.items) || []
        const typed = this._tagToken.query.trim()
        const rowCount = items.length + (typed !== "" ? 1 : 0)
        if (rowCount === 0) return false
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault()
            const delta = e.key === "ArrowDown" ? 1 : -1
            let next = this._tagToken.index + delta
            if (next < 0) next = rowCount - 1
            if (next >= rowCount) next = 0
            this._tagToken.index = next
            this._renderTagTokenItems()
            return true
        }
        if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault()
            this._pickTagToken(this._tagToken.index)
            return true
        }
        return false
    }

    _detectTagToken(input) {
        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return this._closeTagToken()
        const node = sel.anchorNode
        if (!node || node.nodeType !== Node.TEXT_NODE || !input.contains(node)) return this._closeTagToken()

        const offset = sel.anchorOffset
        const match = node.data.slice(0, offset).match(/(?:^|\s)#([^\s#]*)$/)
        if (!match) return this._closeTagToken()

        const query = match[1]
        this._tagToken = {
            input,
            node,
            hashOffset: offset - query.length - 1,
            caretOffset: offset,
            query,
            items: (this._tagToken && this._tagToken.query === query) ? this._tagToken.items : [],
            index: (this._tagToken && this._tagToken.query === query) ? this._tagToken.index : 0
        }
        this._ensureTagTokenMenu()
        this._positionTagTokenMenu()
        this._renderTagTokenItems()
        if (this._tagTokenDebounce) clearTimeout(this._tagTokenDebounce)
        this._tagTokenDebounce = setTimeout(() => this._fetchTagTokenItems(query), 180)
    }

    async _fetchTagTokenItems(query) {
        try {
            const res = await fetch(this._withList(`/tasks/tags?q=${encodeURIComponent(query)}`), {
                headers: { Accept: "application/json" },
                credentials: "same-origin"
            })
            if (!res.ok) throw new Error("tags failed")
            const payload = await res.json()
            if (!this._tagToken || this._tagToken.query !== query) return
            this._tagToken.items = payload.tags || []
            this._tagToken.index = 0
            this._renderTagTokenItems()
        } catch (_error) {
            this._closeTagToken()
        }
    }

    _ensureTagTokenMenu() {
        if (this._tagTokenMenu) return
        const menu = document.createElement("div")
        menu.className = "tasks-mention-menu"
        menu.setAttribute("role", "listbox")
        document.body.appendChild(menu)
        this._tagTokenMenu = menu
    }

    _renderTagTokenItems() {
        const menu = this._tagTokenMenu
        const state = this._tagToken
        if (!menu || !state) return
        const typed = state.query.trim()
        const rows = (state.items || []).slice()
        const canCreate = typed !== ""
        menu.replaceChildren()
        if (rows.length === 0 && !canCreate) {
            menu.hidden = true
            return
        }
        rows.forEach((tag, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "tasks-mention-item"
            row.classList.toggle("is-active", index === state.index)
            const chip = document.createElement("span")
            chip.className = `tasks-outline-tag tasks-outline-tag--${tag.color_key}`
            chip.textContent = tag.name
            row.appendChild(chip)
            const count = document.createElement("span")
            count.className = "tasks-mention-item-meta"
            count.textContent = String(tag.count || 0)
            row.appendChild(count)
            row.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickTagToken(index) })
            menu.appendChild(row)
        })
        if (canCreate) {
            const create = document.createElement("button")
            create.type = "button"
            create.className = "tasks-mention-item"
            create.classList.toggle("is-active", state.index === rows.length)
            create.textContent = `Create \u201C${typed}\u201D`
            create.addEventListener("mousedown", (ev) => { ev.preventDefault(); this._pickTagToken(rows.length) })
            menu.appendChild(create)
        }
        menu.hidden = false
    }

    _positionTagTokenMenu() {
        const menu = this._tagTokenMenu
        if (!menu) return
        const sel = window.getSelection()
        let rect = null
        if (sel && sel.rangeCount > 0) rect = sel.getRangeAt(0).getBoundingClientRect()
        if (!rect || (rect.top === 0 && rect.left === 0)) rect = this._tagToken.input.getBoundingClientRect()
        menu.style.position = "fixed"
        menu.style.top = `${Math.round(rect.bottom + 4)}px`
        menu.style.left = `${Math.round(rect.left)}px`
    }

    _pickTagToken(index) {
        const state = this._tagToken
        if (!state) return
        const rows = state.items || []
        const typed = state.query.trim()
        const picked = rows[index]
        const value = picked ? picked.slug : typed
        if (!value) { this._closeTagToken(); return }

        // Lift the "#query" run out of the title before attaching the tag, so
        // the row commits as plain prose and the tag lives only in the array.
        const range = document.createRange()
        try {
            range.setStart(state.node, state.hashOffset)
            range.setEnd(state.node, Math.min(state.caretOffset, state.node.data.length))
        } catch (_error) {
            this._closeTagToken()
            return
        }
        range.deleteContents()
        const sel = window.getSelection()
        if (sel) { sel.removeAllRanges(); sel.addRange(range) }

        const row = state.input.closest("[data-task-row]")
        this._closeTagToken()
        if (!row) return
        // Reuse the picker's toggle so there is exactly one code path on the
        // client that writes tasks.tags.
        const previous = this._tagPicker
        this._tagPicker = { row, query: "", items: [], index: -1, busy: false }
        this._applyTag(value).then(() => { this._tagPicker = previous || null })
    }

    _closeTagToken() {
        if (this._tagTokenDebounce) { clearTimeout(this._tagTokenDebounce); this._tagTokenDebounce = null }
        this._tagToken = null
        if (this._tagTokenMenu) {
            this._tagTokenMenu.remove()
            this._tagTokenMenu = null
        }
    }

    // --- Tag picker (#) --------------------------------------------------
    // Applying tags is a change to tasks.tags, so it rides the same PATCH
    // contract as every other row edit rather than getting an endpoint of its
    // own. The dictionary behind it (names, colours) is separate — see
    // TaskTagsController for rename/recolour/delete-everywhere.
    //
    // Built here rather than as its own Stimulus controller because it needs
    // the row context, _patch and _withList this controller already owns;
    // a separate controller would have to re-derive all three.

    // Palette keys, mirroring TaskTag::PALETTE. Order matters — the recolour
    // grid renders in this order and the server validates against the same set.
    static TAG_PALETTE = ["slate", "blue", "indigo", "violet", "teal", "green", "amber", "rose"]

    // Mirrors TasksHelper::TASK_TAG_CHIP_CAP / TASK_TAG_CHIP_CAPS, so a
    // client-rendered cluster matches a server-rendered one exactly.
    static TAG_CHIP_CAP = 3
    static TAG_CHIP_CAPS = [3, 2, 1]

    // A chip click filters (it's a real link). All this does is stop the click
    // reaching startEdit, which owns the rest of the title wrap. On a coarse
    // pointer the cluster opens the picker instead: an 18px filter link under a
    // thumb is a misfire, so filtering stays a pointer affordance.
    // A click anywhere in the title wrap. Its only job is the inline mention
    // chips - an @-mentioned contact, company or teammate - which open the
    // same docked panel the trailing company chip does. A teammate chip is a
    // real link to their profile (it is one wherever a title renders), so a
    // modified click still opens that in a new tab.
    //
    // stopImmediatePropagation, not stopPropagation: startEdit is registered on
    // THIS SAME element, and only the immediate form stops a sibling listener on
    // the same node. Registration order comes from the data-action string, so
    // this handler is listed first there.
    //
    // Anything that is not a mention chip falls straight through, and a click
    // inside the open editor is left alone entirely - interrupting a title edit
    // to show a panel would be its own bug.
    onTitleWrapClick(e) {
        if (e.target.closest("[data-task-input]")) return

        const chip = mentionChipFor(e)
        if (!chip) return

        e.preventDefault()
        e.stopImmediatePropagation()
        // The same card /network opens for that row. An inline company
        // carries no task_id, so its card offers no "Unlink from task": it is
        // unlinked by deleting it from the title, and a second way would leave
        // the text naming a company the row no longer links to.
        this._openDetail(chip)
    }

    // The trailing company chip: the same docked panel, rather than navigating,
    // so reading an account doesn't cost you your place in a 400-row pipeline
    // list. stopPropagation keeps the click out of startEdit, which owns the
    // rest of the title wrap. Modified clicks (cmd/ctrl/shift/middle) fall
    // through to the <a> so "open in a new tab" still works.
    onCompanyChipClick(e) {
        e.stopPropagation()
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return

        const chip = e.target.closest("[data-detail-url]")
        if (!chip) return

        e.preventDefault()
        this._openDetail(chip)
    }

    // Hand a chip to the detail-modal controller on this same element - the one
    // /network's rows open through.
    _openDetail(trigger) {
        this.dispatch("open-detail", { detail: { trigger } })
    }

    // --- Keyboard shortcut sheet ("?" / list menu → Keyboard shortcuts) ---

    openShortcuts() {
        if (this._shortcutSheet?.isConnected) {
            this._shortcutSheet.querySelector(".tasks-shortcuts-close")?.focus()
            return
        }
        const mac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
        const mod = mac ? "⌘" : "Ctrl"
        const alt = mac ? "⌥" : "Alt"
        const shift = mac ? "⇧" : "Shift"
        const groups = [
            ["Writing", [
                ["Enter", "New task below"],
                ["Tab / " + shift + " Tab", "Indent / outdent"],
                ["↑ ↓", "Move between tasks"],
                [`${mod} ${shift} ↑ / ↓`, "Move the task up / down"],
                [`${mod} ↑ / ↓`, "Collapse / expand subtasks"],
                [`${mod} Enter`, "Complete / reopen"],
                ["Backspace", "On an empty task: delete it"],
                [`${mod} Z · ${mod} ${shift} Z`, "Undo · redo (moves, completes, formatting, dates)"],
                ["Esc", "Stop editing"]
            ]],
            ["Selecting", [
                [`${shift} click · ${mod} click`, "Select a range · add one task"],
                [`${shift} ↑ / ↓`, "From the edge of a title: select tasks"],
                [`Backspace · ${mod} Enter`, "Delete · complete the selected tasks"],
                ["Esc", "Clear the selection"]
            ]],
            ["Formatting", [
                [`${mod} B · ${mod} I · ${mod} E`, "Bold · italic · code"],
                [`${mod} ${shift} X`, "Strikethrough"],
                [`${mod} K`, "Link (or paste a URL over selected text)"],
                [`${mod} ${shift} H`, "Re-apply the last colour"],
                [`${alt} ${mod} 1 / 2 / 3 · 0`, "Heading 1 / 2 / 3 · text"],
                ["# · ## · ###", "Start a task with these to make a heading"]
            ]],
            ["People & more", [
                ["/", "On an empty task: add a block, heading, date, person…"],
                ["@", "Mention someone"],
                ["#", "Tag the task"],
                ["tomorrow · fri · every monday", "Type a date in a title to set it (× keeps the words)"],
                [`${mod} ${shift} M`, "Comment on the selected text"],
                ["?", "This sheet"]
            ]]
        ]
        const sheet = document.createElement("div")
        sheet.className = "tasks-shortcuts"
        sheet.setAttribute("role", "dialog")
        sheet.setAttribute("aria-modal", "true")
        sheet.setAttribute("aria-label", "Keyboard shortcuts")
        const card = document.createElement("div")
        card.className = "tasks-shortcuts-card"
        const head = document.createElement("div")
        head.className = "tasks-shortcuts-head"
        head.innerHTML = '<h2>Keyboard shortcuts</h2><button type="button" class="tasks-shortcuts-close" aria-label="Close"><i class="bi bi-x-lg" aria-hidden="true"></i></button>'
        card.appendChild(head)
        groups.forEach(([title, rows]) => {
            const section = document.createElement("section")
            const label = document.createElement("div")
            label.className = "tasks-shortcuts-eyebrow"
            label.textContent = title
            section.appendChild(label)
            rows.forEach(([keys, what]) => {
                const row = document.createElement("div")
                row.className = "tasks-shortcuts-row"
                const desc = document.createElement("span")
                desc.textContent = what
                const kbd = document.createElement("kbd")
                kbd.textContent = keys
                row.append(desc, kbd)
                section.appendChild(row)
            })
            card.appendChild(section)
        })
        sheet.appendChild(card)
        const close = () => {
            sheet.remove()
            this._shortcutSheet = null
            this._closeShortcutSheet = null
            document.removeEventListener("keydown", onKey, true)
        }
        // disconnect() closes it too, or its Escape listener outlives the page.
        this._closeShortcutSheet = close
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault()
                e.stopPropagation()
                close()
            }
        }
        sheet.addEventListener("click", (e) => {
            if (e.target === sheet || e.target.closest(".tasks-shortcuts-close")) close()
        })
        document.addEventListener("keydown", onKey, true)
        document.body.appendChild(sheet)
        this._shortcutSheet = sheet
        head.querySelector(".tasks-shortcuts-close").focus()
    }

    // The rail's due date and repeat chips open the Date popover, as a date on a
    // task does everywhere else (Todoist, Things, Asana). Mirrors the
    // server-rendered chips in tasks/_outline_block.html.slim.
    _makeRailChipEditable(chip) {
        chip.dataset.action = "click->tasks-outline#openDate"
        chip.title = chip.title || "Change due date"
    }

    // The hover icons' calendar button carries the date (see _outline_block).
    _renderDueAction(row, due) {
        const btn = row.querySelector("[data-task-due-action]")
        if (!btn) return
        btn.className = `tasks-outline-menu tasks-outline-due-action tasks-outline-task-only${due ? ` has-date is-${due.state}` : ""}`
        btn.innerHTML = '<i class="bi bi-calendar-event" aria-hidden="true"></i>'
        if (due) {
            const label = document.createElement("span")
            label.textContent = due.text
            btn.appendChild(label)
        }
        btn.title = due ? `Due ${due.text}` : "Due date"
        btn.setAttribute("aria-label", due ? `Due ${due.text} - change` : "Set a due date")
    }

    // --- The task pane (TaskPanelsController, task_panels/_panel) ---
    //
    // One sidebar for a task's details, note, subtasks, files and comments.
    // Every way in - the hover icon and "Open task", the note, comment and file
    // chips - opens it at its own section. The pane hands back what the outline owns as window events
    // (task_pane_controller.js), handled in _onPane* below.

    openPane(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeRowMenu(e)
        this._openPane(row, "details")
    }

    _openPane(row, section, extra = {}) {
        if (row.dataset.placeholderPending && !(row.querySelector("[data-task-input]")?.textContent || "").trim()) return
        this._resolveId(row.closest("[data-task-block]")).then((id) => {
            if (id) this._openPaneFor(id, section, extra)
        })
    }

    _openPaneFor(id, section, extra = {}) {
        this._openDetail(taskPaneTrigger(id, section, extra))
    }

    // The pane follows the row being worked on, as Asana's does: with a task's
    // pane open, clicking or arrowing to another row shows that row's pane
    // (its title still edits in place). A beat later, so arrowing down a list
    // fetches the row you stop on, not every row passed; a row not saved yet
    // (tmp- id) has no pane.
    _paneFollow(id) {
        clearTimeout(this._paneFollowTimer)
        if (!id || String(id).startsWith("tmp-")) return
        const showing = () => document.querySelector(".detail-sidebar .task-pane")?.dataset.taskPaneTaskIdValue
        if (!showing() || showing() === String(id)) return
        this._paneFollowTimer = setTimeout(() => {
            const current = showing()
            if (current && current !== String(id) && !this._paneBusy()) this._openPaneFor(id, "details")
        }, 200)
    }

    // The open pane holds something that switching would throw away: a
    // comment being written (beyond a bare "@Jane ") or a file uploading. It
    // stays on its task until that is sent; a note saves on blur already.
    _paneBusy() {
        const pane = document.querySelector(".detail-sidebar .task-pane")
        if (!pane) return false
        const draft = (pane.querySelector(".task-comments-input")?.value || "").trim()
        if (draft && !/^@\S+$/.test(draft)) return true
        const progress = pane.querySelector("[data-task-attachments-target='progress']")
        return Boolean(progress && !progress.hidden)
    }

    _paneRow(taskId) {
        return this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(String(taskId))}"]`)
    }

    // The pane's note editor saved: same write and row update as any note edit.
    _onPaneNotes(event) {
        const { taskId, notes } = event.detail || {}
        const row = this._paneRow(taskId)
        if (!row) return
        event.detail.handled = true
        row.dataset.notes = notes
        this._renderNotes(row)
        const fd = new FormData()
        fd.set("notes", notes)
        this._patch(`/tasks/${taskId}.json`, fd)
    }

    // A checkbox in the pane: the row's own toggle, so recurrence, cascades,
    // the celebration and live sync all behave as a click on the row would.
    _onPaneToggle(event) {
        const { taskId, completed } = event.detail || {}
        const row = this._paneRow(taskId)
        if (!row || (row.dataset.completed === "true") === completed) return
        event.detail.handled = true
        this._toggleCompleteForRow(row)
    }

    // A details button in the pane opens the same picker the row would.
    _onPaneAction(event) {
        const { taskId, action } = event.detail || {}
        const row = this._paneRow(taskId)
        if (!row) return
        event.detail.handled = true
        const target = row.querySelector(".tasks-outline-title-wrap") || row
        const synthetic = { target, preventDefault() {}, stopPropagation() {} }
        if (action === "date") this._openDatePopover(row, event.detail.anchor || row)
        else if (action === "assign") this._openAssignPopover(row, event.detail.anchor || row)
        else if (action === "tags") this.openTagPicker(synthetic)
        else if (action === "company") this.openCompanyPicker(synthetic)
        else if (action === "duplicate") this.duplicateTask(synthetic)
        else if (action === "send") this.openSendPicker(synthetic)
        else if (action === "delete") this._deleteFromPane(row)
    }

    // The pane's Delete: the row's own delete (with its undo), then the pane
    // closes - unless the task still has subtasks, which the row refuses too.
    _deleteFromPane(row) {
        if (this._hasChildren(row)) {
            this._flashNotice("Delete subtasks first", "warning")
            return
        }
        this._deleteTask(row)
        window.dispatchEvent(new CustomEvent("jane:close-panel"))
    }

    // Priority or a custom field set in the pane: the row's own writers, so
    // its marker, badges and undo behave as a pick from the menu would.
    _onPaneUpdate(event) {
        const { taskId, priority, fieldId, fieldValue } = event.detail || {}
        const row = this._paneRow(taskId)
        if (!row) return
        event.detail.handled = true
        if (priority != null) this._writePriority(row, priority)
        else if (fieldId != null) this._writeFieldValue(row, fieldId, fieldValue)
    }

    // The pane asks on connect whether its task is a row here: if not (a
    // filtered-out parent), its detail buttons have no picker to open.
    _onPaneProbe(event) {
        if (this._paneRow(event.detail?.taskId)) event.detail.handled = true
    }

    _onPaneOpen(event) {
        const { taskId, section } = event.detail || {}
        if (!taskId) return
        event.detail.handled = true
        this._openPaneFor(taskId, section || "details")
    }

    // --- Comments (TaskCommentsController, task_comments/_panel) ---

    // Row chip and row-menu "Comments": open the thread in the detail sidebar.
    openComments(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        const toggle = row.querySelector(".tasks-outline-dropdown [data-bs-toggle='dropdown']")
        if (toggle) bootstrap.Dropdown.getInstance(toggle)?.hide()
        this._openComments(row)
    }

    // quote: the title text selected when the toolbar's Comment button (or
    // ⇧⌘M) was used - the panel opens with it attached and the box focused.
    // A fresh trigger and a never-repeated URL each time, because detail-modal
    // caches panels per URL and a thread is live data.
    _openComments(row, quote = "") {
        this._openPane(row, "comments", quote ? { quote: quote.slice(0, 500) } : {})
    }

    _commentFromSelection() {
        const row = this._editingRow()
        const editor = this._activeEditor()
        if (!row || !editor) return
        const sel = window.getSelection()
        const quote = sel && sel.rangeCount && editor.contains(sel.anchorNode) ? sel.toString().replace(/\s+/g, " ").trim() : ""
        this._hideToolbar()
        this._openComments(row, quote)
    }

    // The 💬 n chip in the title wrap; removed at zero. Kept in step with the
    // server-rendered one in _outline_block.html.slim.
    _renderCommentChip(row, count) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        let chip = wrap.querySelector("[data-task-comments-chip]")
        const n = Number(count) || 0
        if (n <= 0) {
            chip?.remove()
            return
        }
        if (!chip) {
            chip = document.createElement("button")
            chip.type = "button"
            chip.className = "tasks-outline-comments"
            chip.dataset.taskCommentsChip = "true"
            chip.dataset.action = "click->tasks-outline#openComments"
            const next = wrap.querySelector("[data-task-attachments-chip]")
            if (next) next.before(chip)
            else wrap.appendChild(chip)
        }
        const label = `${n} ${n === 1 ? "comment" : "comments"}`
        chip.title = label
        chip.setAttribute("aria-label", label)
        chip.innerHTML = `<i class="bi bi-chat-left-text" aria-hidden="true"></i><span>${n}</span>`
    }

    // Another member (or another tab) commented: redraw the chip and let an
    // open thread for that task re-read itself. This tab's own writes carry
    // its client id and are skipped - the panel already updated in place.
    _applyRemoteCommentChanged(data) {
        if (data.client_id && data.client_id === this._clientId) return
        const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(String(data.task_id))}"]`)
        if (row) this._renderCommentChip(row, data.count)
        window.dispatchEvent(new CustomEvent("jane:task-comment-changed", { detail: { taskId: data.task_id } }))
    }

    // --- Files (TaskAttachmentsController, task_attachments/_panel) ---
    //
    // Three ways in, as in Taskade: a file dropped on the row
    // (onDragOver/onDrop), an image pasted while editing the title
    // (_pasteFilesAsAttachments), and the task pane, where the whole pane is
    // the drop target. All upload through utils/task_attachments.js.

    // Row chip: open the task pane at its files.
    openAttachments(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (row) this._openPane(row, "files")
    }

    // Clipboard images (a screenshot) become attachments instead of text. Only
    // when the clipboard holds a FILE: copied text that happens to come with an
    // image flavour (a web page selection) still pastes as text.
    _pasteFilesAsAttachments(e, row) {
        if (!row) return false
        const files = Array.from(e.clipboardData?.files || [])
        if (files.length === 0 || e.clipboardData.getData("text/plain")) return false
        e.preventDefault()
        this._attachTo(row, files)
        return true
    }

    _attachTo(row, files) {
        if (!files.length) return
        this._resolveId(row.closest("[data-task-block]")).then((id) => {
            if (!id) return
            row.classList.add("is-uploading")
            uploadTaskAttachments(id, files).then((data) => {
                row.classList.remove("is-uploading")
                if (data?.added) {
                    const label = `${data.added} ${data.added === 1 ? "file" : "files"} attached`
                    showSystemToast(label, { level: data.error ? "warning" : "success", duration: data.error ? 5000 : 2500, subtitle: data.error || undefined })
                } else {
                    this._flashNotice(data?.error || "Could not attach the file", "danger")
                }
            })
        })
    }

    // The 📎 n chip in the title wrap, after the comment chip; removed at zero.
    // Kept in step with the server-rendered one in _outline_block.html.slim.
    _renderAttachmentChip(row, count) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        let chip = wrap.querySelector("[data-task-attachments-chip]")
        const n = Number(count) || 0
        if (n <= 0) {
            chip?.remove()
            return
        }
        if (!chip) {
            chip = document.createElement("button")
            chip.type = "button"
            chip.className = "tasks-outline-comments tasks-outline-attachments"
            chip.dataset.taskAttachmentsChip = "true"
            chip.dataset.action = "click->tasks-outline#openAttachments"
            const before = wrap.querySelector("[data-task-comments-chip]") || wrap.querySelector("[data-task-note-chip]")
            if (before) before.after(chip)
            else wrap.appendChild(chip)
        }
        const label = `${n} ${n === 1 ? "file" : "files"}`
        chip.title = label
        chip.setAttribute("aria-label", label)
        chip.innerHTML = `<i class="bi bi-paperclip" aria-hidden="true"></i><span>${n}</span>`
    }

    // Another member (or tab) attached or removed a file: redraw the chip and
    // let an open Files panel for that task re-read itself.
    _applyRemoteAttachmentChanged(data) {
        if (data.client_id && data.client_id === this._clientId) return
        const row = this.element.querySelector(`[data-task-row][data-task-id="${CSS.escape(String(data.task_id))}"]`)
        if (row) this._renderAttachmentChip(row, data.count)
        window.dispatchEvent(new CustomEvent("jane:task-attachment-changed", { detail: { taskId: data.task_id } }))
    }

    onTagClick(e) {
        e.stopPropagation()
        if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) return
        e.preventDefault()
        this.openTagPicker(e)
    }

    openTagPicker(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeTagPicker()
        this._tagPicker = { row, query: "", items: [], index: -1, busy: false }
        this._ensureTagPanel()
        this._positionTagPanel(row)
        this._renderTagPanel()
        this._fetchTagItems("")
        const field = this._tagPanel.querySelector("[data-tag-query]")
        if (field) field.focus()
    }

    // --- Company picker ----------------------------------------------------
    // The task pane's "Link a company", for the same job the "@" typeahead does
    // inline. Both exist for the reason tags have both a picker and a "#"
    // shortcut: the shortcut is faster once you know it, and the pane is how
    // you find out it is possible at all.
    //
    // Deliberately the tag picker's shape (fixed panel, query field, fetch on
    // input, outside-mousedown closes) rather than the mention menu's: this one
    // has no caret to anchor to and no text run to replace, so it needs its own
    // input to type into.

    openCompanyPicker(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeRowMenu(e)
        this._openCompanyPicker(row)
    }

    _openCompanyPicker(row) {
        this._closeCompanyPicker()
        this._companyPicker = { row, query: "", items: [], busy: false }
        this._ensureCompanyPanel()
        this._positionCompanyPanel(row)
        this._renderCompanyPanel()
        this._fetchCompanyItems("")
        const field = this._companyPanel.querySelector("[data-company-query]")
        if (field) field.focus()
    }

    _ensureCompanyPanel() {
        if (this._companyPanel) return
        const panel = document.createElement("div")
        panel.className = "network-toolbar-menu-panel tasks-tag-panel tasks-company-panel"
        panel.setAttribute("role", "listbox")
        // mousedown rather than click so a pick lands before the query field's
        // blur can tear the panel down under it.
        panel.addEventListener("mousedown", (ev) => ev.preventDefault())
        document.body.appendChild(panel)
        this._companyPanel = panel
        this._companyOutsideHandler = (ev) => {
            if (this._companyPanel && !this._companyPanel.contains(ev.target)) this._closeCompanyPicker()
        }
        document.addEventListener("mousedown", this._companyOutsideHandler, true)
    }

    _positionCompanyPanel(row) {
        const rect = row.getBoundingClientRect()
        const panel = this._companyPanel
        panel.style.position = "fixed"
        panel.style.top = `${Math.round(Math.min(rect.bottom + 4, window.innerHeight - 320))}px`
        panel.style.left = `${Math.round(Math.min(rect.left + 24, window.innerWidth - 280))}px`
    }

    async _fetchCompanyItems(query) {
        try {
            const res = await fetch(this._withList(`/tasks/mention_candidates?q=${encodeURIComponent(query)}`), {
                headers: { Accept: "application/json" },
                credentials: "same-origin"
            })
            if (!res.ok) throw new Error("candidates failed")
            const payload = await res.json()
            if (!this._companyPicker || this._companyPicker.query !== query) return
            this._companyPicker.items = payload.companies || []
            this._renderCompanyPanel()
        } catch (_error) {
            if (this._companyPicker) { this._companyPicker.items = []; this._renderCompanyPanel() }
        }
    }

    _renderCompanyPanel() {
        const panel = this._companyPanel
        const state = this._companyPicker
        if (!panel || !state) return

        panel.replaceChildren()
        const field = document.createElement("input")
        field.type = "text"
        field.className = "tasks-tag-query"
        field.placeholder = "Company or domain…"
        field.value = state.query
        field.setAttribute("data-company-query", "true")
        field.addEventListener("input", () => {
            state.query = field.value
            if (this._companyDebounce) clearTimeout(this._companyDebounce)
            this._companyDebounce = setTimeout(() => this._fetchCompanyItems(state.query), 180)
        })
        field.addEventListener("keydown", (ev) => {
            if (ev.key === "Escape") { ev.preventDefault(); this._closeCompanyPicker() }
            if (ev.key === "Enter") {
                ev.preventDefault()
                const first = (state.items || [])[0]
                if (first) this._linkCompany(state.row, { company_id: String(first.token).split(":")[1] })
                else if (state.query.trim().length >= 2) this._linkCompany(state.row, { name: state.query.trim() })
            }
        })
        panel.appendChild(field)

        const items = state.items || []
        items.forEach((company) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = "network-toolbar-menu-item"
            btn.innerHTML = `<i class="bi bi-building"></i><span>${this._escape(company.name)}</span>`
            if (company.meta) {
                const meta = document.createElement("span")
                meta.className = "tasks-mention-item-meta"
                meta.textContent = company.meta
                btn.appendChild(meta)
            }
            btn.addEventListener("click", () => this._linkCompany(state.row, { company_id: String(company.token).split(":")[1] }))
            panel.appendChild(btn)
        })

        // Same "assert it" affordance the inline typeahead offers, for the same
        // reason: a target nobody has emailed has to be able to enter Jane here.
        if (state.query.trim().length >= 2 && items.length === 0) {
            const add = document.createElement("button")
            add.type = "button"
            add.className = "network-toolbar-menu-item"
            add.innerHTML = `<i class="bi bi-plus-circle"></i><span>Add &quot;${this._escape(state.query.trim())}&quot;</span>`
            add.addEventListener("click", () => this._linkCompany(state.row, { name: state.query.trim() }))
            panel.appendChild(add)
        }
        if (items.length === 0 && state.query.trim().length < 2) {
            const hint = document.createElement("div")
            hint.className = "tasks-tag-empty"
            hint.textContent = "Type to search companies"
            panel.appendChild(hint)
        }
    }

    async _linkCompany(row, body) {
        if (!row || !this._companyPicker || this._companyPicker.busy) return

        this._companyPicker.busy = true
        try {
            const res = await fetch(`/tasks/${row.dataset.taskId}/company_link`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
                credentials: "same-origin",
                body: JSON.stringify(body)
            })
            if (!res.ok) throw new Error(`link failed: ${res.status}`)
            const payload = await res.json()
            if (payload.ok && payload.chip_html) this._insertCompanyChip(row, payload.chip_html)
            this._closeCompanyPicker()
        } catch (error) {
            console.warn("company link failed", error)
            if (this._companyPicker) this._companyPicker.busy = false
        }
    }

    _closeCompanyPicker() {
        if (this._companyDebounce) { clearTimeout(this._companyDebounce); this._companyDebounce = null }
        this._companyPicker = null
        if (this._companyPanel) {
            this._companyPanel.remove()
            this._companyPanel = null
        }
        if (this._companyOutsideHandler) {
            document.removeEventListener("mousedown", this._companyOutsideHandler, true)
            this._companyOutsideHandler = null
        }
    }

    // --- Send to list (feedback #208) ---
    // File this row as a ticket in a section of another list: a 1:1 item into
    // the Engineering board's "In queue" column. Two steps in one panel - pick
    // the list, then the section (its top-level rows, i.e. the board's
    // columns) - with the company picker's shape: fixed panel, a query field
    // that filters, outside-mousedown closes. The server copies the row and its
    // subtasks (TaskSendsController / Tasks::SendToList) and hands back the
    // chip for this row.

    openSendPicker(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._closeRowMenu(e)
        const block = row.closest("[data-task-block]")

        this._closeSendPicker()
        this._sendPicker = { row, block, step: "lists", query: "", lists: null, list: null, columns: null, busy: false, error: null }
        this._ensureSendPanel()
        this._positionSendPanel(row)
        this._renderSendPanel()
        const exclude = this.hasListValue && this.listValue ? `?exclude_list_id=${encodeURIComponent(this.listValue)}` : ""
        this._fetchSendJson(`/tasks/send_targets${exclude}`).then((payload) => {
            if (!this._sendPicker) return
            this._sendPicker.lists = payload?.lists || []
            // The sender's ClickUp lists, when ClickUp is on: { connected,
            // synced, lists } - filed into ClickUp rather than copied into Jane.
            this._sendPicker.clickup = payload?.clickup || null
            this._renderSendPanel()
        })
    }

    // A picker opened from the ••• menu: stopPropagation (which keeps the click
    // out of the row's other handlers) also keeps it from Bootstrap's document
    // listener, so the menu stayed open on top of the picker. Close it here.
    _closeRowMenu(e) {
        const toggle = e.target.closest(".dropdown")?.querySelector("[data-bs-toggle='dropdown']")
        if (toggle) bootstrap.Dropdown.getInstance(toggle)?.hide()
    }

    onSendChipClick(e) {
        // A real link to the other task: let it navigate, just keep the click
        // out of startEdit, which owns the rest of the title wrap.
        e.stopPropagation()
    }

    _ensureSendPanel() {
        if (this._sendPanel) return
        const panel = document.createElement("div")
        panel.className = "network-toolbar-menu-panel tasks-tag-panel tasks-send-panel"
        panel.setAttribute("role", "listbox")
        panel.dataset.sendPanel = "true"
        // mousedown rather than click so a pick lands before the query field's
        // blur can tear the panel down under it.
        panel.addEventListener("mousedown", (ev) => { if (ev.target.tagName !== "INPUT") ev.preventDefault() })
        document.body.appendChild(panel)
        this._sendPanel = panel
        this._sendOutsideHandler = (ev) => {
            if (this._sendPanel && !this._sendPanel.contains(ev.target)) this._closeSendPicker()
        }
        document.addEventListener("mousedown", this._sendOutsideHandler, true)
    }

    _positionSendPanel(row) {
        const rect = row.getBoundingClientRect()
        const panel = this._sendPanel
        panel.style.position = "fixed"
        panel.style.top = `${Math.round(Math.max(8, Math.min(rect.bottom + 4, window.innerHeight - 360)))}px`
        panel.style.left = `${Math.round(Math.max(8, Math.min(rect.left + 24, window.innerWidth - 300)))}px`
    }

    async _fetchSendJson(url) {
        try {
            const res = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!res.ok) throw new Error(`send targets failed: ${res.status}`)
            return await res.json()
        } catch (_error) {
            return null
        }
    }

    _renderSendPanel() {
        const panel = this._sendPanel
        const state = this._sendPicker
        if (!panel || !state) return

        panel.replaceChildren()
        const header = document.createElement("div")
        header.className = "tasks-send-header"
        if (state.step === "columns") {
            const back = document.createElement("button")
            back.type = "button"
            back.className = "tasks-send-back"
            back.setAttribute("aria-label", "Back to lists")
            back.innerHTML = '<i class="bi bi-chevron-left"></i>'
            back.addEventListener("click", () => this._sendBackToLists())
            header.appendChild(back)
        }
        const heading = document.createElement("span")
        heading.className = "tasks-send-heading"
        heading.textContent = state.step === "columns" ? `Send to ${state.list.external ? "ClickUp › " : ""}${state.list.name}` : "Send to list"
        header.appendChild(heading)
        panel.appendChild(header)

        const field = document.createElement("input")
        field.type = "text"
        field.className = "tasks-tag-query"
        field.placeholder = state.step === "columns" ? (state.list.external ? "Filter statuses…" : "Filter sections…") : "Filter lists…"
        field.value = state.query
        field.setAttribute("data-send-query", "true")
        field.addEventListener("input", () => {
            state.query = field.value
            this._renderSendItems()
        })
        field.addEventListener("keydown", (ev) => {
            if (ev.key === "Escape") { ev.preventDefault(); this._closeSendPicker() }
            if (ev.key === "Backspace" && field.value === "" && state.step === "columns") { ev.preventDefault(); this._sendBackToLists() }
            if (ev.key === "Enter") {
                ev.preventDefault()
                const first = panel.querySelector("[data-send-option]")
                if (first) first.click()
            }
        })
        panel.appendChild(field)

        const items = document.createElement("div")
        items.className = "tasks-send-items"
        items.dataset.sendItems = "true"
        panel.appendChild(items)
        this._renderSendItems()
        field.focus()
    }

    _renderSendItems() {
        const state = this._sendPicker
        const items = this._sendPanel?.querySelector("[data-send-items]")
        if (!state || !items) return

        items.replaceChildren()
        const query = state.query.trim().toLowerCase()
        const hint = (text) => {
            const div = document.createElement("div")
            div.className = "tasks-tag-empty"
            div.textContent = text
            items.appendChild(div)
        }
        const option = (icon, label, meta, onPick) => {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.className = "network-toolbar-menu-item"
            btn.dataset.sendOption = "true"
            btn.innerHTML = `<i class="bi ${icon}"></i><span>${this._escape(label)}</span>`
            if (meta) {
                const span = document.createElement("span")
                span.className = "tasks-mention-item-meta"
                span.textContent = meta
                btn.appendChild(span)
            }
            btn.disabled = state.busy
            btn.addEventListener("click", onPick)
            items.appendChild(btn)
        }

        if (state.error) hint(state.error)

        if (state.step === "lists") {
            if (state.lists === null) return hint("Loading lists…")
            const lists = state.lists.filter((l) => !query || l.name.toLowerCase().includes(query))
            lists.forEach((list) => option(list.icon || "bi-check2-square", list.name, list.owner ? `${list.owner}'s` : null, () => this._pickSendList(list)))
            const shownClickup = this._renderSendClickupLists(items, query, option, hint)
            if (lists.length === 0 && !shownClickup) hint(state.lists.length === 0 ? "No other lists you can add to" : "No list matches")
            return
        }

        if (state.columns === null) return hint(state.list.external ? "Loading statuses…" : "Loading sections…")
        // The top of the list is always on offer: a flat list has no sections,
        // and a ticket that belongs in no column yet is still worth filing. In
        // ClickUp the equivalent is the list's own default status.
        if (!query) option("bi-list-ul", state.list.external ? "Default status" : "Top of the list", null, () => this._sendTo(null))
        const columns = state.columns.filter((c) => !query || c.title.toLowerCase().includes(query))
        columns.forEach((column) => option(state.list.external ? "bi-circle-half" : "bi-kanban", column.title, null, () => this._sendTo(column)))
        if (query && columns.length === 0) hint(state.list.external ? "No status matches" : "No section matches")
    }

    // The ClickUp group under the Jane lists. Returns whether it drew any
    // pickable row, so the caller's "no list matches" stays honest.
    _renderSendClickupLists(items, query, option, hint) {
        const clickup = this._sendPicker?.clickup
        if (!clickup) return false

        const heading = document.createElement("div")
        heading.className = "tasks-send-group"
        heading.textContent = "ClickUp"
        if (!clickup.connected) {
            items.appendChild(heading)
            const connect = document.createElement("a")
            connect.className = "network-toolbar-menu-item"
            connect.href = clickup.settings_url || "/settings/connections"
            connect.innerHTML = '<i class="bi bi-plug"></i><span>Connect ClickUp in Settings</span>'
            items.appendChild(connect)
            return false
        }
        if (!clickup.synced) {
            items.appendChild(heading)
            hint("Loading your ClickUp lists… try again in a minute")
            return false
        }
        const lists = (clickup.lists || []).filter((l) => !query || `${l.name} ${l.path || ""}`.toLowerCase().includes(query))
        if (lists.length === 0) return false
        items.appendChild(heading)
        lists.forEach((list) => option("bi-box-arrow-up-right", list.name, list.path || null, () => this._pickSendList({ ...list, external: true })))
        return true
    }

    _pickSendList(list) {
        const state = this._sendPicker
        if (!state) return
        Object.assign(state, { step: "columns", list, query: "", columns: null, error: null })
        this._renderSendPanel()
        this._fetchSendJson(`/tasks/send_targets?target_list_id=${encodeURIComponent(list.id)}`).then((payload) => {
            if (!this._sendPicker || this._sendPicker.list !== list) return
            this._sendPicker.columns = payload?.columns || []
            if (!payload) this._sendPicker.error = list.external ? "ClickUp didn't answer - you can still send it with the default status" : null
            this._renderSendItems()
        })
    }

    _sendBackToLists() {
        const state = this._sendPicker
        if (!state) return
        Object.assign(state, { step: "lists", list: null, query: "", columns: null, error: null })
        this._renderSendPanel()
    }

    async _sendTo(column) {
        const state = this._sendPicker
        if (!state || state.busy || !state.list) return

        state.busy = true
        state.error = null
        this._renderSendItems()
        try {
            const id = state.block ? await this._resolveId(state.block) : state.row.dataset.taskId
            const res = await fetch(`/tasks/${id}/send_to_list`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken(), Accept: "application/json" },
                credentials: "same-origin",
                body: JSON.stringify({ target_list_id: state.list.id, column_id: column ? column.id : null })
            })
            const payload = await res.json().catch(() => null)
            if (!res.ok || !payload?.ok) throw new Error(payload?.error || "Couldn't send it")

            if (payload.chip_html) this._insertSendChip(state.row, payload.chip_html)
            const where = [payload.ticket.list, payload.ticket.column].filter(Boolean).join(" › ")
            this._flashNotice(`Sent to ${where}`)
            this._closeSendPicker()
        } catch (error) {
            if (!this._sendPicker) return
            this._sendPicker.busy = false
            this._sendPicker.error = error.message
            this._renderSendItems()
        }
    }

    // After the company chip if there is one, else where _insertCompanyChip
    // would have put it - so the live row matches a reloaded one.
    _insertSendChip(row, html) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        const holder = document.createElement("div")
        holder.innerHTML = html.trim()
        const chip = holder.firstElementChild
        if (!chip) return

        const anchor = [...wrap.querySelectorAll(".tasks-outline-send, .tasks-outline-company")].pop() || wrap.querySelector("[data-task-tags]")
        const counts = wrap.querySelector(COUNT_CHIPS)
        if (anchor) anchor.after(chip)
        else if (counts) counts.before(chip)
        else wrap.appendChild(chip)
    }

    _closeSendPicker() {
        this._sendPicker = null
        if (this._sendPanel) {
            this._sendPanel.remove()
            this._sendPanel = null
        }
        if (this._sendOutsideHandler) {
            document.removeEventListener("mousedown", this._sendOutsideHandler, true)
            this._sendOutsideHandler = null
        }
    }

    _escape(value) {
        const div = document.createElement("div")
        div.textContent = String(value)
        return div.innerHTML
    }

    // --- Priority ---------------------------------------------------------
    // One field, four values, one of which is the absence of a value. Set from
    // the kebab and the task pane only: there is no row-focus model in this
    // controller to hang a Cmd+1-4 shortcut on, and building one is a larger
    // change than the field itself.

    setPriority(e) {
        e.preventDefault()
        e.stopPropagation()
        const row = e.target.closest("[data-task-row]")
        if (!row) return
        this._writePriority(row, e.currentTarget.dataset.priority)
        // Reflect the choice in the menu still open under the cursor.
        const group = e.currentTarget.closest("[data-task-priority-options]")
        if (group) {
            group.querySelectorAll(".tasks-priority-option").forEach((b) => {
                b.classList.toggle("is-active", b === e.currentTarget)
            })
        }
    }

    async _writePriority(row, value, { record = true } = {}) {
        if ((row.dataset.priority || "normal") === (value || "normal")) return
        if (record) this._recordFields(row, "Priority")
        const body = new FormData()
        // "normal" is sent as the literal string; the server maps it to NULL.
        // An empty string would be indistinguishable from "field omitted".
        body.append("priority", value || "normal")
        // A just-created row may still carry its tmp- id.
        const id = await this._resolveId(row.closest("[data-task-block]"))
        if (!id) return
        const result = await this._patch(`/tasks/${id}`, body)
        if (!result || !result.ok) return
        row.dataset.priority = result.task.priority || "normal"
        this._renderPriorityMarker(row, result.task.priority)
    }

    // --- Custom fields (TaskListField) -------------------------------------
    // Set from the task pane. Values ride on
    // the row as JSON keyed by field id; the badge is redrawn from them.

    _rowFieldValues(row) {
        try {
            return JSON.parse(row.dataset.fieldValues || "{}") || {}
        } catch {
            return {}
        }
    }

    // "3", "0.5" - never "3.0" - matching TaskListField#format.
    _formatFieldValue(value) {
        if (value === null || value === undefined || value === "") return ""
        const number = Number(value)
        return Number.isFinite(number) ? String(number) : String(value)
    }

    async _writeFieldValue(row, fieldId, value) {
        const current = this._formatFieldValue(this._rowFieldValues(row)[fieldId])
        if (current === (value || "")) return
        this._recordFields(row, "Field")
        const body = new FormData()
        body.append(`field_values[${fieldId}]`, value || "")
        const id = await this._resolveId(row.closest("[data-task-block]"))
        if (!id) return
        const result = await this._patch(`/tasks/${id}`, body)
        if (!result || !result.ok) return
        row.dataset.fieldValues = JSON.stringify(result.task.field_values || {})
        this._renderFieldBadges(row)
    }

    _renderFieldBadges(row) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        wrap.querySelectorAll("[data-field-badge]").forEach((el) => el.remove())
        const values = this._rowFieldValues(row)
        let anchor = wrap.querySelector(".tasks-outline-tags") || wrap.querySelector("[data-task-input]")
        this.fieldsValue.forEach((field) => {
            const label = this._formatFieldValue(values[String(field.id)])
            if (!label) return
            const badge = document.createElement("span")
            badge.className = `tasks-field-badge tasks-field-badge--${field.kind}`
            badge.dataset.fieldBadge = String(field.id)
            badge.title = `${field.name}: ${label}`
            badge.setAttribute("aria-label", `${field.name}: ${label}`)
            badge.innerHTML = '<i class="bi bi-speedometer2" aria-hidden="true"></i>'
            const text = document.createElement("span")
            text.textContent = label
            badge.appendChild(text)
            if (anchor) anchor.after(badge)
            else wrap.appendChild(badge)
            anchor = badge
        })
    }

    // Swap the marker in place rather than re-fetching the row: it's a single
    // element with a fixed shape, so a server round-trip would buy nothing.
    _renderPriorityMarker(row, name) {
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        const existing = wrap.querySelector(".tasks-outline-priority")
        if (existing) existing.remove()
        if (!name) return
        const marker = document.createElement("span")
        marker.className = `tasks-outline-priority tasks-outline-priority--${name}`
        const label = `Priority: ${name.charAt(0).toUpperCase()}${name.slice(1)}`
        marker.title = label
        marker.setAttribute("aria-label", label)
        const icon = document.createElement("i")
        icon.className = name === "low" ? "bi bi-caret-down-fill" : "bi bi-caret-up-fill"
        icon.setAttribute("aria-hidden", "true")
        marker.appendChild(icon)
        wrap.insertBefore(marker, wrap.firstChild)
    }

    _rowTags(row) {
        try {
            return JSON.parse(row.dataset.tags || "[]")
        } catch (_error) {
            return []
        }
    }

    _ensureTagPanel() {
        if (this._tagPanel) return
        const panel = document.createElement("div")
        panel.className = "network-toolbar-menu-panel tasks-tag-panel"
        panel.setAttribute("role", "listbox")
        // mousedown rather than click so a pick lands before the query field's
        // blur can tear the panel down under it.
        panel.addEventListener("mousedown", (ev) => ev.preventDefault())
        document.body.appendChild(panel)
        this._tagPanel = panel
        this._tagOutsideHandler = (ev) => {
            if (this._tagPanel && !this._tagPanel.contains(ev.target)) this._closeTagPicker()
        }
        document.addEventListener("mousedown", this._tagOutsideHandler, true)
    }

    _positionTagPanel(row) {
        const rect = row.getBoundingClientRect()
        const panel = this._tagPanel
        panel.style.position = "fixed"
        panel.style.top = `${Math.round(Math.min(rect.bottom + 4, window.innerHeight - 320))}px`
        panel.style.left = `${Math.round(Math.min(rect.left + 24, window.innerWidth - 280))}px`
    }

    async _fetchTagItems(query) {
        try {
            const res = await fetch(this._withList(`/tasks/tags?q=${encodeURIComponent(query)}`), {
                headers: { Accept: "application/json" },
                credentials: "same-origin"
            })
            if (!res.ok) throw new Error("tags failed")
            const payload = await res.json()
            if (!this._tagPicker || this._tagPicker.query !== query) return
            this._tagPicker.items = payload.tags || []
            this._renderTagPanel()
        } catch (_error) {
            if (this._tagPicker) { this._tagPicker.items = []; this._renderTagPanel() }
        }
    }

    _renderTagPanel() {
        const panel = this._tagPanel
        const state = this._tagPicker
        if (!panel || !state) return
        const applied = this._rowTags(state.row).map((s) => s.toLowerCase())
        panel.replaceChildren()

        const field = document.createElement("input")
        field.type = "text"
        field.className = "tasks-tag-query"
        field.placeholder = "Type a tag name…"
        field.value = state.query
        field.setAttribute("data-tag-query", "true")
        field.addEventListener("input", () => {
            state.query = field.value
            this._fetchTagItems(field.value)
        })
        field.addEventListener("keydown", (ev) => this._onTagKeydown(ev))
        panel.appendChild(field)

        // Empty state: three dashed suggestions, exactly the trio feedback #5
        // named. They're the shortest route from "no tags" to a partitioned
        // list, and they disappear once the member has one tag of their own.
        if (state.items.length === 0 && state.query.trim() === "") {
            const empty = document.createElement("div")
            empty.className = "tasks-tag-empty"
            empty.textContent = "Tags group tasks across your lists — work, personal, a client name."
            panel.appendChild(empty)
            const row = document.createElement("div")
            row.className = "tasks-tag-suggestions"
            for (const name of ["work", "personal", "client"]) {
                const btn = document.createElement("button")
                btn.type = "button"
                btn.className = "tasks-outline-tag tasks-tag-suggestion"
                btn.textContent = name
                btn.addEventListener("click", () => this._applyTag(name))
                row.appendChild(btn)
            }
            panel.appendChild(row)
            const tip = document.createElement("div")
            tip.className = "tasks-tag-tip"
            tip.textContent = "Tip: typing #work in a task title tags it too."
            panel.appendChild(tip)
            return
        }

        state.items.forEach((tag, index) => {
            const row = document.createElement("div")
            row.className = "tasks-tag-row"
            row.classList.toggle("is-active", index === state.index)

            const toggle = document.createElement("button")
            toggle.type = "button"
            toggle.className = "network-toolbar-menu-item tasks-tag-toggle"
            toggle.setAttribute("role", "option")
            const on = applied.includes(tag.slug.toLowerCase())
            toggle.setAttribute("aria-selected", on ? "true" : "false")
            toggle.setAttribute("aria-label", `${tag.name}, ${on ? "applied" : "not applied"}`)
            const check = document.createElement("i")
            check.className = on ? "bi bi-check2" : "bi"
            toggle.appendChild(check)
            const chip = document.createElement("span")
            chip.className = `tasks-outline-tag tasks-outline-tag--${tag.color_key}`
            chip.textContent = tag.name
            toggle.appendChild(chip)
            const count = document.createElement("span")
            count.className = "tasks-tag-count"
            count.textContent = String(tag.count || 0)
            toggle.appendChild(count)
            toggle.addEventListener("click", () => this._applyTag(tag.slug))
            row.appendChild(toggle)

            const kebab = document.createElement("button")
            kebab.type = "button"
            kebab.className = "tasks-tag-kebab"
            kebab.setAttribute("aria-label", `Options for ${tag.name}`)
            kebab.innerHTML = '<i class="bi bi-three-dots"></i>'
            kebab.addEventListener("click", () => this._renderTagMenu(row, tag))
            row.appendChild(kebab)

            panel.appendChild(row)
        })

        // Create is always the last row and always shows the colour the tag
        // will get, so the result is never a surprise after committing.
        const typed = state.query.trim()
        if (typed !== "" && !state.items.some((t) => t.name.toLowerCase() === typed.toLowerCase() || t.slug.toLowerCase() === typed.toLowerCase())) {
            const create = document.createElement("button")
            create.type = "button"
            create.className = "network-toolbar-menu-item tasks-tag-create"
            create.innerHTML = '<i class="bi bi-plus-lg"></i>'
            const label = document.createElement("span")
            label.textContent = `Create “${typed}”`
            create.appendChild(label)
            create.addEventListener("click", () => this._applyTag(typed))
            panel.appendChild(create)
        }
    }

    // Per-tag menu, rendered in place of the row it belongs to rather than as a
    // settings screen — rename/recolour/filter/delete on the row you're looking
    // at is the whole interaction.
    _renderTagMenu(rowEl, tag) {
        rowEl.replaceChildren()
        const wrap = document.createElement("div")
        wrap.className = "tasks-tag-menu"

        const rename = document.createElement("input")
        rename.type = "text"
        rename.className = "tasks-tag-query"
        rename.value = tag.name
        rename.addEventListener("keydown", (ev) => {
            if (ev.key === "Enter") { ev.preventDefault(); this._updateTag(tag, { name: rename.value }) }
            if (ev.key === "Escape") { ev.preventDefault(); this._renderTagPanel() }
        })
        wrap.appendChild(rename)

        const swatches = document.createElement("div")
        swatches.className = "tasks-tag-swatches"
        for (const key of this.constructor.TAG_PALETTE) {
            const sw = document.createElement("button")
            sw.type = "button"
            sw.className = `tasks-tag-swatch tasks-outline-tag--${key}`
            sw.setAttribute("aria-label", key)
            sw.addEventListener("click", () => this._updateTag(tag, { color_key: key }))
            swatches.appendChild(sw)
        }
        wrap.appendChild(swatches)

        const filter = document.createElement("a")
        filter.className = "network-toolbar-menu-item"
        filter.href = this._withList(`?tag=${encodeURIComponent(tag.slug)}`)
        filter.textContent = "Filter by this tag"
        wrap.appendChild(filter)

        const del = document.createElement("button")
        del.type = "button"
        del.className = "network-toolbar-menu-item text-danger"
        del.textContent = "Delete everywhere"
        del.addEventListener("click", () => this._deleteTagEverywhere(tag))
        wrap.appendChild(del)

        rowEl.appendChild(wrap)
        rename.focus()
        rename.select()
    }

    _onTagKeydown(e) {
        const state = this._tagPicker
        if (!state) return
        if (e.key === "Escape") { e.preventDefault(); this._closeTagPicker(); return }
        const items = state.items || []
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault()
            if (items.length === 0) return
            const delta = e.key === "ArrowDown" ? 1 : -1
            let next = state.index + delta
            if (next < 0) next = items.length - 1
            if (next >= items.length) next = 0
            state.index = next
            this._renderTagPanel()
            return
        }
        if (e.key === "Enter") {
            e.preventDefault()
            const picked = items[state.index]
            const typed = state.query.trim()
            if (picked) this._applyTag(picked.slug)
            else if (typed !== "") this._applyTag(typed)
            return
        }
        // Backspace on an empty query removes the last applied tag — the one
        // convention every chip-input in this category shares.
        if (e.key === "Backspace" && state.query === "") {
            const applied = this._rowTags(state.row)
            if (applied.length === 0) return
            e.preventDefault()
            this._applyTag(applied[applied.length - 1])
        }
    }

    // Toggle one tag on the row and persist the whole array, which is the
    // existing update contract — no partial tag endpoint to keep in sync.
    async _applyTag(nameOrSlug) {
        const state = this._tagPicker
        if (!state || state.busy) return
        const row = state.row
        const current = this._rowTags(row)
        const lower = current.map((s) => s.toLowerCase())
        const key = String(nameOrSlug).toLowerCase()
        const next = lower.includes(key) ? current.filter((s) => s.toLowerCase() !== key) : current.concat([nameOrSlug])

        state.busy = true
        const body = new FormData()
        if (next.length === 0) {
            // An empty array has to be sent as a present-but-blank param, or
            // params.key?(:tags) is false server-side and clearing does nothing.
            body.append("tags", "")
        } else {
            next.forEach((slug) => body.append("tags[]", slug))
        }
        const result = await this._patch(`/tasks/${row.dataset.taskId}`, body)
        state.busy = false
        if (!result || !result.ok) return

        row.dataset.tags = JSON.stringify(result.task.tags || [])
        state.query = ""
        this._fetchTagItems("")
        this._renderRowChips(row, result.task.tag_chips, (result.task.tags || []).length)
        // A newly created tag isn't in the dictionary this page was served with;
        // fold it in so a later remote update can colour it correctly.
        this._learnTags(result.task.tag_chips)
    }

    async _updateTag(tag, updates) {
        const body = new FormData()
        Object.entries(updates).forEach(([k, v]) => body.append(k, v))
        const result = await this._patch(`/task_tags/${tag.id}`, body)
        if (!result || !result.ok) {
            if (result && result.error) window.alert(result.error)
            return
        }
        this._fetchTagItems(this._tagPicker ? this._tagPicker.query : "")
        const row = this._tagPicker && this._tagPicker.row
        if (row) this._renderRowChips(row, this._chipsForSlugs(this._rowTags(row)), this._rowTags(row).length)
    }

    async _deleteTagEverywhere(tag) {
        // The confirm names the count, because "remove from this task" and
        // "remove from 41 tasks" are different decisions wearing similar words.
        const uses = tag.count || 0
        const ok = window.confirm(`Remove ${tag.name} from ${uses} ${uses === 1 ? "task" : "tasks"}?`)
        if (!ok) return
        const result = await this._delete(`/task_tags/${tag.id}`)
        if (!result || !result.ok) return
        document.querySelectorAll("[data-task-row]").forEach((row) => {
            const tags = this._rowTags(row).filter((s) => s.toLowerCase() !== String(result.slug).toLowerCase())
            row.dataset.tags = JSON.stringify(tags)
            this._renderRowChips(row, this._chipsForSlugs(tags), tags.length)
        })
        this._fetchTagItems(this._tagPicker ? this._tagPicker.query : "")
    }

    // Redraw a row's chips from data the client already has.
    //
    // This used to refetch the ENTIRE page on every tag toggle and pull one
    // cluster out of the parsed HTML — a full list render per click, and the
    // only way to get colours, because the client had no dictionary. It now
    // gets chips three ways, all cheap: the PATCH response carries them
    // resolved for this viewer; a remote change resolves against the viewer's
    // own dictionary (below); and a slug in neither falls back to a neutral
    // chip, exactly as the server does.
    _renderRowChips(row, chips, total) {
        if (!row) return
        const wrap = row.querySelector(".tasks-outline-title-wrap")
        if (!wrap) return
        const existing = wrap.querySelector("[data-task-tags]")
        if (existing) existing.remove()
        if (!chips || chips.length === 0) return

        const cluster = document.createElement("span")
        cluster.className = "tasks-outline-tags"
        cluster.setAttribute("data-task-tags", "true")
        cluster.setAttribute("data-tag-total", String(total))
        cluster.setAttribute("data-action", "click->tasks-outline#onTagClick")

        // Mirrors tasks/_tag_chips: at most CHIP_CAP chips, then one "+N" per
        // width cap so the count stays right at whatever width is showing.
        chips.slice(0, this.constructor.TAG_CHIP_CAP).forEach((chip, index) => {
            const el = document.createElement("a")
            el.className = `tasks-outline-tag tasks-outline-tag--${chip.color_key || "slate"}`
            el.textContent = chip.label
            el.title = chip.label
            el.href = this._tagFilterHref(chip.slug)
            el.setAttribute("aria-label", `Filter by ${chip.label}`)
            el.dataset.tagSlug = chip.slug
            el.dataset.chipIndex = String(index)
            cluster.appendChild(el)
        })
        this.constructor.TAG_CHIP_CAPS.forEach((cap) => {
            if (total <= cap) return
            const more = document.createElement("button")
            more.type = "button"
            more.className = "tasks-outline-tag tasks-outline-tag--more"
            more.dataset.cap = String(cap)
            more.dataset.action = "click->tasks-outline#openTagPicker"
            more.setAttribute("aria-label", `${total - cap} more tags`)
            more.textContent = `+${total - cap}`
            cluster.appendChild(more)
        })

        // After the title span and its editor, before the other chips - the
        // order the Slim partial produces.
        const next = wrap.querySelector(".tasks-field-badge, .tasks-outline-company, .tasks-outline-send, " + COUNT_CHIPS)
        if (next) wrap.insertBefore(cluster, next)
        else wrap.appendChild(cluster)
    }

    // The viewer's own dictionary, sent once with the outline. Used for changes
    // this client did not make, where the broadcast carries slugs only.
    _tagDictionary() {
        if (this._tagDict) return this._tagDict
        try {
            this._tagDict = JSON.parse(this.element.dataset.tagDictionary || "{}")
        } catch (_error) {
            this._tagDict = {}
        }
        return this._tagDict
    }

    _chipsForSlugs(slugs) {
        const dict = this._tagDictionary()
        return Array(slugs || []).flat().filter(Boolean).map((slug) => {
            const hit = dict[String(slug).toLowerCase()]
            return { slug: slug, label: (hit && hit.name) || slug, color_key: (hit && hit.color) || "slate" }
        })
    }

    // Fold chips the server just resolved into the local dictionary. A tag
    // created moments ago wasn't in the page's dictionary, and without this a
    // later remote change would redraw it as a neutral chip.
    _learnTags(chips) {
        if (!chips || chips.length === 0) return
        const dict = this._tagDictionary()
        chips.forEach((chip) => {
            if (!chip || !chip.slug) return
            dict[String(chip.slug).toLowerCase()] = { name: chip.label, color: chip.color_key }
        })
    }

    _tagFilterHref(slug) {
        const url = new URL(window.location.href)
        const active = url.searchParams.getAll("tag[]").concat(url.searchParams.getAll("tag"))
        url.searchParams.delete("tag")
        url.searchParams.delete("tag[]")
        const next = active.includes(slug) ? active.filter((s) => s !== slug) : active.concat([slug])
        next.forEach((s) => url.searchParams.append("tag[]", s))
        return `${url.pathname}${url.search}`
    }

    _closeTagPicker() {
        this._tagPicker = null
        if (this._tagOutsideHandler) {
            document.removeEventListener("mousedown", this._tagOutsideHandler, true)
            this._tagOutsideHandler = null
        }
        if (this._tagPanel) {
            this._tagPanel.remove()
            this._tagPanel = null
        }
    }

    _fetchHeaders() {
        const headers = { "Accept": "application/json", "X-CSRF-Token": csrfToken() }
        if (this._clientId) headers["X-Task-List-Client-Id"] = this._clientId
        return headers
    }

    _post(url, formData) {
        return fetch(this._withList(url), {
            method: "POST",
            headers: this._fetchHeaders(),
            body: formData,
            credentials: "same-origin"
        }).then((r) => r.json()).catch(() => null).then((data) => this._announceWrite(url, data))
    }

    _patch(url, formData) {
        return fetch(this._withList(url), {
            method: "PATCH",
            headers: this._fetchHeaders(),
            body: formData,
            credentials: "same-origin"
        }).then((r) => r.json()).catch(() => null).then((data) => this._announceWrite(url, data))
    }

    // Every write to a task (/tasks/:id/...) - completing, priority, fields,
    // a date, a title - tells an open task pane, which re-reads itself
    // (task_pane_controller.js, jane:task-changed). Passes the reply through.
    _announceWrite(url, data) {
        const id = data?.ok ? String(url).match(/^\/tasks\/(\d+)/)?.[1] : null
        if (id) window.dispatchEvent(new CustomEvent("jane:task-changed", { detail: { taskId: Number(id) } }))
        return data
    }

    _delete(url, { keepalive = false } = {}) {
        return fetch(this._withList(url), {
            method: "DELETE",
            headers: this._fetchHeaders(),
            credentials: "same-origin",
            keepalive
        }).then((r) => r.json()).catch(() => null)
    }
}
